import * as z from "zod";
//#region src/supabase.schemas.ts
const publicBusinessTypeSchema = z.enum([
	"device_user",
	"service_provider",
	"manufacturer"
]);
const publicCaseOriginSchema = z.enum([
	"user",
	"trigger",
	"portal"
]);
const publicCaseStatusSchema = z.enum([
	"reported",
	"assessed",
	"started",
	"archived",
	"resolved"
]);
const publicCaseTypeSchema = z.enum([
	"accident",
	"anomaly",
	"damage",
	"improvement"
]);
const publicCasesHistoryTypeSchema = z.enum([
	"case_item",
	"case_to_part",
	"case_to_case_tag",
	"case_to_maintenance",
	"case_to_service_job"
]);
const publicCasesInsightsPeriodSchema = z.enum([
	"day",
	"week",
	"month"
]);
const publicCasesToMaintenancesOriginSchema = z.enum(["case", "maintenance"]);
const publicChartTypeSchema = z.enum([
	"line",
	"area",
	"bar"
]);
const publicConnectionStatusSchema = z.enum([
	"pending",
	"online",
	"offline"
]);
const publicDataTriggerActionSchema = z.enum(["case", "maintenance"]);
const publicDataTriggerOperatorSchema = z.enum([
	"greater",
	"less",
	"equal",
	"not_equal",
	"greater_equal",
	"less_equal"
]);
const publicDataTriggerTargetTableSchema = z.enum([
	"device_scalar_values",
	"device_errors",
	"device_orders"
]);
const publicDataTriggerThresholdSourceSchema = z.enum([
	"fixed",
	"step",
	"two_sigma"
]);
const publicDataTriggerTransformationSchema = z.enum([
	"mean",
	"stddev",
	"max",
	"min",
	"sum",
	"count"
]);
const publicDbOperationSchema = z.enum([
	"INSERT",
	"UPDATE",
	"DELETE"
]);
const publicDeploymentRoleSchema = z.enum([
	"admin",
	"technician",
	"viewer",
	"none",
	"end_customer"
]);
const publicDeviceOrderStatusSchema = z.enum([
	"pending",
	"started",
	"finished",
	"aborted",
	"cancelled",
	"missing"
]);
const publicDevicesHistoryTypeSchema = z.enum([
	"case_item",
	"maintenance_item",
	"maintenance",
	"history_event",
	"service_job"
]);
const publicGlobalRoleSchema = z.enum(["admin", "none"]);
const publicInventoryActionSchema = z.enum([
	"add",
	"remove",
	"change",
	"move",
	"isolate",
	"assemble",
	"disassemble"
]);
const publicInventoryLocationTypeSchema = z.enum(["source", "target"]);
const publicMaintenanceItemStatusSchema = z.enum([
	"pending",
	"started",
	"skipped",
	"completed",
	"failed"
]);
const publicMaintenanceItemsToPartsTypeSchema = z.enum(["proposed", "executed"]);
const publicMaintenanceStatusSchema = z.enum([
	"scheduled",
	"started",
	"finished",
	"skipped"
]);
const publicMaintenanceStrategiesHistoryTypeSchema = z.enum(["maintenance_strategy"]);
const publicMaintenancesFinishedDeviceUtilizationPeriodSchema = z.enum([
	"day",
	"week",
	"month"
]);
const publicMaintenancesHistoryTypeSchema = z.enum([
	"maintenance",
	"maintenance_to_device",
	"maintenance_approval",
	"maintenance_to_maintenance_tag",
	"case_to_maintenance",
	"maintenance_to_maintenance_parent",
	"maintenance_to_maintenance_child",
	"maintenance_to_service_job"
]);
const publicPartLocationTypeSchema = z.enum([
	"inventory",
	"incoming",
	"staging"
]);
const publicPartUsageTypeSchema = z.enum([
	"disassembly",
	"assembly",
	"cleaning",
	"repair",
	"calibration",
	"update",
	"unspecified"
]);
const publicServiceJobsHistoryTypeSchema = z.enum([
	"service_job",
	"case_to_service_job",
	"maintenance_to_service_job",
	"user_to_service_job"
]);
const publicWorkspaceRoleSchema = z.enum([
	"admin",
	"viewer",
	"associate",
	"none"
]);
const storageBuckettypeSchema = z.enum([
	"STANDARD",
	"ANALYTICS",
	"VECTOR"
]);
const jsonSchema = z.lazy(() => z.union([
	z.string(),
	z.number(),
	z.boolean(),
	z.record(z.string(), z.union([jsonSchema, z.undefined()])),
	z.array(jsonSchema)
]).nullable());
const caseCommentsRowSchema = z.object({
	case_comment_parent_id: z.string().nullable(),
	case_id: z.string(),
	content: z.string().nullable(),
	content_html: z.string().nullable(),
	created_at: z.string(),
	deleted_at: z.string().nullable(),
	deployment_id: z.string(),
	edited_at: z.string().nullable(),
	id: z.string(),
	is_public: z.boolean(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const caseCommentsInsertSchema = z.object({
	case_comment_parent_id: z.string().optional().nullable(),
	case_id: z.string(),
	content: z.string().optional().nullable(),
	content_html: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deleted_at: z.string().optional().nullable(),
	deployment_id: z.string(),
	edited_at: z.string().optional().nullable(),
	id: z.string().optional(),
	is_public: z.boolean().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const caseCommentsUpdateSchema = z.object({
	case_comment_parent_id: z.string().optional().nullable(),
	case_id: z.string().optional(),
	content: z.string().optional().nullable(),
	content_html: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deleted_at: z.string().optional().nullable(),
	deployment_id: z.string().optional(),
	edited_at: z.string().optional().nullable(),
	id: z.string().optional(),
	is_public: z.boolean().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const caseCommentsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("case_comments_case_comment_parent_id_fkey"),
		columns: z.tuple([z.literal("case_comment_parent_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_comments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("case_comments_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("case_comments_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("cases"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("case_comments_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("case_comments_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("case_comments_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const caseFilesRowSchema = z.object({
	bucket_id: z.string(),
	case_comment_id: z.string().nullable(),
	case_id: z.string(),
	created_at: z.string(),
	description: z.string().nullable(),
	id: z.string(),
	locale: z.string().nullable(),
	path: z.string(),
	thumbnail: z.string().nullable(),
	title: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const caseFilesInsertSchema = z.object({
	bucket_id: z.string(),
	case_comment_id: z.string().optional().nullable(),
	case_id: z.string(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	locale: z.string().optional().nullable(),
	path: z.string(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const caseFilesUpdateSchema = z.object({
	bucket_id: z.string().optional(),
	case_comment_id: z.string().optional().nullable(),
	case_id: z.string().optional(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	locale: z.string().optional().nullable(),
	path: z.string().optional(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const caseFilesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("case_files_case_comment_id_fkey"),
		columns: z.tuple([z.literal("case_comment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_comments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("case_files_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("case_files_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("cases"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("case_files_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const caseTagsRowSchema = z.object({
	created_at: z.string(),
	description: z.string().nullable(),
	id: z.string(),
	name: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const caseTagsInsertSchema = z.object({
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	name: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const caseTagsUpdateSchema = z.object({
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	name: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const caseTagsRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("case_tags_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("case_tags_workspace_id_fkey"),
	columns: z.tuple([z.literal("workspace_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("workspaces"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const caseTemplatesRowSchema = z.object({
	created_at: z.string(),
	description: z.string().nullable(),
	description_html: z.string().nullable(),
	id: z.string(),
	name: z.string(),
	priority: z.number(),
	proposal: z.string().nullable(),
	proposal_html: z.string().nullable(),
	solution: z.string().nullable(),
	solution_html: z.string().nullable(),
	type: publicCaseTypeSchema.nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const caseTemplatesInsertSchema = z.object({
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	description_html: z.string().optional().nullable(),
	id: z.string().optional(),
	name: z.string(),
	priority: z.number().optional(),
	proposal: z.string().optional().nullable(),
	proposal_html: z.string().optional().nullable(),
	solution: z.string().optional().nullable(),
	solution_html: z.string().optional().nullable(),
	type: publicCaseTypeSchema.optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const caseTemplatesUpdateSchema = z.object({
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	description_html: z.string().optional().nullable(),
	id: z.string().optional(),
	name: z.string().optional(),
	priority: z.number().optional(),
	proposal: z.string().optional().nullable(),
	proposal_html: z.string().optional().nullable(),
	solution: z.string().optional().nullable(),
	solution_html: z.string().optional().nullable(),
	type: publicCaseTypeSchema.optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const caseTemplatesRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("case_templates_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("case_templates_workspace_id_fkey"),
	columns: z.tuple([z.literal("workspace_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("workspaces"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const caseTemplatesToChecklistTemplatesRowSchema = z.object({
	case_template_id: z.string(),
	checklist_template_id: z.string(),
	created_at: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const caseTemplatesToChecklistTemplatesInsertSchema = z.object({
	case_template_id: z.string(),
	checklist_template_id: z.string(),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const caseTemplatesToChecklistTemplatesUpdateSchema = z.object({
	case_template_id: z.string().optional(),
	checklist_template_id: z.string().optional(),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const caseTemplatesToChecklistTemplatesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("case_templates_to_clt_case_template_id_fkey"),
		columns: z.tuple([z.literal("case_template_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_templates"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("case_templates_to_clt_public_portal_id_fkey"),
		columns: z.tuple([z.literal("checklist_template_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("checklist_templates"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("case_templates_to_clt_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const caseTemplatesToPublicPortalsRowSchema = z.object({
	case_template_id: z.string(),
	created_at: z.string(),
	public_portal_id: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const caseTemplatesToPublicPortalsInsertSchema = z.object({
	case_template_id: z.string(),
	created_at: z.string().optional(),
	public_portal_id: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const caseTemplatesToPublicPortalsUpdateSchema = z.object({
	case_template_id: z.string().optional(),
	created_at: z.string().optional(),
	public_portal_id: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const caseTemplatesToPublicPortalsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("case_templates_to_pp_case_template_id_fkey"),
		columns: z.tuple([z.literal("case_template_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_templates"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("case_templates_to_pp_public_portal_id_fkey"),
		columns: z.tuple([z.literal("public_portal_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("public_portals"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("case_templates_to_pp_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const casesRowSchema = z.object({
	assessed_at: z.string().nullable(),
	assignee_id: z.string().nullable(),
	case_template_id: z.string().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	description_html: z.string().nullable(),
	device_id: z.string().nullable(),
	due_at: z.string().nullable(),
	embedding: z.string().nullable(),
	event_finished_at: z.string().nullable(),
	event_started_at: z.string().nullable(),
	fts: z.unknown(),
	id: z.string(),
	name: z.string(),
	origin: publicCaseOriginSchema.nullable(),
	priority: z.number(),
	proposal: z.string().nullable(),
	proposal_html: z.string().nullable(),
	public_id: z.string(),
	public_portal_contact_mail: z.string().nullable(),
	public_portal_contact_name: z.string().nullable(),
	public_portal_contact_phone: z.string().nullable(),
	reported_at: z.string().nullable(),
	resolution_duration: z.string().nullable(),
	resolved_at: z.string().nullable(),
	serial_id: z.number(),
	serial_id_prefix: z.string(),
	solution: z.string().nullable(),
	solution_html: z.string().nullable(),
	started_at: z.string().nullable(),
	status: publicCaseStatusSchema,
	type: publicCaseTypeSchema.nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const casesInsertSchema = z.object({
	assessed_at: z.string().optional().nullable(),
	assignee_id: z.string().optional().nullable(),
	case_template_id: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string(),
	description: z.string().optional().nullable(),
	description_html: z.string().optional().nullable(),
	device_id: z.string().optional().nullable(),
	due_at: z.string().optional().nullable(),
	embedding: z.string().optional().nullable(),
	event_finished_at: z.string().optional().nullable(),
	event_started_at: z.string().optional().nullable(),
	fts: z.unknown().optional(),
	id: z.string().optional(),
	name: z.string(),
	origin: publicCaseOriginSchema.optional().nullable(),
	priority: z.number().optional(),
	proposal: z.string().optional().nullable(),
	proposal_html: z.string().optional().nullable(),
	public_id: z.string().optional(),
	public_portal_contact_mail: z.string().optional().nullable(),
	public_portal_contact_name: z.string().optional().nullable(),
	public_portal_contact_phone: z.string().optional().nullable(),
	reported_at: z.string().optional().nullable(),
	resolution_duration: z.string().optional().nullable(),
	resolved_at: z.string().optional().nullable(),
	serial_id: z.number().optional(),
	serial_id_prefix: z.string().optional(),
	solution: z.string().optional().nullable(),
	solution_html: z.string().optional().nullable(),
	started_at: z.string().optional().nullable(),
	status: publicCaseStatusSchema.optional(),
	type: publicCaseTypeSchema.optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const casesUpdateSchema = z.object({
	assessed_at: z.string().optional().nullable(),
	assignee_id: z.string().optional().nullable(),
	case_template_id: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	description: z.string().optional().nullable(),
	description_html: z.string().optional().nullable(),
	device_id: z.string().optional().nullable(),
	due_at: z.string().optional().nullable(),
	embedding: z.string().optional().nullable(),
	event_finished_at: z.string().optional().nullable(),
	event_started_at: z.string().optional().nullable(),
	fts: z.unknown().optional(),
	id: z.string().optional(),
	name: z.string().optional(),
	origin: publicCaseOriginSchema.optional().nullable(),
	priority: z.number().optional(),
	proposal: z.string().optional().nullable(),
	proposal_html: z.string().optional().nullable(),
	public_id: z.string().optional(),
	public_portal_contact_mail: z.string().optional().nullable(),
	public_portal_contact_name: z.string().optional().nullable(),
	public_portal_contact_phone: z.string().optional().nullable(),
	reported_at: z.string().optional().nullable(),
	resolution_duration: z.string().optional().nullable(),
	resolved_at: z.string().optional().nullable(),
	serial_id: z.number().optional(),
	serial_id_prefix: z.string().optional(),
	solution: z.string().optional().nullable(),
	solution_html: z.string().optional().nullable(),
	started_at: z.string().optional().nullable(),
	status: publicCaseStatusSchema.optional(),
	type: publicCaseTypeSchema.optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const casesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("cases_assignee_id_fkey"),
		columns: z.tuple([z.literal("assignee_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_case_template_id_fkey"),
		columns: z.tuple([z.literal("case_template_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_templates"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const casesHistoryRowSchema = z.object({
	case_id: z.string().nullable(),
	changed_fields: z.array(z.string()).nullable(),
	created_at: z.string().nullable(),
	deployment_id: z.string().nullable(),
	id: z.string(),
	new_val: jsonSchema.nullable(),
	old_val: jsonSchema.nullable(),
	operation: publicDbOperationSchema.nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const casesHistoryInsertSchema = z.object({
	case_id: z.string().optional().nullable(),
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	deployment_id: z.string().optional().nullable(),
	id: z.string().optional(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const casesHistoryUpdateSchema = z.object({
	case_id: z.string().optional().nullable(),
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	deployment_id: z.string().optional().nullable(),
	id: z.string().optional(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const casesHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("cases_history_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_history_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("cases"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_history_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_history_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const casesToCaseTagsRowSchema = z.object({
	case_id: z.string(),
	case_tag_id: z.string(),
	created_at: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const casesToCaseTagsInsertSchema = z.object({
	case_id: z.string(),
	case_tag_id: z.string(),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const casesToCaseTagsUpdateSchema = z.object({
	case_id: z.string().optional(),
	case_tag_id: z.string().optional(),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const casesToCaseTagsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("cases_to_case_tags_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_case_tags_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("cases"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_case_tags_case_tag_id_fkey"),
		columns: z.tuple([z.literal("case_tag_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_tags"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_case_tags_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const casesToCaseTagsHistoryRowSchema = z.object({
	case_id: z.string().nullable(),
	case_tag_id: z.string().nullable(),
	changed_fields: z.array(z.string()).nullable(),
	created_at: z.string().nullable(),
	id: z.string(),
	new_val: jsonSchema.nullable(),
	old_val: jsonSchema.nullable(),
	operation: publicDbOperationSchema.nullable(),
	user_id: z.string().nullable()
});
const casesToCaseTagsHistoryInsertSchema = z.object({
	case_id: z.string().optional().nullable(),
	case_tag_id: z.string().optional().nullable(),
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable()
});
const casesToCaseTagsHistoryUpdateSchema = z.object({
	case_id: z.string().optional().nullable(),
	case_tag_id: z.string().optional().nullable(),
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable()
});
const casesToCaseTagsHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("cases_to_case_tags_history_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_case_tags_history_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("cases"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_case_tags_history_case_tag_id_fkey"),
		columns: z.tuple([z.literal("case_tag_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_tags"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_case_tags_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const casesToDeviceDataTriggersRowSchema = z.object({
	case_id: z.string(),
	created_at: z.string(),
	device_data_trigger_id: z.string(),
	updated_at: z.string()
});
const casesToDeviceDataTriggersInsertSchema = z.object({
	case_id: z.string(),
	created_at: z.string().optional(),
	device_data_trigger_id: z.string(),
	updated_at: z.string().optional()
});
const casesToDeviceDataTriggersUpdateSchema = z.object({
	case_id: z.string().optional(),
	created_at: z.string().optional(),
	device_data_trigger_id: z.string().optional(),
	updated_at: z.string().optional()
});
const casesToDeviceDataTriggersRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("cases_to_device_data_triggers_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_device_data_triggers_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("cases"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_device_data_triggers_device_data_trigger_id_fkey"),
		columns: z.tuple([z.literal("device_data_trigger_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_data_triggers"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const casesToMaintenancesRowSchema = z.object({
	case_id: z.string(),
	created_at: z.string(),
	maintenance_id: z.string(),
	origin: publicCasesToMaintenancesOriginSchema,
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const casesToMaintenancesInsertSchema = z.object({
	case_id: z.string(),
	created_at: z.string().optional(),
	maintenance_id: z.string(),
	origin: publicCasesToMaintenancesOriginSchema,
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const casesToMaintenancesUpdateSchema = z.object({
	case_id: z.string().optional(),
	created_at: z.string().optional(),
	maintenance_id: z.string().optional(),
	origin: publicCasesToMaintenancesOriginSchema.optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const casesToMaintenancesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("cases_to_maintenances_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_maintenances_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("cases"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_maintenances_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_maintenances_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_maintenances_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_maintenances_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const casesToMaintenancesHistoryRowSchema = z.object({
	case_id: z.string().nullable(),
	changed_fields: z.array(z.string()).nullable(),
	created_at: z.string().nullable(),
	id: z.string(),
	maintenance_id: z.string().nullable(),
	new_val: jsonSchema.nullable(),
	old_val: jsonSchema.nullable(),
	operation: publicDbOperationSchema.nullable(),
	user_id: z.string().nullable()
});
const casesToMaintenancesHistoryInsertSchema = z.object({
	case_id: z.string().optional().nullable(),
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable()
});
const casesToMaintenancesHistoryUpdateSchema = z.object({
	case_id: z.string().optional().nullable(),
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable()
});
const casesToMaintenancesHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("cases_to_maintenances_history_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_maintenances_history_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("cases"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_maintenances_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_maintenances_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_maintenances_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_maintenances_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const casesToPartsRowSchema = z.object({
	case_id: z.string(),
	created_at: z.string(),
	id: z.string(),
	part_category_id: z.string().nullable(),
	part_id: z.string().nullable(),
	part_model_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const casesToPartsInsertSchema = z.object({
	case_id: z.string(),
	created_at: z.string().optional(),
	id: z.string().optional(),
	part_category_id: z.string().optional().nullable(),
	part_id: z.string().optional().nullable(),
	part_model_id: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const casesToPartsUpdateSchema = z.object({
	case_id: z.string().optional(),
	created_at: z.string().optional(),
	id: z.string().optional(),
	part_category_id: z.string().optional().nullable(),
	part_id: z.string().optional().nullable(),
	part_model_id: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const casesToPartsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("cases_to_parts_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_parts_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("cases"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_parts_part_category_id_fkey"),
		columns: z.tuple([z.literal("part_category_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_categories"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_parts_part_id_fkey"),
		columns: z.tuple([z.literal("part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_parts_part_id_fkey"),
		columns: z.tuple([z.literal("part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("parts"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_parts_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_model_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_parts_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_models"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_parts_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const casesToPartsHistoryRowSchema = z.object({
	case_id: z.string().nullable(),
	changed_fields: z.array(z.string()).nullable(),
	created_at: z.string().nullable(),
	id: z.string(),
	new_val: jsonSchema.nullable(),
	old_val: jsonSchema.nullable(),
	operation: publicDbOperationSchema.nullable(),
	part_category_id: z.string().nullable(),
	part_id: z.string().nullable(),
	part_model_id: z.string().nullable(),
	user_id: z.string().nullable()
});
const casesToPartsHistoryInsertSchema = z.object({
	case_id: z.string().optional().nullable(),
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	part_category_id: z.string().optional().nullable(),
	part_id: z.string().optional().nullable(),
	part_model_id: z.string().optional().nullable(),
	user_id: z.string().optional().nullable()
});
const casesToPartsHistoryUpdateSchema = z.object({
	case_id: z.string().optional().nullable(),
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	part_category_id: z.string().optional().nullable(),
	part_id: z.string().optional().nullable(),
	part_model_id: z.string().optional().nullable(),
	user_id: z.string().optional().nullable()
});
const casesToPartsHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("cases_to_parts_history_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_parts_history_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("cases"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_parts_history_part_category_id_fkey"),
		columns: z.tuple([z.literal("part_category_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_categories"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_parts_history_part_id_fkey"),
		columns: z.tuple([z.literal("part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_parts_history_part_id_fkey"),
		columns: z.tuple([z.literal("part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("parts"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_parts_history_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_model_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_parts_history_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_models"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_parts_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const casesToServiceJobsRowSchema = z.object({
	case_id: z.string(),
	created_at: z.string(),
	service_job_id: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const casesToServiceJobsInsertSchema = z.object({
	case_id: z.string(),
	created_at: z.string().optional(),
	service_job_id: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const casesToServiceJobsUpdateSchema = z.object({
	case_id: z.string().optional(),
	created_at: z.string().optional(),
	service_job_id: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const casesToServiceJobsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("c_to_sj_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("c_to_sj_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("cases"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("c_to_sj_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_job_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("c_to_sj_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_job_details_full"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("c_to_sj_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_jobs"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("c_to_sj_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const casesToServiceJobsHistoryRowSchema = z.object({
	case_id: z.string().nullable(),
	changed_fields: z.array(z.string()).nullable(),
	created_at: z.string().nullable(),
	id: z.string(),
	new_val: jsonSchema.nullable(),
	old_val: jsonSchema.nullable(),
	operation: publicDbOperationSchema.nullable(),
	service_job_id: z.string().nullable(),
	user_id: z.string().nullable()
});
const casesToServiceJobsHistoryInsertSchema = z.object({
	case_id: z.string().optional().nullable(),
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	service_job_id: z.string().optional().nullable(),
	user_id: z.string().optional().nullable()
});
const casesToServiceJobsHistoryUpdateSchema = z.object({
	case_id: z.string().optional().nullable(),
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	service_job_id: z.string().optional().nullable(),
	user_id: z.string().optional().nullable()
});
const casesToServiceJobsHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("cases_to_service_jobs_history_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_service_jobs_history_case_id_fkey"),
		columns: z.tuple([z.literal("case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("cases"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_service_jobs_history_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_job_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_service_jobs_history_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_job_details_full"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_service_jobs_history_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_jobs"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_to_service_jobs_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const checklistTemplateItemFilesRowSchema = z.object({
	bucket_id: z.string(),
	checklist_template_item_id: z.string(),
	created_at: z.string(),
	description: z.string().nullable(),
	id: z.string(),
	is_deleted: z.boolean().nullable(),
	locale: z.string().nullable(),
	ordering: z.number(),
	path: z.string(),
	thumbnail: z.string().nullable(),
	title: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const checklistTemplateItemFilesInsertSchema = z.object({
	bucket_id: z.string(),
	checklist_template_item_id: z.string(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	is_deleted: z.boolean().optional().nullable(),
	locale: z.string().optional().nullable(),
	ordering: z.number().optional(),
	path: z.string(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const checklistTemplateItemFilesUpdateSchema = z.object({
	bucket_id: z.string().optional(),
	checklist_template_item_id: z.string().optional(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	is_deleted: z.boolean().optional().nullable(),
	locale: z.string().optional().nullable(),
	ordering: z.number().optional(),
	path: z.string().optional(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const checklistTemplateItemFilesRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("checklist_template_item_files_checklist_template_item_id_fkey"),
	columns: z.tuple([z.literal("checklist_template_item_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("checklist_template_items"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("checklist_template_item_files_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const checklistTemplateItemsRowSchema = z.object({
	checklist_template_id: z.string(),
	created_at: z.string(),
	description: z.string(),
	description_html: z.string().nullable(),
	duration: z.string().nullable(),
	id: z.string(),
	is_deleted: z.boolean().nullable(),
	ordering: z.number(),
	title: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const checklistTemplateItemsInsertSchema = z.object({
	checklist_template_id: z.string(),
	created_at: z.string().optional(),
	description: z.string(),
	description_html: z.string().optional().nullable(),
	duration: z.string().optional().nullable(),
	id: z.string().optional(),
	is_deleted: z.boolean().optional().nullable(),
	ordering: z.number().optional(),
	title: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const checklistTemplateItemsUpdateSchema = z.object({
	checklist_template_id: z.string().optional(),
	created_at: z.string().optional(),
	description: z.string().optional(),
	description_html: z.string().optional().nullable(),
	duration: z.string().optional().nullable(),
	id: z.string().optional(),
	is_deleted: z.boolean().optional().nullable(),
	ordering: z.number().optional(),
	title: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const checklistTemplateItemsRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("checklist_template_items_checklist_template_id_fkey"),
	columns: z.tuple([z.literal("checklist_template_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("checklist_templates"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("checklist_template_items_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const checklistTemplateItemsToPartsRowSchema = z.object({
	action: publicPartUsageTypeSchema,
	checklist_template_item_id: z.string(),
	created_at: z.string(),
	id: z.string(),
	part_category_id: z.string().nullable(),
	part_id: z.string().nullable(),
	part_model_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const checklistTemplateItemsToPartsInsertSchema = z.object({
	action: publicPartUsageTypeSchema.optional(),
	checklist_template_item_id: z.string(),
	created_at: z.string().optional(),
	id: z.string().optional(),
	part_category_id: z.string().optional().nullable(),
	part_id: z.string().optional().nullable(),
	part_model_id: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const checklistTemplateItemsToPartsUpdateSchema = z.object({
	action: publicPartUsageTypeSchema.optional(),
	checklist_template_item_id: z.string().optional(),
	created_at: z.string().optional(),
	id: z.string().optional(),
	part_category_id: z.string().optional().nullable(),
	part_id: z.string().optional().nullable(),
	part_model_id: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const checklistTemplateItemsToPartsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("checklist_template_items_to_p_checklist_template_item_id_fkey"),
		columns: z.tuple([z.literal("checklist_template_item_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("checklist_template_items"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("checklist_template_items_to_parts_part_category_id_fkey"),
		columns: z.tuple([z.literal("part_category_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_categories"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("checklist_template_items_to_parts_part_id_fkey"),
		columns: z.tuple([z.literal("part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("checklist_template_items_to_parts_part_id_fkey"),
		columns: z.tuple([z.literal("part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("parts"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("checklist_template_items_to_parts_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_model_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("checklist_template_items_to_parts_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_models"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("checklist_template_items_to_parts_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const checklistTemplatesRowSchema = z.object({
	content_duration: z.string().nullable(),
	created_at: z.string(),
	description: z.string(),
	id: z.string(),
	is_deleted: z.boolean().nullable(),
	is_public: z.boolean().nullable(),
	title: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const checklistTemplatesInsertSchema = z.object({
	content_duration: z.string().optional().nullable(),
	created_at: z.string().optional(),
	description: z.string(),
	id: z.string().optional(),
	is_deleted: z.boolean().optional().nullable(),
	is_public: z.boolean().optional().nullable(),
	title: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const checklistTemplatesUpdateSchema = z.object({
	content_duration: z.string().optional().nullable(),
	created_at: z.string().optional(),
	description: z.string().optional(),
	id: z.string().optional(),
	is_deleted: z.boolean().optional().nullable(),
	is_public: z.boolean().optional().nullable(),
	title: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const checklistTemplatesRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("checklist_templates_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("checklist_templates_workspace_id_fkey"),
	columns: z.tuple([z.literal("workspace_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("workspaces"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const checklistTemplatesToWorkspacesRowSchema = z.object({
	checklist_template_id: z.string(),
	created_at: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const checklistTemplatesToWorkspacesInsertSchema = z.object({
	checklist_template_id: z.string(),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const checklistTemplatesToWorkspacesUpdateSchema = z.object({
	checklist_template_id: z.string().optional(),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const checklistTemplatesToWorkspacesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("checklist_templates_to_worksp_checklist_template_id_fkey"),
		columns: z.tuple([z.literal("checklist_template_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("checklist_templates"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("checklist_templates_to_workspaces_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("checklist_templates_to_workspaces_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const connectionsRowSchema = z.object({
	created_at: z.string(),
	id: z.string(),
	msg_time: z.string().nullable(),
	status: publicConnectionStatusSchema,
	updated_at: z.string(),
	workspace_id: z.string()
});
const connectionsInsertSchema = z.object({
	created_at: z.string().optional(),
	id: z.string(),
	msg_time: z.string().optional().nullable(),
	status: publicConnectionStatusSchema.optional(),
	updated_at: z.string().optional(),
	workspace_id: z.string()
});
const connectionsUpdateSchema = z.object({
	created_at: z.string().optional(),
	id: z.string().optional(),
	msg_time: z.string().optional().nullable(),
	status: publicConnectionStatusSchema.optional(),
	updated_at: z.string().optional(),
	workspace_id: z.string().optional()
});
const connectionsRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("connections_workspace_id_fkey"),
	columns: z.tuple([z.literal("workspace_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("workspaces"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const connectionsToDevicesRowSchema = z.object({
	connection_id: z.string(),
	created_at: z.string(),
	device_id: z.string(),
	msg_time: z.string().nullable(),
	status: publicConnectionStatusSchema,
	updated_at: z.string()
});
const connectionsToDevicesInsertSchema = z.object({
	connection_id: z.string(),
	created_at: z.string().optional(),
	device_id: z.string(),
	msg_time: z.string().optional().nullable(),
	status: publicConnectionStatusSchema,
	updated_at: z.string().optional()
});
const connectionsToDevicesUpdateSchema = z.object({
	connection_id: z.string().optional(),
	created_at: z.string().optional(),
	device_id: z.string().optional(),
	msg_time: z.string().optional().nullable(),
	status: publicConnectionStatusSchema.optional(),
	updated_at: z.string().optional()
});
const connectionsToDevicesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("connections_to_devices_connection_id_customer_fkey"),
		columns: z.tuple([z.literal("connection_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("connections"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("connections_to_devices_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("connections_to_devices_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const deploymentSettingsRowSchema = z.object({
	created_at: z.string(),
	default_case_assignee: z.string().nullable(),
	default_case_notification_email: z.string().nullable(),
	default_case_notification_email_locale: z.string(),
	default_maintenance_assignee: z.string().nullable(),
	default_maintenance_notification_email: z.string().nullable(),
	default_maintenance_notification_email_locale: z.string(),
	default_service_job_assignee: z.string().nullable(),
	default_service_job_notification_email: z.string().nullable(),
	default_service_job_notification_email_locale: z.string(),
	deployment_id: z.string(),
	id: z.string(),
	updated_at: z.string()
});
const deploymentSettingsInsertSchema = z.object({
	created_at: z.string().optional(),
	default_case_assignee: z.string().optional().nullable(),
	default_case_notification_email: z.string().optional().nullable(),
	default_case_notification_email_locale: z.string().optional(),
	default_maintenance_assignee: z.string().optional().nullable(),
	default_maintenance_notification_email: z.string().optional().nullable(),
	default_maintenance_notification_email_locale: z.string().optional(),
	default_service_job_assignee: z.string().optional().nullable(),
	default_service_job_notification_email: z.string().optional().nullable(),
	default_service_job_notification_email_locale: z.string().optional(),
	deployment_id: z.string(),
	id: z.string().optional(),
	updated_at: z.string().optional()
});
const deploymentSettingsUpdateSchema = z.object({
	created_at: z.string().optional(),
	default_case_assignee: z.string().optional().nullable(),
	default_case_notification_email: z.string().optional().nullable(),
	default_case_notification_email_locale: z.string().optional(),
	default_maintenance_assignee: z.string().optional().nullable(),
	default_maintenance_notification_email: z.string().optional().nullable(),
	default_maintenance_notification_email_locale: z.string().optional(),
	default_service_job_assignee: z.string().optional().nullable(),
	default_service_job_notification_email: z.string().optional().nullable(),
	default_service_job_notification_email_locale: z.string().optional(),
	deployment_id: z.string().optional(),
	id: z.string().optional(),
	updated_at: z.string().optional()
});
const deploymentSettingsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("deployment_settings_default_case_assignee_fkey"),
		columns: z.tuple([z.literal("default_case_assignee")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("deployment_settings_default_maintenance_assignee_fkey"),
		columns: z.tuple([z.literal("default_maintenance_assignee")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("deployment_settings_default_service_job_assignee_fkey"),
		columns: z.tuple([z.literal("default_service_job_assignee")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("deployment_settings_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(true),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const deploymentsRowSchema = z.object({
	address_1: z.string().nullable(),
	address_2: z.string().nullable(),
	city: z.string().nullable(),
	country: z.string().nullable(),
	created_at: z.string(),
	deleted_at: z.string().nullable(),
	id: z.string(),
	name: z.string(),
	portal_token: z.string(),
	timezone: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string(),
	zipcode: z.string().nullable()
});
const deploymentsInsertSchema = z.object({
	address_1: z.string().optional().nullable(),
	address_2: z.string().optional().nullable(),
	city: z.string().optional().nullable(),
	country: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deleted_at: z.string().optional().nullable(),
	id: z.string().optional(),
	name: z.string(),
	portal_token: z.string().optional(),
	timezone: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string(),
	zipcode: z.string().optional().nullable()
});
const deploymentsUpdateSchema = z.object({
	address_1: z.string().optional().nullable(),
	address_2: z.string().optional().nullable(),
	city: z.string().optional().nullable(),
	country: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deleted_at: z.string().optional().nullable(),
	id: z.string().optional(),
	name: z.string().optional(),
	portal_token: z.string().optional(),
	timezone: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional(),
	zipcode: z.string().optional().nullable()
});
const deploymentsRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("deployments_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("deployments_workspace_id_fkey"),
	columns: z.tuple([z.literal("workspace_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("workspaces"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const deviceDataSeriesRowSchema = z.object({
	calculate_derivative: z.boolean(),
	created_at: z.string(),
	deployment_id: z.string(),
	id: z.string(),
	metric_name: z.string(),
	target_table: publicDataTriggerTargetTableSchema,
	time_window: z.string().nullable(),
	transform: publicDataTriggerTransformationSchema.nullable()
});
const deviceDataSeriesInsertSchema = z.object({
	calculate_derivative: z.boolean().optional(),
	created_at: z.string().optional(),
	deployment_id: z.string(),
	id: z.string().optional(),
	metric_name: z.string(),
	target_table: publicDataTriggerTargetTableSchema,
	time_window: z.string().optional().nullable(),
	transform: publicDataTriggerTransformationSchema.optional().nullable()
});
const deviceDataSeriesUpdateSchema = z.object({
	calculate_derivative: z.boolean().optional(),
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	id: z.string().optional(),
	metric_name: z.string().optional(),
	target_table: publicDataTriggerTargetTableSchema.optional(),
	time_window: z.string().optional().nullable(),
	transform: publicDataTriggerTransformationSchema.optional().nullable()
});
const deviceDataSeriesRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("device_data_series_deployment_id_fkey"),
	columns: z.tuple([z.literal("deployment_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("deployments"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const deviceDataTriggersRowSchema = z.object({
	assignee_id: z.string().nullable(),
	case_template_id: z.string().nullable(),
	checklist_template_id: z.string().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	device_id: z.string(),
	device_model_data_trigger_id: z.string().nullable(),
	evaluate_every: z.string(),
	firing_since: z.string().nullable(),
	id: z.string(),
	is_active: z.boolean(),
	last_data_at: z.string().nullable(),
	last_run_at: z.string().nullable(),
	last_value: z.number().nullable(),
	name: z.string(),
	next_fire_value: z.number().nullable(),
	offset_value: z.number().nullable(),
	on_trigger: publicDataTriggerActionSchema.nullable(),
	operator: publicDataTriggerOperatorSchema,
	series_id: z.string().nullable(),
	threshold: z.number(),
	threshold_source: publicDataTriggerThresholdSourceSchema,
	time_window: z.string(),
	updated_at: z.string(),
	user_id: z.string()
});
const deviceDataTriggersInsertSchema = z.object({
	assignee_id: z.string().optional().nullable(),
	case_template_id: z.string().optional().nullable(),
	checklist_template_id: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string(),
	device_id: z.string(),
	device_model_data_trigger_id: z.string().optional().nullable(),
	evaluate_every: z.string(),
	firing_since: z.string().optional().nullable(),
	id: z.string().optional(),
	is_active: z.boolean().optional(),
	last_data_at: z.string().optional().nullable(),
	last_run_at: z.string().optional().nullable(),
	last_value: z.number().optional().nullable(),
	name: z.string(),
	next_fire_value: z.number().optional().nullable(),
	offset_value: z.number().optional().nullable(),
	on_trigger: publicDataTriggerActionSchema.optional().nullable(),
	operator: publicDataTriggerOperatorSchema,
	series_id: z.string().optional().nullable(),
	threshold: z.number(),
	threshold_source: publicDataTriggerThresholdSourceSchema.optional(),
	time_window: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string()
});
const deviceDataTriggersUpdateSchema = z.object({
	assignee_id: z.string().optional().nullable(),
	case_template_id: z.string().optional().nullable(),
	checklist_template_id: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	device_id: z.string().optional(),
	device_model_data_trigger_id: z.string().optional().nullable(),
	evaluate_every: z.string().optional(),
	firing_since: z.string().optional().nullable(),
	id: z.string().optional(),
	is_active: z.boolean().optional(),
	last_data_at: z.string().optional().nullable(),
	last_run_at: z.string().optional().nullable(),
	last_value: z.number().optional().nullable(),
	name: z.string().optional(),
	next_fire_value: z.number().optional().nullable(),
	offset_value: z.number().optional().nullable(),
	on_trigger: publicDataTriggerActionSchema.optional().nullable(),
	operator: publicDataTriggerOperatorSchema.optional(),
	series_id: z.string().optional().nullable(),
	threshold: z.number().optional(),
	threshold_source: publicDataTriggerThresholdSourceSchema.optional(),
	time_window: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional()
});
const deviceDataTriggersRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("device_data_triggers_assignee_id_fkey"),
		columns: z.tuple([z.literal("assignee_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_data_triggers_case_template_id_fkey"),
		columns: z.tuple([z.literal("case_template_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_templates"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_data_triggers_checklist_template_id_fkey"),
		columns: z.tuple([z.literal("checklist_template_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("checklist_templates"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_data_triggers_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_data_triggers_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_data_triggers_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_data_triggers_device_model_data_trigger_id_fkey"),
		columns: z.tuple([z.literal("device_model_data_trigger_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_model_data_triggers"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_data_triggers_series_id_fkey"),
		columns: z.tuple([z.literal("series_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_data_series"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_data_triggers_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const deviceErrorsRowSchema = z.object({
	code: z.string(),
	component: z.string().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	device_id: z.string(),
	id: z.number(),
	parameters: jsonSchema.nullable(),
	resolved_at: z.string().nullable(),
	severity: z.number().nullable(),
	started_at: z.string(),
	time_range: z.unknown(),
	title: z.string(),
	updated_at: z.string(),
	x: z.number().nullable(),
	y: z.number().nullable(),
	z: z.number().nullable()
});
const deviceErrorsInsertSchema = z.object({
	code: z.string(),
	component: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string(),
	description: z.string().optional().nullable(),
	device_id: z.string(),
	id: z.number().optional(),
	parameters: jsonSchema.optional().nullable(),
	resolved_at: z.string().optional().nullable(),
	severity: z.number().optional().nullable(),
	started_at: z.string(),
	time_range: z.unknown().optional(),
	title: z.string(),
	updated_at: z.string().optional(),
	x: z.number().optional().nullable(),
	y: z.number().optional().nullable(),
	z: z.number().optional().nullable()
});
const deviceErrorsUpdateSchema = z.object({
	code: z.string().optional(),
	component: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	description: z.string().optional().nullable(),
	device_id: z.string().optional(),
	id: z.number().optional(),
	parameters: jsonSchema.optional().nullable(),
	resolved_at: z.string().optional().nullable(),
	severity: z.number().optional().nullable(),
	started_at: z.string().optional(),
	time_range: z.unknown().optional(),
	title: z.string().optional(),
	updated_at: z.string().optional(),
	x: z.number().optional().nullable(),
	y: z.number().optional().nullable(),
	z: z.number().optional().nullable()
});
const deviceErrorsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("device_errors_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_errors_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_errors_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const deviceFilesRowSchema = z.object({
	bucket_id: z.string(),
	created_at: z.string(),
	description: z.string().nullable(),
	device_id: z.string(),
	id: z.string(),
	locale: z.string().nullable(),
	path: z.string(),
	thumbnail: z.string().nullable(),
	title: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const deviceFilesInsertSchema = z.object({
	bucket_id: z.string(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	device_id: z.string(),
	id: z.string().optional(),
	locale: z.string().optional().nullable(),
	path: z.string(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const deviceFilesUpdateSchema = z.object({
	bucket_id: z.string().optional(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	device_id: z.string().optional(),
	id: z.string().optional(),
	locale: z.string().optional().nullable(),
	path: z.string().optional(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const deviceFilesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("device_files_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_files_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_files_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const deviceInsightsChartsRowSchema = z.object({
	created_at: z.string(),
	deployment_id: z.string(),
	device_id: z.string(),
	id: z.string(),
	name: z.string(),
	series_id: z.string(),
	type: publicChartTypeSchema,
	unit_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const deviceInsightsChartsInsertSchema = z.object({
	created_at: z.string().optional(),
	deployment_id: z.string(),
	device_id: z.string(),
	id: z.string().optional(),
	name: z.string(),
	series_id: z.string(),
	type: publicChartTypeSchema.optional(),
	unit_id: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const deviceInsightsChartsUpdateSchema = z.object({
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	device_id: z.string().optional(),
	id: z.string().optional(),
	name: z.string().optional(),
	series_id: z.string().optional(),
	type: publicChartTypeSchema.optional(),
	unit_id: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const deviceInsightsChartsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("device_insights_charts_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_insights_charts_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_insights_charts_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_insights_charts_series_id_fkey"),
		columns: z.tuple([z.literal("series_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_data_series"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_insights_charts_unit_id_fkey"),
		columns: z.tuple([z.literal("unit_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("physical_units"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_insights_charts_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_insights_charts_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const deviceInsightsSettingsRowSchema = z.object({
	created_at: z.string(),
	default_exclude_error_titles: z.array(z.string()),
	default_min_error_severity_threshold: z.number(),
	default_shift_end_time: z.string(),
	default_shift_start_time: z.string(),
	deployment_id: z.string(),
	id: z.string(),
	updated_at: z.string()
});
const deviceInsightsSettingsInsertSchema = z.object({
	created_at: z.string().optional(),
	default_exclude_error_titles: z.array(z.string()).optional(),
	default_min_error_severity_threshold: z.number().optional(),
	default_shift_end_time: z.string().optional(),
	default_shift_start_time: z.string().optional(),
	deployment_id: z.string(),
	id: z.string().optional(),
	updated_at: z.string().optional()
});
const deviceInsightsSettingsUpdateSchema = z.object({
	created_at: z.string().optional(),
	default_exclude_error_titles: z.array(z.string()).optional(),
	default_min_error_severity_threshold: z.number().optional(),
	default_shift_end_time: z.string().optional(),
	default_shift_start_time: z.string().optional(),
	deployment_id: z.string().optional(),
	id: z.string().optional(),
	updated_at: z.string().optional()
});
const deviceInsightsSettingsRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("device_insights_settings_deployment_id_fkey"),
	columns: z.tuple([z.literal("deployment_id")]),
	isOneToOne: z.literal(true),
	referencedRelation: z.literal("deployments"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const deviceModelDataTriggersRowSchema = z.object({
	created_at: z.string(),
	deployment_id: z.string().nullable(),
	device_data_trigger_id: z.string(),
	device_model_id: z.string(),
	id: z.string(),
	init_offset_from_last_reading: z.boolean(),
	is_active: z.boolean(),
	updated_at: z.string(),
	user_id: z.string(),
	workspace_id: z.string()
});
const deviceModelDataTriggersInsertSchema = z.object({
	created_at: z.string().optional(),
	deployment_id: z.string().optional().nullable(),
	device_data_trigger_id: z.string(),
	device_model_id: z.string(),
	id: z.string().optional(),
	init_offset_from_last_reading: z.boolean().optional(),
	is_active: z.boolean().optional(),
	updated_at: z.string().optional(),
	user_id: z.string(),
	workspace_id: z.string()
});
const deviceModelDataTriggersUpdateSchema = z.object({
	created_at: z.string().optional(),
	deployment_id: z.string().optional().nullable(),
	device_data_trigger_id: z.string().optional(),
	device_model_id: z.string().optional(),
	id: z.string().optional(),
	init_offset_from_last_reading: z.boolean().optional(),
	is_active: z.boolean().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional(),
	workspace_id: z.string().optional()
});
const deviceModelDataTriggersRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("device_model_data_triggers_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_model_data_triggers_device_data_trigger_id_fkey"),
		columns: z.tuple([z.literal("device_data_trigger_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_data_triggers"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_model_data_triggers_device_model_id_fkey"),
		columns: z.tuple([z.literal("device_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_models"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_model_data_triggers_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_model_data_triggers_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const deviceModelFileChunksRowSchema = z.object({
	bbox: z.array(z.number()).nullable(),
	content: z.string().nullable(),
	device_model_file_id: z.string(),
	embedding: z.string().nullable(),
	fts: z.unknown(),
	headings: z.array(z.string()).nullable(),
	id: z.string(),
	label: z.string().nullable(),
	original_content: z.string().nullable(),
	page_number: z.number().nullable()
});
const deviceModelFileChunksInsertSchema = z.object({
	bbox: z.array(z.number()).optional().nullable(),
	content: z.string().optional().nullable(),
	device_model_file_id: z.string(),
	embedding: z.string().optional().nullable(),
	fts: z.unknown().optional(),
	headings: z.array(z.string()).optional().nullable(),
	id: z.string(),
	label: z.string().optional().nullable(),
	original_content: z.string().optional().nullable(),
	page_number: z.number().optional().nullable()
});
const deviceModelFileChunksUpdateSchema = z.object({
	bbox: z.array(z.number()).optional().nullable(),
	content: z.string().optional().nullable(),
	device_model_file_id: z.string().optional(),
	embedding: z.string().optional().nullable(),
	fts: z.unknown().optional(),
	headings: z.array(z.string()).optional().nullable(),
	id: z.string().optional(),
	label: z.string().optional().nullable(),
	original_content: z.string().optional().nullable(),
	page_number: z.number().optional().nullable()
});
const deviceModelFileChunksRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("device_model_file_chunks_device_model_file_id_fkey"),
	columns: z.tuple([z.literal("device_model_file_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("device_model_files"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const deviceModelFilesRowSchema = z.object({
	bucket_id: z.string(),
	created_at: z.string(),
	description: z.string().nullable(),
	id: z.string(),
	locale: z.string().nullable(),
	path: z.string(),
	processed_at: z.string().nullable(),
	shared_with_children: z.boolean(),
	thumbnail: z.string().nullable(),
	title: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const deviceModelFilesInsertSchema = z.object({
	bucket_id: z.string(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	locale: z.string().optional().nullable(),
	path: z.string(),
	processed_at: z.string().optional().nullable(),
	shared_with_children: z.boolean().optional(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const deviceModelFilesUpdateSchema = z.object({
	bucket_id: z.string().optional(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	locale: z.string().optional().nullable(),
	path: z.string().optional(),
	processed_at: z.string().optional().nullable(),
	shared_with_children: z.boolean().optional(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const deviceModelFilesRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("device_model_files_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("device_model_files_workspace_id_fkey"),
	columns: z.tuple([z.literal("workspace_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("workspaces"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const deviceModelProfileFilesRowSchema = z.object({
	bucket_id: z.string(),
	created_at: z.string(),
	device_model_id: z.string(),
	id: z.string(),
	path: z.string(),
	thumbnail: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const deviceModelProfileFilesInsertSchema = z.object({
	bucket_id: z.string(),
	created_at: z.string().optional(),
	device_model_id: z.string(),
	id: z.string().optional(),
	path: z.string(),
	thumbnail: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const deviceModelProfileFilesUpdateSchema = z.object({
	bucket_id: z.string().optional(),
	created_at: z.string().optional(),
	device_model_id: z.string().optional(),
	id: z.string().optional(),
	path: z.string().optional(),
	thumbnail: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const deviceModelProfileFilesRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("device_model_profile_files_device_id_fkey"),
	columns: z.tuple([z.literal("device_model_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("device_models"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("device_model_profile_files_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const deviceModelsRowSchema = z.object({
	created_at: z.string(),
	id: z.string(),
	is_public: z.boolean(),
	manufacturer_id: z.string(),
	name: z.string(),
	parent_id: z.string().nullable(),
	slug: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const deviceModelsInsertSchema = z.object({
	created_at: z.string().optional(),
	id: z.string().optional(),
	is_public: z.boolean().optional(),
	manufacturer_id: z.string(),
	name: z.string(),
	parent_id: z.string().optional().nullable(),
	slug: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const deviceModelsUpdateSchema = z.object({
	created_at: z.string().optional(),
	id: z.string().optional(),
	is_public: z.boolean().optional(),
	manufacturer_id: z.string().optional(),
	name: z.string().optional(),
	parent_id: z.string().optional().nullable(),
	slug: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const deviceModelsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("device_models_manufacturer_id_fkey"),
		columns: z.tuple([z.literal("manufacturer_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("manufacturers"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_models_parent_id_fkey"),
		columns: z.tuple([z.literal("parent_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_models"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_models_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_models_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const deviceModelsToDeviceModelFilesRowSchema = z.object({
	created_at: z.string(),
	device_model_file_id: z.string(),
	device_model_id: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const deviceModelsToDeviceModelFilesInsertSchema = z.object({
	created_at: z.string().optional(),
	device_model_file_id: z.string(),
	device_model_id: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const deviceModelsToDeviceModelFilesUpdateSchema = z.object({
	created_at: z.string().optional(),
	device_model_file_id: z.string().optional(),
	device_model_id: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const deviceModelsToDeviceModelFilesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("dm_to_dm_files_device_model_file_id_fkey"),
		columns: z.tuple([z.literal("device_model_file_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_model_files"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("dm_to_dm_files_device_model_id_fkey"),
		columns: z.tuple([z.literal("device_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_models"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("dm_to_dm_files_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const deviceOrdersRowSchema = z.object({
	created_at: z.string(),
	deployment_id: z.string(),
	device_id: z.string(),
	device_order_id: z.string().nullable(),
	finished_at: z.string().nullable(),
	name: z.string().nullable(),
	order_id: z.string(),
	parameters: jsonSchema.nullable(),
	started_at: z.string().nullable(),
	status: publicDeviceOrderStatusSchema,
	updated_at: z.string()
});
const deviceOrdersInsertSchema = z.object({
	created_at: z.string().optional(),
	deployment_id: z.string(),
	device_id: z.string(),
	device_order_id: z.string().optional().nullable(),
	finished_at: z.string().optional().nullable(),
	name: z.string().optional().nullable(),
	order_id: z.string(),
	parameters: jsonSchema.optional().nullable(),
	started_at: z.string().optional().nullable(),
	status: publicDeviceOrderStatusSchema,
	updated_at: z.string().optional()
});
const deviceOrdersUpdateSchema = z.object({
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	device_id: z.string().optional(),
	device_order_id: z.string().optional().nullable(),
	finished_at: z.string().optional().nullable(),
	name: z.string().optional().nullable(),
	order_id: z.string().optional(),
	parameters: jsonSchema.optional().nullable(),
	started_at: z.string().optional().nullable(),
	status: publicDeviceOrderStatusSchema.optional(),
	updated_at: z.string().optional()
});
const deviceOrdersRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("device_orders_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_orders_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_orders_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const deviceScalarValuesRowSchema = z.object({
	base_unit_id: z.string().nullable(),
	base_value: z.number().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	device_id: z.string(),
	id: z.number(),
	interval: z.number().nullable(),
	name: z.string(),
	unit_id: z.string(),
	updated_at: z.string(),
	value: z.number(),
	workspace_id: z.string()
});
const deviceScalarValuesInsertSchema = z.object({
	base_unit_id: z.string().optional().nullable(),
	base_value: z.number().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string(),
	device_id: z.string(),
	id: z.number().optional(),
	interval: z.number().optional().nullable(),
	name: z.string(),
	unit_id: z.string(),
	updated_at: z.string().optional(),
	value: z.number(),
	workspace_id: z.string()
});
const deviceScalarValuesUpdateSchema = z.object({
	base_unit_id: z.string().optional().nullable(),
	base_value: z.number().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	device_id: z.string().optional(),
	id: z.number().optional(),
	interval: z.number().optional().nullable(),
	name: z.string().optional(),
	unit_id: z.string().optional(),
	updated_at: z.string().optional(),
	value: z.number().optional(),
	workspace_id: z.string().optional()
});
const deviceScalarValuesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("device_scalar_values_base_unit_id_fkey"),
		columns: z.tuple([z.literal("base_unit_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("physical_units"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_scalar_values_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_scalar_values_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_scalar_values_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_scalar_values_unit_id_fkey"),
		columns: z.tuple([z.literal("unit_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("physical_units"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_scalar_values_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const deviceTagsRowSchema = z.object({
	created_at: z.string(),
	description: z.string().nullable(),
	id: z.string(),
	meta_data: jsonSchema.nullable(),
	name: z.string(),
	parent_device_tag_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const deviceTagsInsertSchema = z.object({
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	meta_data: jsonSchema.optional().nullable(),
	name: z.string(),
	parent_device_tag_id: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const deviceTagsUpdateSchema = z.object({
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	meta_data: jsonSchema.optional().nullable(),
	name: z.string().optional(),
	parent_device_tag_id: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const deviceTagsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("device_tags_parent_device_tag_id_fkey"),
		columns: z.tuple([z.literal("parent_device_tag_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_tags"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_tags_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("device_tags_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const devicesRowSchema = z.object({
	created_at: z.string(),
	deleted_at: z.string().nullable(),
	deleted_by: z.string().nullable(),
	deployment_id: z.string(),
	id: z.string(),
	last_auth: z.string().nullable(),
	manufactured_at: z.string().nullable(),
	manufacturer_id: z.string().nullable(),
	model_id: z.string(),
	name: z.string(),
	portal_token: z.string(),
	ref: z.string().nullable(),
	software_version: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const devicesInsertSchema = z.object({
	created_at: z.string().optional(),
	deleted_at: z.string().optional().nullable(),
	deleted_by: z.string().optional().nullable(),
	deployment_id: z.string(),
	id: z.string().optional(),
	last_auth: z.string().optional().nullable(),
	manufactured_at: z.string().optional().nullable(),
	manufacturer_id: z.string().optional().nullable(),
	model_id: z.string(),
	name: z.string(),
	portal_token: z.string().optional(),
	ref: z.string().optional().nullable(),
	software_version: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const devicesUpdateSchema = z.object({
	created_at: z.string().optional(),
	deleted_at: z.string().optional().nullable(),
	deleted_by: z.string().optional().nullable(),
	deployment_id: z.string().optional(),
	id: z.string().optional(),
	last_auth: z.string().optional().nullable(),
	manufactured_at: z.string().optional().nullable(),
	manufacturer_id: z.string().optional().nullable(),
	model_id: z.string().optional(),
	name: z.string().optional(),
	portal_token: z.string().optional(),
	ref: z.string().optional().nullable(),
	software_version: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const devicesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("devices_deleted_by_fkey"),
		columns: z.tuple([z.literal("deleted_by")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_manufacturer_id_fkey"),
		columns: z.tuple([z.literal("manufacturer_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("manufacturers"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_model_id_fkey"),
		columns: z.tuple([z.literal("model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_models"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const devicesHistoryRowSchema = z.object({
	changed_fields: z.array(z.string()).nullable(),
	created_at: z.string().nullable(),
	deployment_id: z.string().nullable(),
	device_id: z.string().nullable(),
	id: z.string(),
	new_val: jsonSchema.nullable(),
	old_val: jsonSchema.nullable(),
	operation: publicDbOperationSchema.nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const devicesHistoryInsertSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	deployment_id: z.string().optional().nullable(),
	device_id: z.string().optional().nullable(),
	id: z.string().optional(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const devicesHistoryUpdateSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	deployment_id: z.string().optional().nullable(),
	device_id: z.string().optional().nullable(),
	id: z.string().optional(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const devicesHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("devices_history_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_history_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_history_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_history_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const devicesToDeviceTagsRowSchema = z.object({
	created_at: z.string(),
	device_id: z.string(),
	device_tag_id: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const devicesToDeviceTagsInsertSchema = z.object({
	created_at: z.string().optional(),
	device_id: z.string(),
	device_tag_id: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const devicesToDeviceTagsUpdateSchema = z.object({
	created_at: z.string().optional(),
	device_id: z.string().optional(),
	device_tag_id: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const devicesToDeviceTagsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("devices_to_device_tags_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_to_device_tags_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_to_device_tags_device_tag_id_fkey"),
		columns: z.tuple([z.literal("device_tag_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_tags"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_to_device_tags_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenanceApprovalFilesRowSchema = z.object({
	bucket_id: z.string(),
	created_at: z.string(),
	description: z.string().nullable(),
	id: z.string(),
	locale: z.string().nullable(),
	maintenance_approval_id: z.string(),
	path: z.string(),
	thumbnail: z.string().nullable(),
	title: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const maintenanceApprovalFilesInsertSchema = z.object({
	bucket_id: z.string(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	locale: z.string().optional().nullable(),
	maintenance_approval_id: z.string(),
	path: z.string(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenanceApprovalFilesUpdateSchema = z.object({
	bucket_id: z.string().optional(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	locale: z.string().optional().nullable(),
	maintenance_approval_id: z.string().optional(),
	path: z.string().optional(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenanceApprovalFilesRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("maintenance_approval_files_maintenance_approval_id_fkey"),
	columns: z.tuple([z.literal("maintenance_approval_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("maintenance_approvals"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("maintenance_approval_files_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const maintenanceApprovalsRowSchema = z.object({
	approver_email: z.string().nullable(),
	approver_id: z.string().nullable(),
	approver_name: z.string().nullable(),
	created_at: z.string(),
	id: z.string(),
	maintenance_id: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const maintenanceApprovalsInsertSchema = z.object({
	approver_email: z.string().optional().nullable(),
	approver_id: z.string().optional().nullable(),
	approver_name: z.string().optional().nullable(),
	created_at: z.string().optional(),
	id: z.string().optional(),
	maintenance_id: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenanceApprovalsUpdateSchema = z.object({
	approver_email: z.string().optional().nullable(),
	approver_id: z.string().optional().nullable(),
	approver_name: z.string().optional().nullable(),
	created_at: z.string().optional(),
	id: z.string().optional(),
	maintenance_id: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenanceApprovalsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenance_approvals_approver_id_fkey"),
		columns: z.tuple([z.literal("approver_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_approvals_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_approvals_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_approvals_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_approvals_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenanceApprovalsHistoryRowSchema = z.object({
	changed_fields: z.array(z.string()).nullable(),
	created_at: z.string().nullable(),
	id: z.string(),
	maintenance_approval_id: z.string().nullable(),
	maintenance_id: z.string().nullable(),
	new_val: jsonSchema.nullable(),
	old_val: jsonSchema.nullable(),
	operation: publicDbOperationSchema.nullable(),
	user_id: z.string().nullable()
});
const maintenanceApprovalsHistoryInsertSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_approval_id: z.string().optional().nullable(),
	maintenance_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable()
});
const maintenanceApprovalsHistoryUpdateSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_approval_id: z.string().optional().nullable(),
	maintenance_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable()
});
const maintenanceApprovalsHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenance_approvals_history_maintenance_approval_id_fkey"),
		columns: z.tuple([z.literal("maintenance_approval_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_approvals"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_approvals_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_approvals_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_approvals_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_approvals_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenanceItemFilesRowSchema = z.object({
	bucket_id: z.string(),
	created_at: z.string(),
	description: z.string().nullable(),
	id: z.string(),
	locale: z.string().nullable(),
	maintenance_item_id: z.string(),
	path: z.string(),
	thumbnail: z.string().nullable(),
	title: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const maintenanceItemFilesInsertSchema = z.object({
	bucket_id: z.string(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	locale: z.string().optional().nullable(),
	maintenance_item_id: z.string(),
	path: z.string(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenanceItemFilesUpdateSchema = z.object({
	bucket_id: z.string().optional(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	locale: z.string().optional().nullable(),
	maintenance_item_id: z.string().optional(),
	path: z.string().optional(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenanceItemFilesRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("maintenance_item_files_maintenance_item_id_fkey"),
	columns: z.tuple([z.literal("maintenance_item_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("maintenance_items"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("maintenance_item_files_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const maintenanceItemTemplateFilesRowSchema = z.object({
	bucket_id: z.string(),
	checklist_template_item_file_id: z.string().nullable(),
	created_at: z.string(),
	description: z.string().nullable(),
	id: z.string(),
	locale: z.string().nullable(),
	maintenance_item_id: z.string(),
	ordering: z.number(),
	path: z.string(),
	thumbnail: z.string().nullable(),
	title: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const maintenanceItemTemplateFilesInsertSchema = z.object({
	bucket_id: z.string(),
	checklist_template_item_file_id: z.string().optional().nullable(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	locale: z.string().optional().nullable(),
	maintenance_item_id: z.string(),
	ordering: z.number().optional(),
	path: z.string(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenanceItemTemplateFilesUpdateSchema = z.object({
	bucket_id: z.string().optional(),
	checklist_template_item_file_id: z.string().optional().nullable(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	locale: z.string().optional().nullable(),
	maintenance_item_id: z.string().optional(),
	ordering: z.number().optional(),
	path: z.string().optional(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenanceItemTemplateFilesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenance_item_template_files_checklist_template_item_file_id"),
		columns: z.tuple([z.literal("checklist_template_item_file_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("checklist_template_item_files"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_item_template_files_maintenance_item_id_fkey"),
		columns: z.tuple([z.literal("maintenance_item_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_items"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_item_template_files_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenanceItemsRowSchema = z.object({
	assignee_id: z.string().nullable(),
	checklist_template_item_id: z.string().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	description_html: z.string().nullable(),
	device_id: z.string().nullable(),
	duration: z.string().nullable(),
	id: z.string(),
	is_custom: z.boolean().nullable(),
	maintenance_id: z.string().nullable(),
	name: z.string(),
	notes: z.string(),
	notes_html: z.string(),
	ordering: z.number(),
	resolution_duration: z.string().nullable(),
	resolved_at: z.string().nullable(),
	started_at: z.string().nullable(),
	status: publicMaintenanceItemStatusSchema,
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const maintenanceItemsInsertSchema = z.object({
	assignee_id: z.string().optional().nullable(),
	checklist_template_item_id: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string(),
	description: z.string().optional().nullable(),
	description_html: z.string().optional().nullable(),
	device_id: z.string().optional().nullable(),
	duration: z.string().optional().nullable(),
	id: z.string().optional(),
	is_custom: z.boolean().optional().nullable(),
	maintenance_id: z.string().optional().nullable(),
	name: z.string(),
	notes: z.string().optional(),
	notes_html: z.string().optional(),
	ordering: z.number().optional(),
	resolution_duration: z.string().optional().nullable(),
	resolved_at: z.string().optional().nullable(),
	started_at: z.string().optional().nullable(),
	status: publicMaintenanceItemStatusSchema.optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const maintenanceItemsUpdateSchema = z.object({
	assignee_id: z.string().optional().nullable(),
	checklist_template_item_id: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	description: z.string().optional().nullable(),
	description_html: z.string().optional().nullable(),
	device_id: z.string().optional().nullable(),
	duration: z.string().optional().nullable(),
	id: z.string().optional(),
	is_custom: z.boolean().optional().nullable(),
	maintenance_id: z.string().optional().nullable(),
	name: z.string().optional(),
	notes: z.string().optional(),
	notes_html: z.string().optional(),
	ordering: z.number().optional(),
	resolution_duration: z.string().optional().nullable(),
	resolved_at: z.string().optional().nullable(),
	started_at: z.string().optional().nullable(),
	status: publicMaintenanceItemStatusSchema.optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const maintenanceItemsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenance_items_assignee_id_fkey"),
		columns: z.tuple([z.literal("assignee_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_checklist_template_item_id_fkey"),
		columns: z.tuple([z.literal("checklist_template_item_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("checklist_template_items"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenanceItemsToPartsRowSchema = z.object({
	action: publicPartUsageTypeSchema,
	created_at: z.string(),
	id: z.string(),
	maintenance_item_id: z.string(),
	notes: z.string().nullable(),
	part_category_id: z.string().nullable(),
	part_id: z.string().nullable(),
	part_location_id: z.string().nullable(),
	part_model_id: z.string().nullable(),
	quantity: z.number().nullable(),
	skip_inventory: z.boolean().nullable(),
	type: publicMaintenanceItemsToPartsTypeSchema.nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const maintenanceItemsToPartsInsertSchema = z.object({
	action: publicPartUsageTypeSchema.optional(),
	created_at: z.string().optional(),
	id: z.string().optional(),
	maintenance_item_id: z.string(),
	notes: z.string().optional().nullable(),
	part_category_id: z.string().optional().nullable(),
	part_id: z.string().optional().nullable(),
	part_location_id: z.string().optional().nullable(),
	part_model_id: z.string().optional().nullable(),
	quantity: z.number().optional().nullable(),
	skip_inventory: z.boolean().optional().nullable(),
	type: publicMaintenanceItemsToPartsTypeSchema.optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenanceItemsToPartsUpdateSchema = z.object({
	action: publicPartUsageTypeSchema.optional(),
	created_at: z.string().optional(),
	id: z.string().optional(),
	maintenance_item_id: z.string().optional(),
	notes: z.string().optional().nullable(),
	part_category_id: z.string().optional().nullable(),
	part_id: z.string().optional().nullable(),
	part_location_id: z.string().optional().nullable(),
	part_model_id: z.string().optional().nullable(),
	quantity: z.number().optional().nullable(),
	skip_inventory: z.boolean().optional().nullable(),
	type: publicMaintenanceItemsToPartsTypeSchema.optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenanceItemsToPartsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenance_items_to_parts_maintenance_item_id_fkey"),
		columns: z.tuple([z.literal("maintenance_item_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_items"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_to_parts_part_category_id_fkey"),
		columns: z.tuple([z.literal("part_category_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_categories"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_to_parts_part_id_fkey"),
		columns: z.tuple([z.literal("part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_to_parts_part_id_fkey"),
		columns: z.tuple([z.literal("part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("parts"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_to_parts_part_location_id_fkey"),
		columns: z.tuple([z.literal("part_location_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_locations"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_to_parts_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_model_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_to_parts_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_models"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_items_to_parts_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenanceStrategiesRowSchema = z.object({
	assignee_id: z.string().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string(),
	duration: z.string().nullable(),
	excluded_dates: z.array(z.string()),
	id: z.string(),
	is_active: z.boolean(),
	schedule_interval: z.string(),
	title: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const maintenanceStrategiesInsertSchema = z.object({
	assignee_id: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string(),
	description: z.string().optional(),
	duration: z.string().optional().nullable(),
	excluded_dates: z.array(z.string()).optional(),
	id: z.string().optional(),
	is_active: z.boolean().optional(),
	schedule_interval: z.string(),
	title: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const maintenanceStrategiesUpdateSchema = z.object({
	assignee_id: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	description: z.string().optional(),
	duration: z.string().optional().nullable(),
	excluded_dates: z.array(z.string()).optional(),
	id: z.string().optional(),
	is_active: z.boolean().optional(),
	schedule_interval: z.string().optional(),
	title: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const maintenanceStrategiesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenance_strategies_assignee_id_fkey"),
		columns: z.tuple([z.literal("assignee_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_strategies_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_strategies_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_strategies_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenanceStrategiesHistoryRowSchema = z.object({
	changed_fields: z.array(z.string()).nullable(),
	created_at: z.string().nullable(),
	deployment_id: z.string().nullable(),
	id: z.string(),
	maintenance_strategy_id: z.string().nullable(),
	new_val: jsonSchema.nullable(),
	old_val: jsonSchema.nullable(),
	operation: publicDbOperationSchema.nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const maintenanceStrategiesHistoryInsertSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	deployment_id: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_strategy_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const maintenanceStrategiesHistoryUpdateSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	deployment_id: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_strategy_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const maintenanceStrategiesHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("m_strategies_history_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_strategies_history_maintenance_strategy_id_fkey"),
		columns: z.tuple([z.literal("maintenance_strategy_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_strategies"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_strategies_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_strategies_history_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenanceStrategiesToDevicesRowSchema = z.object({
	checklist_template_id: z.string().nullable(),
	created_at: z.string(),
	device_id: z.string(),
	maintenance_strategy_id: z.string(),
	ordering: z.number(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const maintenanceStrategiesToDevicesInsertSchema = z.object({
	checklist_template_id: z.string().optional().nullable(),
	created_at: z.string().optional(),
	device_id: z.string(),
	maintenance_strategy_id: z.string(),
	ordering: z.number().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenanceStrategiesToDevicesUpdateSchema = z.object({
	checklist_template_id: z.string().optional().nullable(),
	created_at: z.string().optional(),
	device_id: z.string().optional(),
	maintenance_strategy_id: z.string().optional(),
	ordering: z.number().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenanceStrategiesToDevicesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenance_strategies_to_devices_checklist_template_id_fkey"),
		columns: z.tuple([z.literal("checklist_template_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("checklist_templates"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_strategies_to_devices_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_strategies_to_devices_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_strategies_to_devices_maintenance_strategy_id_fkey"),
		columns: z.tuple([z.literal("maintenance_strategy_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_strategies"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenance_strategies_to_devices_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenanceStrategiesToMaintenanceTagsRowSchema = z.object({
	created_at: z.string(),
	maintenance_strategy_id: z.string(),
	maintenance_tag_id: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const maintenanceStrategiesToMaintenanceTagsInsertSchema = z.object({
	created_at: z.string().optional(),
	maintenance_strategy_id: z.string(),
	maintenance_tag_id: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenanceStrategiesToMaintenanceTagsUpdateSchema = z.object({
	created_at: z.string().optional(),
	maintenance_strategy_id: z.string().optional(),
	maintenance_tag_id: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenanceStrategiesToMaintenanceTagsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("m_strategies_to_maintenance_tags_maintenance_strategy_id_fkey"),
		columns: z.tuple([z.literal("maintenance_strategy_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_strategies"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_strategies_to_maintenance_tags_maintenance_tag_id_fkey"),
		columns: z.tuple([z.literal("maintenance_tag_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_tags"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_strategies_to_maintenance_tags_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenanceTagsRowSchema = z.object({
	created_at: z.string(),
	description: z.string().nullable(),
	id: z.string(),
	name: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const maintenanceTagsInsertSchema = z.object({
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	name: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const maintenanceTagsUpdateSchema = z.object({
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	name: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const maintenanceTagsRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("maintenance_tags_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("maintenance_tags_workspace_id_fkey"),
	columns: z.tuple([z.literal("workspace_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("workspaces"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const maintenancesRowSchema = z.object({
	assignee_id: z.string().nullable(),
	content_planned_duration: z.string().nullable(),
	content_resolution_duration: z.string().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	id: z.string(),
	maintenance_strategy_id: z.string().nullable(),
	notes: z.string(),
	part_location_id: z.string().nullable(),
	planned_at: z.string().nullable(),
	planned_date: z.string().nullable(),
	planned_date_until: z.string().nullable(),
	planned_duration: z.string().nullable(),
	planned_until: z.string().nullable(),
	public_id: z.string(),
	resolution_duration: z.string().nullable(),
	resolved_at: z.string().nullable(),
	scheduled_at: z.string().nullable(),
	scheduled_duration: z.string().nullable(),
	scheduled_until: z.string().nullable(),
	serial_id: z.number(),
	serial_id_prefix: z.string(),
	skip_reason: z.string().nullable(),
	started_at: z.string().nullable(),
	status: publicMaintenanceStatusSchema,
	title: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const maintenancesInsertSchema = z.object({
	assignee_id: z.string().optional().nullable(),
	content_planned_duration: z.string().optional().nullable(),
	content_resolution_duration: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string(),
	id: z.string().optional(),
	maintenance_strategy_id: z.string().optional().nullable(),
	notes: z.string().optional(),
	part_location_id: z.string().optional().nullable(),
	planned_at: z.string().optional().nullable(),
	planned_date: z.string().optional().nullable(),
	planned_date_until: z.string().optional().nullable(),
	planned_duration: z.string().optional().nullable(),
	planned_until: z.string().optional().nullable(),
	public_id: z.string().optional(),
	resolution_duration: z.string().optional().nullable(),
	resolved_at: z.string().optional().nullable(),
	scheduled_at: z.string().optional().nullable(),
	scheduled_duration: z.string().optional().nullable(),
	scheduled_until: z.string().optional().nullable(),
	serial_id: z.number().optional(),
	serial_id_prefix: z.string().optional(),
	skip_reason: z.string().optional().nullable(),
	started_at: z.string().optional().nullable(),
	status: publicMaintenanceStatusSchema.optional(),
	title: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const maintenancesUpdateSchema = z.object({
	assignee_id: z.string().optional().nullable(),
	content_planned_duration: z.string().optional().nullable(),
	content_resolution_duration: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	id: z.string().optional(),
	maintenance_strategy_id: z.string().optional().nullable(),
	notes: z.string().optional(),
	part_location_id: z.string().optional().nullable(),
	planned_at: z.string().optional().nullable(),
	planned_date: z.string().optional().nullable(),
	planned_date_until: z.string().optional().nullable(),
	planned_duration: z.string().optional().nullable(),
	planned_until: z.string().optional().nullable(),
	public_id: z.string().optional(),
	resolution_duration: z.string().optional().nullable(),
	resolved_at: z.string().optional().nullable(),
	scheduled_at: z.string().optional().nullable(),
	scheduled_duration: z.string().optional().nullable(),
	scheduled_until: z.string().optional().nullable(),
	serial_id: z.number().optional(),
	serial_id_prefix: z.string().optional(),
	skip_reason: z.string().optional().nullable(),
	started_at: z.string().optional().nullable(),
	status: publicMaintenanceStatusSchema.optional(),
	title: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const maintenancesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenances_assignee_id_fkey"),
		columns: z.tuple([z.literal("assignee_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_maintenance_strategy_id_fkey"),
		columns: z.tuple([z.literal("maintenance_strategy_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_strategies"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_part_location_id_fkey"),
		columns: z.tuple([z.literal("part_location_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_locations"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenancesHistoryRowSchema = z.object({
	changed_fields: z.array(z.string()).nullable(),
	created_at: z.string().nullable(),
	deployment_id: z.string().nullable(),
	id: z.string(),
	maintenance_id: z.string().nullable(),
	new_val: jsonSchema.nullable(),
	old_val: jsonSchema.nullable(),
	operation: publicDbOperationSchema.nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const maintenancesHistoryInsertSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	deployment_id: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const maintenancesHistoryUpdateSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	deployment_id: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const maintenancesHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenances_history_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_history_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenancesToDeviceDataTriggersRowSchema = z.object({
	created_at: z.string(),
	device_data_trigger_id: z.string(),
	maintenance_id: z.string(),
	updated_at: z.string()
});
const maintenancesToDeviceDataTriggersInsertSchema = z.object({
	created_at: z.string().optional(),
	device_data_trigger_id: z.string(),
	maintenance_id: z.string(),
	updated_at: z.string().optional()
});
const maintenancesToDeviceDataTriggersUpdateSchema = z.object({
	created_at: z.string().optional(),
	device_data_trigger_id: z.string().optional(),
	maintenance_id: z.string().optional(),
	updated_at: z.string().optional()
});
const maintenancesToDeviceDataTriggersRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenances_to_device_data_triggers_device_data_trigger_id_fke"),
		columns: z.tuple([z.literal("device_data_trigger_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_data_triggers"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_device_data_triggers_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_device_data_triggers_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_device_data_triggers_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	})
]);
const maintenancesToDevicesRowSchema = z.object({
	checklist_template_id: z.string().nullable(),
	created_at: z.string(),
	device_id: z.string(),
	maintenance_id: z.string(),
	ordering: z.number(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const maintenancesToDevicesInsertSchema = z.object({
	checklist_template_id: z.string().optional().nullable(),
	created_at: z.string().optional(),
	device_id: z.string(),
	maintenance_id: z.string(),
	ordering: z.number().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenancesToDevicesUpdateSchema = z.object({
	checklist_template_id: z.string().optional().nullable(),
	created_at: z.string().optional(),
	device_id: z.string().optional(),
	maintenance_id: z.string().optional(),
	ordering: z.number().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenancesToDevicesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenances_to_devices_checklist_template_id_fkey"),
		columns: z.tuple([z.literal("checklist_template_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("checklist_templates"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_devices_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_devices_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_devices_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_devices_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_devices_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_devices_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenancesToDevicesHistoryRowSchema = z.object({
	changed_fields: z.array(z.string()).nullable(),
	created_at: z.string().nullable(),
	device_id: z.string().nullable(),
	id: z.string(),
	maintenance_id: z.string().nullable(),
	new_val: jsonSchema.nullable(),
	old_val: jsonSchema.nullable(),
	operation: publicDbOperationSchema.nullable(),
	user_id: z.string().nullable()
});
const maintenancesToDevicesHistoryInsertSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	device_id: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable()
});
const maintenancesToDevicesHistoryUpdateSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	device_id: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable()
});
const maintenancesToDevicesHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenances_to_devices_history_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_devices_history_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_devices_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_devices_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_devices_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_devices_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenancesToMaintenanceTagsRowSchema = z.object({
	created_at: z.string(),
	maintenance_id: z.string(),
	maintenance_tag_id: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const maintenancesToMaintenanceTagsInsertSchema = z.object({
	created_at: z.string().optional(),
	maintenance_id: z.string(),
	maintenance_tag_id: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenancesToMaintenanceTagsUpdateSchema = z.object({
	created_at: z.string().optional(),
	maintenance_id: z.string().optional(),
	maintenance_tag_id: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenancesToMaintenanceTagsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenances_to_maintenance_tags_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_maintenance_tags_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_maintenance_tags_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_maintenance_tags_maintenance_tag_id_fkey"),
		columns: z.tuple([z.literal("maintenance_tag_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_tags"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_maintenance_tags_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenancesToMaintenanceTagsHistoryRowSchema = z.object({
	changed_fields: z.array(z.string()).nullable(),
	created_at: z.string().nullable(),
	id: z.string(),
	maintenance_id: z.string().nullable(),
	maintenance_tag_id: z.string().nullable(),
	new_val: jsonSchema.nullable(),
	old_val: jsonSchema.nullable(),
	operation: publicDbOperationSchema.nullable(),
	user_id: z.string().nullable()
});
const maintenancesToMaintenanceTagsHistoryInsertSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_id: z.string().optional().nullable(),
	maintenance_tag_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable()
});
const maintenancesToMaintenanceTagsHistoryUpdateSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_id: z.string().optional().nullable(),
	maintenance_tag_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable()
});
const maintenancesToMaintenanceTagsHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenances_to_maintenance_tags_histor_maintenance_tag_id_fkey"),
		columns: z.tuple([z.literal("maintenance_tag_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_tags"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_maintenance_tags_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_maintenance_tags_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_maintenance_tags_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_maintenance_tags_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenancesToMaintenancesRowSchema = z.object({
	created_at: z.string(),
	maintenance_child_id: z.string(),
	maintenance_item_id: z.string().nullable(),
	maintenance_parent_id: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const maintenancesToMaintenancesInsertSchema = z.object({
	created_at: z.string().optional(),
	maintenance_child_id: z.string(),
	maintenance_item_id: z.string().optional().nullable(),
	maintenance_parent_id: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenancesToMaintenancesUpdateSchema = z.object({
	created_at: z.string().optional(),
	maintenance_child_id: z.string().optional(),
	maintenance_item_id: z.string().optional().nullable(),
	maintenance_parent_id: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenancesToMaintenancesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("m_to_m_item_id_fkey"),
		columns: z.tuple([z.literal("maintenance_item_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_items"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_m_maintenance_child_id_fkey"),
		columns: z.tuple([z.literal("maintenance_child_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_m_maintenance_child_id_fkey"),
		columns: z.tuple([z.literal("maintenance_child_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_m_maintenance_child_id_fkey"),
		columns: z.tuple([z.literal("maintenance_child_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_m_maintenance_parent_id_fkey"),
		columns: z.tuple([z.literal("maintenance_parent_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_m_maintenance_parent_id_fkey"),
		columns: z.tuple([z.literal("maintenance_parent_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_m_maintenance_parent_id_fkey"),
		columns: z.tuple([z.literal("maintenance_parent_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_m_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenancesToMaintenancesHistoryRowSchema = z.object({
	changed_fields: z.array(z.string()).nullable(),
	created_at: z.string().nullable(),
	id: z.string(),
	maintenance_child_id: z.string().nullable(),
	maintenance_parent_id: z.string().nullable(),
	new_val: jsonSchema.nullable(),
	old_val: jsonSchema.nullable(),
	operation: publicDbOperationSchema.nullable(),
	user_id: z.string().nullable()
});
const maintenancesToMaintenancesHistoryInsertSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_child_id: z.string().optional().nullable(),
	maintenance_parent_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable()
});
const maintenancesToMaintenancesHistoryUpdateSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_child_id: z.string().optional().nullable(),
	maintenance_parent_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	user_id: z.string().optional().nullable()
});
const maintenancesToMaintenancesHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("m_to_m_history_maintenance_child_id_fkey"),
		columns: z.tuple([z.literal("maintenance_child_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_m_history_maintenance_child_id_fkey"),
		columns: z.tuple([z.literal("maintenance_child_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_m_history_maintenance_child_id_fkey"),
		columns: z.tuple([z.literal("maintenance_child_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_m_history_maintenance_parent_id_fkey"),
		columns: z.tuple([z.literal("maintenance_parent_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_m_history_maintenance_parent_id_fkey"),
		columns: z.tuple([z.literal("maintenance_parent_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_m_history_maintenance_parent_id_fkey"),
		columns: z.tuple([z.literal("maintenance_parent_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_m_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenancesToPartModelsRequestedRowSchema = z.object({
	created_at: z.string(),
	deployment_id: z.string(),
	id: z.string(),
	maintenance_id: z.string(),
	notes: z.string().nullable(),
	part_model_id: z.string(),
	quantity: z.number(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const maintenancesToPartModelsRequestedInsertSchema = z.object({
	created_at: z.string().optional(),
	deployment_id: z.string(),
	id: z.string().optional(),
	maintenance_id: z.string(),
	notes: z.string().optional().nullable(),
	part_model_id: z.string(),
	quantity: z.number().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const maintenancesToPartModelsRequestedUpdateSchema = z.object({
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	id: z.string().optional(),
	maintenance_id: z.string().optional(),
	notes: z.string().optional().nullable(),
	part_model_id: z.string().optional(),
	quantity: z.number().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const maintenancesToPartModelsRequestedRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_model_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_models"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenancesToServiceJobsRowSchema = z.object({
	created_at: z.string(),
	maintenance_id: z.string(),
	service_job_id: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const maintenancesToServiceJobsInsertSchema = z.object({
	created_at: z.string().optional(),
	maintenance_id: z.string(),
	service_job_id: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenancesToServiceJobsUpdateSchema = z.object({
	created_at: z.string().optional(),
	maintenance_id: z.string().optional(),
	service_job_id: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const maintenancesToServiceJobsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("m_to_service_jobs_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_service_jobs_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_service_jobs_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_service_jobs_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_job_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_service_jobs_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_job_details_full"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_service_jobs_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_jobs"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("m_to_service_jobs_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenancesToServiceJobsHistoryRowSchema = z.object({
	changed_fields: z.array(z.string()).nullable(),
	created_at: z.string().nullable(),
	id: z.string(),
	maintenance_id: z.string().nullable(),
	new_val: jsonSchema.nullable(),
	old_val: jsonSchema.nullable(),
	operation: publicDbOperationSchema.nullable(),
	service_job_id: z.string().nullable(),
	user_id: z.string().nullable()
});
const maintenancesToServiceJobsHistoryInsertSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	service_job_id: z.string().optional().nullable(),
	user_id: z.string().optional().nullable()
});
const maintenancesToServiceJobsHistoryUpdateSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	maintenance_id: z.string().optional().nullable(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	service_job_id: z.string().optional().nullable(),
	user_id: z.string().optional().nullable()
});
const maintenancesToServiceJobsHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenances_to_service_jobs_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_service_jobs_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_service_jobs_history_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_service_jobs_history_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_job_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_service_jobs_history_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_job_details_full"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_service_jobs_history_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_jobs"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_service_jobs_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const manufacturersRowSchema = z.object({
	created_at: z.string(),
	id: z.string(),
	is_public: z.boolean(),
	name: z.string(),
	parent_id: z.string().nullable(),
	slug: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const manufacturersInsertSchema = z.object({
	created_at: z.string().optional(),
	id: z.string().optional(),
	is_public: z.boolean().optional(),
	name: z.string(),
	parent_id: z.string().optional().nullable(),
	slug: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const manufacturersUpdateSchema = z.object({
	created_at: z.string().optional(),
	id: z.string().optional(),
	is_public: z.boolean().optional(),
	name: z.string().optional(),
	parent_id: z.string().optional().nullable(),
	slug: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const manufacturersRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("manufacturers_parent_id_fkey"),
		columns: z.tuple([z.literal("parent_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("manufacturers"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("manufacturers_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("manufacturers_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const partCategoriesRowSchema = z.object({
	created_at: z.string(),
	id: z.string(),
	name: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const partCategoriesInsertSchema = z.object({
	created_at: z.string().optional(),
	id: z.string().optional(),
	name: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const partCategoriesUpdateSchema = z.object({
	created_at: z.string().optional(),
	id: z.string().optional(),
	name: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const partCategoriesRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("part_categories_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("part_categories_workspace_id_fkey"),
	columns: z.tuple([z.literal("workspace_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("workspaces"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const partInventoriesRowSchema = z.object({
	action: publicInventoryActionSchema,
	created_at: z.string(),
	deployment_id: z.string(),
	executed_at: z.string(),
	id: z.string(),
	maintenance_item_id: z.string().nullable(),
	note: z.string().nullable(),
	package_number: z.string().nullable(),
	package_price: z.number().nullable(),
	part_id: z.string(),
	quantity: z.number().nullable(),
	source_part_id: z.string().nullable(),
	target_part_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const partInventoriesInsertSchema = z.object({
	action: publicInventoryActionSchema.optional(),
	created_at: z.string().optional(),
	deployment_id: z.string(),
	executed_at: z.string().optional(),
	id: z.string().optional(),
	maintenance_item_id: z.string().optional().nullable(),
	note: z.string().optional().nullable(),
	package_number: z.string().optional().nullable(),
	package_price: z.number().optional().nullable(),
	part_id: z.string(),
	quantity: z.number().optional().nullable(),
	source_part_id: z.string().optional().nullable(),
	target_part_id: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partInventoriesUpdateSchema = z.object({
	action: publicInventoryActionSchema.optional(),
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	executed_at: z.string().optional(),
	id: z.string().optional(),
	maintenance_item_id: z.string().optional().nullable(),
	note: z.string().optional().nullable(),
	package_number: z.string().optional().nullable(),
	package_price: z.number().optional().nullable(),
	part_id: z.string().optional(),
	quantity: z.number().optional().nullable(),
	source_part_id: z.string().optional().nullable(),
	target_part_id: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partInventoriesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("part_inventories_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_inventories_maintenance_item_id_fkey"),
		columns: z.tuple([z.literal("maintenance_item_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_items"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_inventories_part_id_fkey"),
		columns: z.tuple([z.literal("part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_inventories_part_id_fkey"),
		columns: z.tuple([z.literal("part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("parts"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_inventories_source_part_id_fkey"),
		columns: z.tuple([z.literal("source_part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("parts_history"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_inventories_target_part_id_fkey"),
		columns: z.tuple([z.literal("target_part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("parts_history"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_inventories_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const partInventoriesToLocationsRowSchema = z.object({
	created_at: z.string(),
	id: z.string(),
	location_type: publicInventoryLocationTypeSchema,
	part_inventory_id: z.string(),
	part_location_id: z.string(),
	quantity: z.number(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const partInventoriesToLocationsInsertSchema = z.object({
	created_at: z.string().optional(),
	id: z.string().optional(),
	location_type: publicInventoryLocationTypeSchema,
	part_inventory_id: z.string(),
	part_location_id: z.string(),
	quantity: z.number(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partInventoriesToLocationsUpdateSchema = z.object({
	created_at: z.string().optional(),
	id: z.string().optional(),
	location_type: publicInventoryLocationTypeSchema.optional(),
	part_inventory_id: z.string().optional(),
	part_location_id: z.string().optional(),
	quantity: z.number().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partInventoriesToLocationsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("part_inventories_to_locations_part_inventory_id_fkey"),
		columns: z.tuple([z.literal("part_inventory_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_inventories"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_inventories_to_locations_part_location_id_fkey"),
		columns: z.tuple([z.literal("part_location_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_locations"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_inventories_to_locations_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const partInventoryFilesRowSchema = z.object({
	bucket_id: z.string(),
	created_at: z.string(),
	description: z.string().nullable(),
	id: z.string(),
	locale: z.string().nullable(),
	part_inventory_id: z.string(),
	path: z.string(),
	thumbnail: z.string().nullable(),
	title: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const partInventoryFilesInsertSchema = z.object({
	bucket_id: z.string(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	locale: z.string().optional().nullable(),
	part_inventory_id: z.string(),
	path: z.string(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partInventoryFilesUpdateSchema = z.object({
	bucket_id: z.string().optional(),
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	locale: z.string().optional().nullable(),
	part_inventory_id: z.string().optional(),
	path: z.string().optional(),
	thumbnail: z.string().optional().nullable(),
	title: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partInventoryFilesRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("part_inventory_files_part_inventory_id_fkey"),
	columns: z.tuple([z.literal("part_inventory_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("part_inventories"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("part_inventory_files_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const partLocationTemplatesRowSchema = z.object({
	created_at: z.string(),
	deployment_id: z.string(),
	id: z.string(),
	name: z.string(),
	parent_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const partLocationTemplatesInsertSchema = z.object({
	created_at: z.string().optional(),
	deployment_id: z.string(),
	id: z.string().optional(),
	name: z.string(),
	parent_id: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partLocationTemplatesUpdateSchema = z.object({
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	id: z.string().optional(),
	name: z.string().optional(),
	parent_id: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partLocationTemplatesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("part_location_templates_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_location_templates_parent_id_fkey"),
		columns: z.tuple([z.literal("parent_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_location_templates"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_location_templates_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const partLocationsRowSchema = z.object({
	archived_at: z.string().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	id: z.string(),
	level: z.number(),
	name: z.string(),
	parent_part_location_id: z.string().nullable(),
	part_location_template_id: z.string(),
	type: publicPartLocationTypeSchema,
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const partLocationsInsertSchema = z.object({
	archived_at: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string(),
	id: z.string().optional(),
	level: z.number().optional(),
	name: z.string(),
	parent_part_location_id: z.string().optional().nullable(),
	part_location_template_id: z.string(),
	type: publicPartLocationTypeSchema.optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partLocationsUpdateSchema = z.object({
	archived_at: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	id: z.string().optional(),
	level: z.number().optional(),
	name: z.string().optional(),
	parent_part_location_id: z.string().optional().nullable(),
	part_location_template_id: z.string().optional(),
	type: publicPartLocationTypeSchema.optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partLocationsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("part_locations_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_locations_parent_part_location_id_fkey"),
		columns: z.tuple([z.literal("parent_part_location_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_locations"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_locations_part_location_template_id_fkey"),
		columns: z.tuple([z.literal("part_location_template_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_location_templates"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_locations_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const partManufacturersRowSchema = z.object({
	created_at: z.string(),
	id: z.string(),
	name: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const partManufacturersInsertSchema = z.object({
	created_at: z.string().optional(),
	id: z.string().optional(),
	name: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const partManufacturersUpdateSchema = z.object({
	created_at: z.string().optional(),
	id: z.string().optional(),
	name: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const partManufacturersRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("part_manufacturers_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("part_manufacturers_workspace_id_fkey"),
	columns: z.tuple([z.literal("workspace_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("workspaces"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const partModelConstraintsRowSchema = z.object({
	created_at: z.string(),
	current_quantity: z.number().nullable(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	id: z.string(),
	max_quantity: z.number().nullable(),
	min_quantity: z.number().nullable(),
	part_model_id: z.string(),
	quantity_above_max: z.boolean().nullable(),
	quantity_below_min: z.boolean().nullable(),
	quantity_equals_max: z.boolean().nullable(),
	quantity_equals_min: z.boolean().nullable(),
	title: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const partModelConstraintsInsertSchema = z.object({
	created_at: z.string().optional(),
	current_quantity: z.number().optional().nullable(),
	deployment_id: z.string(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	max_quantity: z.number().optional().nullable(),
	min_quantity: z.number().optional().nullable(),
	part_model_id: z.string(),
	quantity_above_max: z.boolean().optional().nullable(),
	quantity_below_min: z.boolean().optional().nullable(),
	quantity_equals_max: z.boolean().optional().nullable(),
	quantity_equals_min: z.boolean().optional().nullable(),
	title: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partModelConstraintsUpdateSchema = z.object({
	created_at: z.string().optional(),
	current_quantity: z.number().optional().nullable(),
	deployment_id: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	max_quantity: z.number().optional().nullable(),
	min_quantity: z.number().optional().nullable(),
	part_model_id: z.string().optional(),
	quantity_above_max: z.boolean().optional().nullable(),
	quantity_below_min: z.boolean().optional().nullable(),
	quantity_equals_max: z.boolean().optional().nullable(),
	quantity_equals_min: z.boolean().optional().nullable(),
	title: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partModelConstraintsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("part_model_constraints_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_model_constraints_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_model_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_model_constraints_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_models"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_model_constraints_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const partModelsRowSchema = z.object({
	created_at: z.string(),
	description: z.string().nullable(),
	id: z.string(),
	manufacturer_ref: z.string().nullable(),
	name: z.string(),
	order_information: z.string().nullable(),
	order_lot_size: z.number().nullable(),
	order_min_quantity: z.number().nullable(),
	part_category_id: z.string(),
	part_manufacturer_id: z.string(),
	ref: z.string().nullable(),
	supplier: z.string().nullable(),
	supplier_part_ref: z.string().nullable(),
	supplier_ref: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const partModelsInsertSchema = z.object({
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	manufacturer_ref: z.string().optional().nullable(),
	name: z.string(),
	order_information: z.string().optional().nullable(),
	order_lot_size: z.number().optional().nullable(),
	order_min_quantity: z.number().optional().nullable(),
	part_category_id: z.string(),
	part_manufacturer_id: z.string(),
	ref: z.string().optional().nullable(),
	supplier: z.string().optional().nullable(),
	supplier_part_ref: z.string().optional().nullable(),
	supplier_ref: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const partModelsUpdateSchema = z.object({
	created_at: z.string().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	manufacturer_ref: z.string().optional().nullable(),
	name: z.string().optional(),
	order_information: z.string().optional().nullable(),
	order_lot_size: z.number().optional().nullable(),
	order_min_quantity: z.number().optional().nullable(),
	part_category_id: z.string().optional(),
	part_manufacturer_id: z.string().optional(),
	ref: z.string().optional().nullable(),
	supplier: z.string().optional().nullable(),
	supplier_part_ref: z.string().optional().nullable(),
	supplier_ref: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const partModelsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("part_models_part_category_id_fkey"),
		columns: z.tuple([z.literal("part_category_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_categories"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_models_part_manufacturer_id_fkey"),
		columns: z.tuple([z.literal("part_manufacturer_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_manufacturers"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_models_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_models_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const partToPartLocationsRowSchema = z.object({
	created_at: z.string(),
	part_id: z.string(),
	part_location_id: z.string(),
	quantity: z.number(),
	updated_at: z.string()
});
const partToPartLocationsInsertSchema = z.object({
	created_at: z.string().optional(),
	part_id: z.string(),
	part_location_id: z.string(),
	quantity: z.number(),
	updated_at: z.string().optional()
});
const partToPartLocationsUpdateSchema = z.object({
	created_at: z.string().optional(),
	part_id: z.string().optional(),
	part_location_id: z.string().optional(),
	quantity: z.number().optional(),
	updated_at: z.string().optional()
});
const partToPartLocationsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("part_to_part_locations_part_id_fkey"),
		columns: z.tuple([z.literal("part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_to_part_locations_part_id_fkey"),
		columns: z.tuple([z.literal("part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("parts"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_to_part_locations_part_location_id_fkey"),
		columns: z.tuple([z.literal("part_location_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_locations"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const partsRowSchema = z.object({
	archived_at: z.string().nullable(),
	average_price: z.number().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	device_id: z.string().nullable(),
	executed_at: z.string(),
	id: z.string(),
	part_model_id: z.string(),
	quantity: z.number().nullable(),
	ref: z.string().nullable(),
	transient_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const partsInsertSchema = z.object({
	archived_at: z.string().optional().nullable(),
	average_price: z.number().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string(),
	description: z.string().optional().nullable(),
	device_id: z.string().optional().nullable(),
	executed_at: z.string().optional(),
	id: z.string().optional(),
	part_model_id: z.string(),
	quantity: z.number().optional().nullable(),
	ref: z.string().optional().nullable(),
	transient_id: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partsUpdateSchema = z.object({
	archived_at: z.string().optional().nullable(),
	average_price: z.number().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	description: z.string().optional().nullable(),
	device_id: z.string().optional().nullable(),
	executed_at: z.string().optional(),
	id: z.string().optional(),
	part_model_id: z.string().optional(),
	quantity: z.number().optional().nullable(),
	ref: z.string().optional().nullable(),
	transient_id: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("parts_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("parts_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("parts_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("parts_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_model_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("parts_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_models"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("parts_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const partsHistoryRowSchema = z.object({
	archived_at: z.string().nullable(),
	average_price: z.number().nullable(),
	created_at: z.string(),
	deployment_id: z.string().nullable(),
	description: z.string().nullable(),
	device_id: z.string().nullable(),
	executed_at: z.string(),
	id: z.string(),
	maintenance_item_id: z.string().nullable(),
	part_id: z.string(),
	part_model_id: z.string().nullable(),
	quantity: z.number().nullable(),
	ref: z.string().nullable(),
	seq_id: z.number(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const partsHistoryInsertSchema = z.object({
	archived_at: z.string().optional().nullable(),
	average_price: z.number().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string().optional().nullable(),
	description: z.string().optional().nullable(),
	device_id: z.string().optional().nullable(),
	executed_at: z.string().optional(),
	id: z.string().optional(),
	maintenance_item_id: z.string().optional().nullable(),
	part_id: z.string(),
	part_model_id: z.string().optional().nullable(),
	quantity: z.number().optional().nullable(),
	ref: z.string().optional().nullable(),
	seq_id: z.number().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partsHistoryUpdateSchema = z.object({
	archived_at: z.string().optional().nullable(),
	average_price: z.number().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string().optional().nullable(),
	description: z.string().optional().nullable(),
	device_id: z.string().optional().nullable(),
	executed_at: z.string().optional(),
	id: z.string().optional(),
	maintenance_item_id: z.string().optional().nullable(),
	part_id: z.string().optional(),
	part_model_id: z.string().optional().nullable(),
	quantity: z.number().optional().nullable(),
	ref: z.string().optional().nullable(),
	seq_id: z.number().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const partsHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("parts_history_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("parts_history_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("parts_history_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("parts_history_maintenance_item_id_fkey"),
		columns: z.tuple([z.literal("maintenance_item_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_items"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("parts_history_part_id_fkey"),
		columns: z.tuple([z.literal("part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("parts_history_part_id_fkey"),
		columns: z.tuple([z.literal("part_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("parts"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("parts_history_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_model_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("parts_history_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_models"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("parts_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const physicalUnitsRowSchema = z.object({
	base_unit_id: z.string().nullable(),
	conversion_factor: z.number(),
	conversion_offset: z.number(),
	description: z.string().nullable(),
	id: z.string(),
	name: z.string(),
	symbol: z.string(),
	type: z.string()
});
const physicalUnitsInsertSchema = z.object({
	base_unit_id: z.string().optional().nullable(),
	conversion_factor: z.number(),
	conversion_offset: z.number(),
	description: z.string().optional().nullable(),
	id: z.string(),
	name: z.string(),
	symbol: z.string(),
	type: z.string()
});
const physicalUnitsUpdateSchema = z.object({
	base_unit_id: z.string().optional().nullable(),
	conversion_factor: z.number().optional(),
	conversion_offset: z.number().optional(),
	description: z.string().optional().nullable(),
	id: z.string().optional(),
	name: z.string().optional(),
	symbol: z.string().optional(),
	type: z.string().optional()
});
const physicalUnitsRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("physical_units_base_unit_id_fkey"),
	columns: z.tuple([z.literal("base_unit_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("physical_units"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const publicPortalsRowSchema = z.object({
	contact_details: z.string().nullable(),
	contact_details_html: z.string().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	id: z.string(),
	instructions: z.string().nullable(),
	instructions_html: z.string().nullable(),
	intro_details: z.string().nullable(),
	intro_details_html: z.string().nullable(),
	pin: z.string().nullable(),
	show_device_selector: z.boolean(),
	show_open_cases: z.boolean(),
	show_workspace_profile_logo: z.boolean(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const publicPortalsInsertSchema = z.object({
	contact_details: z.string().optional().nullable(),
	contact_details_html: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string(),
	id: z.string().optional(),
	instructions: z.string().optional().nullable(),
	instructions_html: z.string().optional().nullable(),
	intro_details: z.string().optional().nullable(),
	intro_details_html: z.string().optional().nullable(),
	pin: z.string().optional().nullable(),
	show_device_selector: z.boolean().optional(),
	show_open_cases: z.boolean().optional(),
	show_workspace_profile_logo: z.boolean().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const publicPortalsUpdateSchema = z.object({
	contact_details: z.string().optional().nullable(),
	contact_details_html: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	id: z.string().optional(),
	instructions: z.string().optional().nullable(),
	instructions_html: z.string().optional().nullable(),
	intro_details: z.string().optional().nullable(),
	intro_details_html: z.string().optional().nullable(),
	pin: z.string().optional().nullable(),
	show_device_selector: z.boolean().optional(),
	show_open_cases: z.boolean().optional(),
	show_workspace_profile_logo: z.boolean().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const publicPortalsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("public_portals_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("public_portals_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("public_portals_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const qrCodesRowSchema = z.object({
	created_at: z.string(),
	deployment_id: z.string(),
	deployment_portal_token: z.string().nullable(),
	device_portal_token: z.string().nullable(),
	id: z.string(),
	revoked_at: z.string().nullable(),
	scan_count: z.number().nullable(),
	scanned_at: z.string().nullable(),
	settings: jsonSchema.nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const qrCodesInsertSchema = z.object({
	created_at: z.string().optional(),
	deployment_id: z.string(),
	deployment_portal_token: z.string().optional().nullable(),
	device_portal_token: z.string().optional().nullable(),
	id: z.string().optional(),
	revoked_at: z.string().optional().nullable(),
	scan_count: z.number().optional().nullable(),
	scanned_at: z.string().optional().nullable(),
	settings: jsonSchema.optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const qrCodesUpdateSchema = z.object({
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	deployment_portal_token: z.string().optional().nullable(),
	device_portal_token: z.string().optional().nullable(),
	id: z.string().optional(),
	revoked_at: z.string().optional().nullable(),
	scan_count: z.number().optional().nullable(),
	scanned_at: z.string().optional().nullable(),
	settings: jsonSchema.optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const qrCodesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("qr_codes_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("qr_codes_deployment_portal_token_fkey"),
		columns: z.tuple([z.literal("deployment_portal_token")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("portal_token")])
	}),
	z.object({
		foreignKeyName: z.literal("qr_codes_device_portal_token_fkey"),
		columns: z.tuple([z.literal("device_portal_token")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("portal_token")])
	}),
	z.object({
		foreignKeyName: z.literal("qr_codes_device_portal_token_fkey"),
		columns: z.tuple([z.literal("device_portal_token")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("portal_token")])
	}),
	z.object({
		foreignKeyName: z.literal("qr_codes_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("qr_codes_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const serviceJobsRowSchema = z.object({
	address_1: z.string().nullable(),
	address_2: z.string().nullable(),
	city: z.string().nullable(),
	content_children_planned_duration: z.string().nullable(),
	content_children_resolution_duration: z.string().nullable(),
	content_planned_duration: z.string().nullable(),
	content_resolution_duration: z.string().nullable(),
	country: z.string().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	end_at: z.string(),
	id: z.string(),
	name: z.string(),
	planned_duration: z.string().nullable(),
	primary_case_id: z.string().nullable(),
	primary_maintenance_id: z.string().nullable(),
	public_id: z.string(),
	serial_id: z.number(),
	serial_id_prefix: z.string(),
	start_at: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string(),
	zipcode: z.string().nullable()
});
const serviceJobsInsertSchema = z.object({
	address_1: z.string().optional().nullable(),
	address_2: z.string().optional().nullable(),
	city: z.string().optional().nullable(),
	content_children_planned_duration: z.string().optional().nullable(),
	content_children_resolution_duration: z.string().optional().nullable(),
	content_planned_duration: z.string().optional().nullable(),
	content_resolution_duration: z.string().optional().nullable(),
	country: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string(),
	description: z.string().optional().nullable(),
	end_at: z.string().optional(),
	id: z.string().optional(),
	name: z.string(),
	planned_duration: z.string().optional().nullable(),
	primary_case_id: z.string().optional().nullable(),
	primary_maintenance_id: z.string().optional().nullable(),
	public_id: z.string().optional(),
	serial_id: z.number().optional(),
	serial_id_prefix: z.string().optional(),
	start_at: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string(),
	zipcode: z.string().optional().nullable()
});
const serviceJobsUpdateSchema = z.object({
	address_1: z.string().optional().nullable(),
	address_2: z.string().optional().nullable(),
	city: z.string().optional().nullable(),
	content_children_planned_duration: z.string().optional().nullable(),
	content_children_resolution_duration: z.string().optional().nullable(),
	content_planned_duration: z.string().optional().nullable(),
	content_resolution_duration: z.string().optional().nullable(),
	country: z.string().optional().nullable(),
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	description: z.string().optional().nullable(),
	end_at: z.string().optional(),
	id: z.string().optional(),
	name: z.string().optional(),
	planned_duration: z.string().optional().nullable(),
	primary_case_id: z.string().optional().nullable(),
	primary_maintenance_id: z.string().optional().nullable(),
	public_id: z.string().optional(),
	serial_id: z.number().optional(),
	serial_id_prefix: z.string().optional(),
	start_at: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional(),
	zipcode: z.string().optional().nullable()
});
const serviceJobsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("service_jobs_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_primary_case_id_fkey"),
		columns: z.tuple([z.literal("primary_case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_primary_case_id_fkey"),
		columns: z.tuple([z.literal("primary_case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("cases"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_primary_maintenance_id_fkey"),
		columns: z.tuple([z.literal("primary_maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_primary_maintenance_id_fkey"),
		columns: z.tuple([z.literal("primary_maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_primary_maintenance_id_fkey"),
		columns: z.tuple([z.literal("primary_maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const serviceJobsHistoryRowSchema = z.object({
	changed_fields: z.array(z.string()).nullable(),
	created_at: z.string().nullable(),
	deployment_id: z.string().nullable(),
	id: z.string(),
	new_val: jsonSchema.nullable(),
	old_val: jsonSchema.nullable(),
	operation: publicDbOperationSchema.nullable(),
	service_job_id: z.string().nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const serviceJobsHistoryInsertSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	deployment_id: z.string().optional().nullable(),
	id: z.string().optional(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	service_job_id: z.string().optional().nullable(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const serviceJobsHistoryUpdateSchema = z.object({
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	deployment_id: z.string().optional().nullable(),
	id: z.string().optional(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	service_job_id: z.string().optional().nullable(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional().nullable()
});
const serviceJobsHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("service_jobs_history_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_history_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_job_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_history_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_job_details_full"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_history_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_jobs"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_history_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const userProfilesRowSchema = z.object({
	created_at: z.string(),
	email: z.string(),
	first_name: z.string(),
	id: z.string(),
	last_name: z.string(),
	locale: z.string(),
	phone: z.string().nullable(),
	timezone: z.string(),
	updated_at: z.string()
});
const userProfilesInsertSchema = z.object({
	created_at: z.string().optional(),
	email: z.string(),
	first_name: z.string().optional(),
	id: z.string(),
	last_name: z.string().optional(),
	locale: z.string().optional(),
	phone: z.string().optional().nullable(),
	timezone: z.string().optional(),
	updated_at: z.string().optional()
});
const userProfilesUpdateSchema = z.object({
	created_at: z.string().optional(),
	email: z.string().optional(),
	first_name: z.string().optional(),
	id: z.string().optional(),
	last_name: z.string().optional(),
	locale: z.string().optional(),
	phone: z.string().optional().nullable(),
	timezone: z.string().optional(),
	updated_at: z.string().optional()
});
const usersToDeploymentsRowSchema = z.object({
	created_at: z.string(),
	deployment_id: z.string(),
	role: publicDeploymentRoleSchema,
	updated_at: z.string(),
	user_id: z.string()
});
const usersToDeploymentsInsertSchema = z.object({
	created_at: z.string().optional(),
	deployment_id: z.string(),
	role: publicDeploymentRoleSchema.optional(),
	updated_at: z.string().optional(),
	user_id: z.string()
});
const usersToDeploymentsUpdateSchema = z.object({
	created_at: z.string().optional(),
	deployment_id: z.string().optional(),
	role: publicDeploymentRoleSchema.optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional()
});
const usersToDeploymentsRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("users_to_deployments_deployment_id_fkey"),
	columns: z.tuple([z.literal("deployment_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("deployments"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("users_to_deployments_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const usersToRolesRowSchema = z.object({
	created_at: z.string(),
	role: publicGlobalRoleSchema,
	updated_at: z.string(),
	user_id: z.string()
});
const usersToRolesInsertSchema = z.object({
	created_at: z.string().optional(),
	role: publicGlobalRoleSchema.optional(),
	updated_at: z.string().optional(),
	user_id: z.string()
});
const usersToRolesUpdateSchema = z.object({
	created_at: z.string().optional(),
	role: publicGlobalRoleSchema.optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional()
});
const usersToRolesRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("users_to_roles_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(true),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const usersToServiceJobsRowSchema = z.object({
	assignee_id: z.string(),
	created_at: z.string(),
	service_job_id: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const usersToServiceJobsInsertSchema = z.object({
	assignee_id: z.string(),
	created_at: z.string().optional(),
	service_job_id: z.string(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const usersToServiceJobsUpdateSchema = z.object({
	assignee_id: z.string().optional(),
	created_at: z.string().optional(),
	service_job_id: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable()
});
const usersToServiceJobsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("u_to_sj_assignee_id_fkey"),
		columns: z.tuple([z.literal("assignee_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("u_to_sj_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_job_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("u_to_sj_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_job_details_full"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("u_to_sj_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_jobs"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("u_to_sj_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const usersToServiceJobsHistoryRowSchema = z.object({
	assignee_id: z.string().nullable(),
	changed_fields: z.array(z.string()).nullable(),
	created_at: z.string().nullable(),
	id: z.string(),
	new_val: jsonSchema.nullable(),
	old_val: jsonSchema.nullable(),
	operation: publicDbOperationSchema.nullable(),
	service_job_id: z.string().nullable(),
	user_id: z.string().nullable()
});
const usersToServiceJobsHistoryInsertSchema = z.object({
	assignee_id: z.string().optional().nullable(),
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	service_job_id: z.string().optional().nullable(),
	user_id: z.string().optional().nullable()
});
const usersToServiceJobsHistoryUpdateSchema = z.object({
	assignee_id: z.string().optional().nullable(),
	changed_fields: z.array(z.string()).optional().nullable(),
	created_at: z.string().optional().nullable(),
	id: z.string().optional(),
	new_val: jsonSchema.optional().nullable(),
	old_val: jsonSchema.optional().nullable(),
	operation: publicDbOperationSchema.optional().nullable(),
	service_job_id: z.string().optional().nullable(),
	user_id: z.string().optional().nullable()
});
const usersToServiceJobsHistoryRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("users_to_service_jobs_history_assignee_id_fkey"),
		columns: z.tuple([z.literal("assignee_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("users_to_service_jobs_history_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_job_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("users_to_service_jobs_history_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_job_details_full"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("users_to_service_jobs_history_service_job_id_fkey"),
		columns: z.tuple([z.literal("service_job_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("service_jobs"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("users_to_service_jobs_history_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const usersToWorkspacesRowSchema = z.object({
	created_at: z.string(),
	role: publicWorkspaceRoleSchema,
	updated_at: z.string(),
	user_id: z.string(),
	workspace_id: z.string()
});
const usersToWorkspacesInsertSchema = z.object({
	created_at: z.string().optional(),
	role: publicWorkspaceRoleSchema.optional(),
	updated_at: z.string().optional(),
	user_id: z.string(),
	workspace_id: z.string()
});
const usersToWorkspacesUpdateSchema = z.object({
	created_at: z.string().optional(),
	role: publicWorkspaceRoleSchema.optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional(),
	workspace_id: z.string().optional()
});
const usersToWorkspacesRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("users_to_workspaces_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("users_to_workspaces_workspace_id_fkey"),
	columns: z.tuple([z.literal("workspace_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("workspaces"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const whitelabelBrandsRowSchema = z.object({
	app_url: z.string(),
	created_at: z.string(),
	email_logo: z.string(),
	id: z.string(),
	key: z.string(),
	logo: z.string(),
	name: z.string(),
	primary_color: z.string(),
	secondary_color: z.string(),
	updated_at: z.string()
});
const whitelabelBrandsInsertSchema = z.object({
	app_url: z.string(),
	created_at: z.string().optional(),
	email_logo: z.string(),
	id: z.string().optional(),
	key: z.string(),
	logo: z.string(),
	name: z.string(),
	primary_color: z.string(),
	secondary_color: z.string(),
	updated_at: z.string().optional()
});
const whitelabelBrandsUpdateSchema = z.object({
	app_url: z.string().optional(),
	created_at: z.string().optional(),
	email_logo: z.string().optional(),
	id: z.string().optional(),
	key: z.string().optional(),
	logo: z.string().optional(),
	name: z.string().optional(),
	primary_color: z.string().optional(),
	secondary_color: z.string().optional(),
	updated_at: z.string().optional()
});
const workspaceInvitationsRowSchema = z.object({
	created_at: z.string(),
	email: z.string(),
	id: z.string(),
	updated_at: z.string(),
	user_id: z.string(),
	user_name: z.string(),
	workspace_id: z.string(),
	workspace_name: z.string(),
	workspace_role: publicWorkspaceRoleSchema
});
const workspaceInvitationsInsertSchema = z.object({
	created_at: z.string().optional(),
	email: z.string(),
	id: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string(),
	user_name: z.string(),
	workspace_id: z.string(),
	workspace_name: z.string(),
	workspace_role: publicWorkspaceRoleSchema.optional()
});
const workspaceInvitationsUpdateSchema = z.object({
	created_at: z.string().optional(),
	email: z.string().optional(),
	id: z.string().optional(),
	updated_at: z.string().optional(),
	user_id: z.string().optional(),
	user_name: z.string().optional(),
	workspace_id: z.string().optional(),
	workspace_name: z.string().optional(),
	workspace_role: publicWorkspaceRoleSchema.optional()
});
const workspaceInvitationsRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("workspace_invitations_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("workspace_invitations_workspace_id_fkey"),
	columns: z.tuple([z.literal("workspace_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("workspaces"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const workspaceProfileFilesRowSchema = z.object({
	bucket_id: z.string(),
	created_at: z.string(),
	id: z.string(),
	path: z.string(),
	thumbnail: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const workspaceProfileFilesInsertSchema = z.object({
	bucket_id: z.string(),
	created_at: z.string().optional(),
	id: z.string().optional(),
	path: z.string(),
	thumbnail: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string()
});
const workspaceProfileFilesUpdateSchema = z.object({
	bucket_id: z.string().optional(),
	created_at: z.string().optional(),
	id: z.string().optional(),
	path: z.string().optional(),
	thumbnail: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	workspace_id: z.string().optional()
});
const workspaceProfileFilesRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("workspace_profile_files_user_id_fkey"),
	columns: z.tuple([z.literal("user_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("user_profiles"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("workspace_profile_files_workspace_id_fkey"),
	columns: z.tuple([z.literal("workspace_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("workspace_profiles"),
	referencedColumns: z.tuple([z.literal("workspace_id")])
})]);
const workspaceProfilesRowSchema = z.object({
	created_at: z.string(),
	description: z.string(),
	is_public: z.boolean().nullable(),
	name: z.string(),
	updated_at: z.string(),
	website: z.string(),
	workspace_id: z.string()
});
const workspaceProfilesInsertSchema = z.object({
	created_at: z.string().optional(),
	description: z.string().optional(),
	is_public: z.boolean().optional().nullable(),
	name: z.string().optional(),
	updated_at: z.string().optional(),
	website: z.string().optional(),
	workspace_id: z.string()
});
const workspaceProfilesUpdateSchema = z.object({
	created_at: z.string().optional(),
	description: z.string().optional(),
	is_public: z.boolean().optional().nullable(),
	name: z.string().optional(),
	updated_at: z.string().optional(),
	website: z.string().optional(),
	workspace_id: z.string().optional()
});
const workspaceProfilesRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("workspace_profiles_workspace_id_fkey"),
	columns: z.tuple([z.literal("workspace_id")]),
	isOneToOne: z.literal(true),
	referencedRelation: z.literal("workspaces"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const workspacesRowSchema = z.object({
	abbreviation: z.string(),
	address_1: z.string().nullable(),
	address_2: z.string().nullable(),
	business_type: publicBusinessTypeSchema.nullable(),
	city: z.string().nullable(),
	country: z.string().nullable(),
	created_at: z.string(),
	creator_workspace_id: z.string().nullable(),
	deleted_at: z.string().nullable(),
	hide_public_materials: z.boolean(),
	id: z.string(),
	is_paid: z.boolean(),
	name: z.string(),
	trial_end_date: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	whitelabel_brand_id: z.string().nullable(),
	zipcode: z.string().nullable()
});
const workspacesInsertSchema = z.object({
	abbreviation: z.string().optional(),
	address_1: z.string().optional().nullable(),
	address_2: z.string().optional().nullable(),
	business_type: publicBusinessTypeSchema.optional().nullable(),
	city: z.string().optional().nullable(),
	country: z.string().optional().nullable(),
	created_at: z.string().optional(),
	creator_workspace_id: z.string().optional().nullable(),
	deleted_at: z.string().optional().nullable(),
	hide_public_materials: z.boolean().optional(),
	id: z.string().optional(),
	is_paid: z.boolean().optional(),
	name: z.string(),
	trial_end_date: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	whitelabel_brand_id: z.string().optional().nullable(),
	zipcode: z.string().optional().nullable()
});
const workspacesUpdateSchema = z.object({
	abbreviation: z.string().optional(),
	address_1: z.string().optional().nullable(),
	address_2: z.string().optional().nullable(),
	business_type: publicBusinessTypeSchema.optional().nullable(),
	city: z.string().optional().nullable(),
	country: z.string().optional().nullable(),
	created_at: z.string().optional(),
	creator_workspace_id: z.string().optional().nullable(),
	deleted_at: z.string().optional().nullable(),
	hide_public_materials: z.boolean().optional(),
	id: z.string().optional(),
	is_paid: z.boolean().optional(),
	name: z.string().optional(),
	trial_end_date: z.string().optional().nullable(),
	updated_at: z.string().optional(),
	user_id: z.string().optional().nullable(),
	whitelabel_brand_id: z.string().optional().nullable(),
	zipcode: z.string().optional().nullable()
});
const workspacesRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("workspaces_creator_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("workspaces_creator_workspace_id_fkey"),
		columns: z.tuple([z.literal("creator_workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("workspaces_whitelabel_brand_id_fkey"),
		columns: z.tuple([z.literal("whitelabel_brand_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("whitelabel_brands"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const deviceDetailsRowSchema = z.object({
	connection_status: publicConnectionStatusSchema.nullable(),
	connections_to_devices: z.array(connectionsToDevicesRowSchema).nullable(),
	created_at: z.string().nullable(),
	deleted_at: z.string().nullable(),
	deleted_by: z.string().nullable(),
	deployment_id: z.string().nullable(),
	device_tag_ids: z.array(z.string()).nullable(),
	device_tag_names_search: z.string().nullable(),
	device_tags: z.array(deviceTagsRowSchema).nullable(),
	id: z.string().nullable(),
	last_auth: z.string().nullable(),
	manufactured_at: z.string().nullable(),
	manufacturer: manufacturersRowSchema.nullable(),
	manufacturer_id: z.string().nullable(),
	manufacturer_name: z.string().nullable(),
	model: deviceModelsRowSchema.nullable(),
	model_id: z.string().nullable(),
	model_name: z.string().nullable(),
	name: z.string().nullable(),
	portal_token: z.string().nullable(),
	qr_code: z.string().nullable(),
	ref: z.string().nullable(),
	software_version: z.string().nullable(),
	updated_at: z.string().nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const caseDetailsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("cases_assignee_id_fkey"),
		columns: z.tuple([z.literal("assignee_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_case_template_id_fkey"),
		columns: z.tuple([z.literal("case_template_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_templates"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_device_id_fkey"),
		columns: z.tuple([z.literal("device_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("devices"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("cases_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const deviceDetailsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("devices_deleted_by_fkey"),
		columns: z.tuple([z.literal("deleted_by")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_manufacturer_id_fkey"),
		columns: z.tuple([z.literal("manufacturer_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("manufacturers"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_model_id_fkey"),
		columns: z.tuple([z.literal("model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("device_models"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("devices_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const maintenanceDetailsRowSchema = z.object({
	assignee: userProfilesRowSchema.nullable(),
	assignee_id: z.string().nullable(),
	cases_to_maintenances: z.array(z.string()).nullable(),
	child_maintenances: z.array(z.string()).nullable(),
	content_planned_duration: z.string().nullable(),
	content_resolution_duration: z.string().nullable(),
	created_at: z.string().nullable(),
	deployment: deploymentsRowSchema.nullable(),
	deployment_id: z.string().nullable(),
	device_ids: z.array(z.string()).nullable(),
	devices: z.array(deviceDetailsRowSchema).nullable(),
	id: z.string().nullable(),
	maintenance_items: z.array(publicMaintenanceItemStatusSchema).nullable(),
	maintenance_strategy: maintenanceStrategiesRowSchema.nullable(),
	maintenance_strategy_id: z.string().nullable(),
	maintenance_tag_ids: z.array(z.string()).nullable(),
	maintenance_tags: z.array(maintenanceTagsRowSchema).nullable(),
	manufacturer_ids: z.array(z.string()).nullable(),
	model_ids: z.array(z.string()).nullable(),
	notes: z.string().nullable(),
	parent_maintenances: z.array(z.string()).nullable(),
	part_location_id: z.string().nullable(),
	planned_at: z.string().nullable(),
	planned_date: z.string().nullable(),
	planned_date_until: z.string().nullable(),
	planned_duration: z.string().nullable(),
	planned_until: z.string().nullable(),
	public_id: z.string().nullable(),
	resolution_duration: z.string().nullable(),
	resolved_at: z.string().nullable(),
	scheduled_at: z.string().nullable(),
	scheduled_duration: z.string().nullable(),
	scheduled_until: z.string().nullable(),
	serial_id: z.number().nullable(),
	serial_id_prefix: z.string().nullable(),
	skip_reason: z.string().nullable(),
	started_at: z.string().nullable(),
	status: publicMaintenanceStatusSchema.nullable(),
	title: z.string().nullable(),
	updated_at: z.string().nullable(),
	user: userProfilesRowSchema.nullable(),
	user_id: z.string().nullable(),
	workspace: workspacesRowSchema.nullable(),
	workspace_id: z.string().nullable()
});
const maintenanceDetailsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenances_assignee_id_fkey"),
		columns: z.tuple([z.literal("assignee_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_maintenance_strategy_id_fkey"),
		columns: z.tuple([z.literal("maintenance_strategy_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_strategies"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_part_location_id_fkey"),
		columns: z.tuple([z.literal("part_location_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_locations"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const partModelDetailsRowSchema = z.object({
	created_at: z.string().nullable(),
	description: z.string().nullable(),
	id: z.string().nullable(),
	manufacturer_ref: z.string().nullable(),
	name: z.string().nullable(),
	order_information: z.string().nullable(),
	order_lot_size: z.number().nullable(),
	order_min_quantity: z.number().nullable(),
	part_category: partCategoriesRowSchema.nullable(),
	part_category_id: z.string().nullable(),
	part_manufacturer: partManufacturersRowSchema.nullable(),
	part_manufacturer_id: z.string().nullable(),
	ref: z.string().nullable(),
	supplier: z.string().nullable(),
	supplier_part_ref: z.string().nullable(),
	supplier_ref: z.string().nullable(),
	updated_at: z.string().nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const maintenancesToPartModelsAllocatedDetailsRelationshipsSchema = z.tuple([z.object({
	foreignKeyName: z.literal("maintenances_deployment_id_fkey"),
	columns: z.tuple([z.literal("deployment_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("deployments"),
	referencedColumns: z.tuple([z.literal("id")])
}), z.object({
	foreignKeyName: z.literal("maintenances_workspace_id_fkey"),
	columns: z.tuple([z.literal("workspace_id")]),
	isOneToOne: z.literal(false),
	referencedRelation: z.literal("workspaces"),
	referencedColumns: z.tuple([z.literal("id")])
})]);
const maintenancesToPartModelsRequestedDetailsRowSchema = z.object({
	allocated_part_locations: z.array(partToPartLocationsRowSchema).nullable(),
	allocated_parts: z.array(partsRowSchema).nullable(),
	created_at: z.string().nullable(),
	deployment_id: z.string().nullable(),
	id: z.string().nullable(),
	maintenance: maintenancesRowSchema.nullable(),
	maintenance_id: z.string().nullable(),
	notes: z.string().nullable(),
	part_model: partModelDetailsRowSchema.nullable(),
	part_model_id: z.string().nullable(),
	planned_date: z.string().nullable(),
	quantity: z.number().nullable(),
	quantity_allocated: z.number().nullable(),
	updated_at: z.string().nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const maintenancesToPartModelsRequestedDetailsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_maintenance_id_fkey"),
		columns: z.tuple([z.literal("maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_model_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_part_model_id_fkey"),
		columns: z.tuple([z.literal("part_model_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_models"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("maintenances_to_part_models_requested_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const partModelDetailsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("part_models_part_category_id_fkey"),
		columns: z.tuple([z.literal("part_category_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_categories"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_models_part_manufacturer_id_fkey"),
		columns: z.tuple([z.literal("part_manufacturer_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("part_manufacturers"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_models_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("part_models_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const serviceJobDetailsRowSchema = z.object({
	address_1: z.string().nullable(),
	address_2: z.string().nullable(),
	assignee_ids: z.array(z.string()).nullable(),
	assignees: z.array(userProfilesRowSchema).nullable(),
	cases: z.array(casesRowSchema).nullable(),
	city: z.string().nullable(),
	content_children_planned_duration: z.string().nullable(),
	content_children_resolution_duration: z.string().nullable(),
	content_planned_duration: z.string().nullable(),
	content_resolution_duration: z.string().nullable(),
	country: z.string().nullable(),
	created_at: z.string().nullable(),
	deployment_id: z.string().nullable(),
	description: z.string().nullable(),
	end_at: z.string().nullable(),
	id: z.string().nullable(),
	maintenances: z.array(maintenancesRowSchema).nullable(),
	name: z.string().nullable(),
	planned_duration: z.string().nullable(),
	primary_case_id: z.string().nullable(),
	primary_maintenance_id: z.string().nullable(),
	public_id: z.string().nullable(),
	serial_id: z.number().nullable(),
	serial_id_prefix: z.string().nullable(),
	start_at: z.string().nullable(),
	updated_at: z.string().nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable(),
	zipcode: z.string().nullable()
});
const serviceJobDetailsRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("service_jobs_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_primary_case_id_fkey"),
		columns: z.tuple([z.literal("primary_case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_primary_case_id_fkey"),
		columns: z.tuple([z.literal("primary_case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("cases"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_primary_maintenance_id_fkey"),
		columns: z.tuple([z.literal("primary_maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_primary_maintenance_id_fkey"),
		columns: z.tuple([z.literal("primary_maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_primary_maintenance_id_fkey"),
		columns: z.tuple([z.literal("primary_maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const caseDetailsRowSchema = z.object({
	assessed_at: z.string().nullable(),
	assignee_id: z.string().nullable(),
	case_tag_ids: z.array(z.string()).nullable(),
	case_tags: z.array(caseTagsRowSchema).nullable(),
	case_template_id: z.string().nullable(),
	cases_to_device_data_triggers: z.array(casesToDeviceDataTriggersRowSchema).nullable(),
	cases_to_maintenances: z.array(casesToMaintenancesRowSchema).nullable(),
	cases_to_parts: z.array(casesToPartsRowSchema).nullable(),
	created_at: z.string().nullable(),
	deployment_id: z.string().nullable(),
	description: z.string().nullable(),
	description_html: z.string().nullable(),
	device: deviceDetailsRowSchema.nullable(),
	device_id: z.string().nullable(),
	due_at: z.string().nullable(),
	embedding: z.string().nullable(),
	event_finished_at: z.string().nullable(),
	event_started_at: z.string().nullable(),
	fts: z.unknown(),
	id: z.string().nullable(),
	name: z.string().nullable(),
	origin: publicCaseOriginSchema.nullable(),
	priority: z.number().nullable(),
	proposal: z.string().nullable(),
	proposal_html: z.string().nullable(),
	public_id: z.string().nullable(),
	public_portal_contact_mail: z.string().nullable(),
	public_portal_contact_name: z.string().nullable(),
	public_portal_contact_phone: z.string().nullable(),
	reported_at: z.string().nullable(),
	resolution_duration: z.string().nullable(),
	resolved_at: z.string().nullable(),
	serial_id: z.number().nullable(),
	serial_id_prefix: z.string().nullable(),
	solution: z.string().nullable(),
	solution_html: z.string().nullable(),
	started_at: z.string().nullable(),
	status: publicCaseStatusSchema.nullable(),
	type: publicCaseTypeSchema.nullable(),
	updated_at: z.string().nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const serviceJobDetailsFullRelationshipsSchema = z.tuple([
	z.object({
		foreignKeyName: z.literal("service_jobs_deployment_id_fkey"),
		columns: z.tuple([z.literal("deployment_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("deployments"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_primary_case_id_fkey"),
		columns: z.tuple([z.literal("primary_case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("case_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_primary_case_id_fkey"),
		columns: z.tuple([z.literal("primary_case_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("cases"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_primary_maintenance_id_fkey"),
		columns: z.tuple([z.literal("primary_maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenance_details"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_primary_maintenance_id_fkey"),
		columns: z.tuple([z.literal("primary_maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_primary_maintenance_id_fkey"),
		columns: z.tuple([z.literal("primary_maintenance_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("maintenances_to_part_models_allocated_details"),
		referencedColumns: z.tuple([z.literal("maintenance_id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_user_id_fkey"),
		columns: z.tuple([z.literal("user_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("user_profiles"),
		referencedColumns: z.tuple([z.literal("id")])
	}),
	z.object({
		foreignKeyName: z.literal("service_jobs_workspace_id_fkey"),
		columns: z.tuple([z.literal("workspace_id")]),
		isOneToOne: z.literal(false),
		referencedRelation: z.literal("workspaces"),
		referencedColumns: z.tuple([z.literal("id")])
	})
]);
const acceptWorkspaceInvitationArgsSchema = z.object({ invitation_id: z.string() });
const acceptWorkspaceInvitationReturnsSchema = z.undefined();
const addUserToDeploymentArgsSchema = z.object({
	_deployment_id: z.string(),
	_invited_user_id: z.string(),
	_role: publicDeploymentRoleSchema
});
const addUserToDeploymentReturnsSchema = z.undefined();
const applySparePartUsageArgsSchema = z.object({ _maintenance_item_id: z.string() });
const applySparePartUsageReturnsSchema = z.undefined();
const casesSearchArgsSchema = z.object({
	_device_id: z.string().optional(),
	_device_model_id: z.string().optional(),
	_full_text_weight: z.number().optional(),
	_manufacturer_id: z.string().optional(),
	_match_count: z.number(),
	_query_embedding: z.string(),
	_query_text: z.string(),
	_rrf_k: z.number().optional(),
	_semantic_weight: z.number().optional(),
	_workspace_ids: z.array(z.string())
});
const casesSearchReturnsSchema = z.array(z.object({
	assessed_at: z.string().nullable(),
	assignee_id: z.string().nullable(),
	case_template_id: z.string().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	description_html: z.string().nullable(),
	device_id: z.string().nullable(),
	due_at: z.string().nullable(),
	embedding: z.string().nullable(),
	event_finished_at: z.string().nullable(),
	event_started_at: z.string().nullable(),
	fts: z.unknown(),
	id: z.string(),
	name: z.string(),
	origin: publicCaseOriginSchema.nullable(),
	priority: z.number(),
	proposal: z.string().nullable(),
	proposal_html: z.string().nullable(),
	public_id: z.string(),
	public_portal_contact_mail: z.string().nullable(),
	public_portal_contact_name: z.string().nullable(),
	public_portal_contact_phone: z.string().nullable(),
	reported_at: z.string().nullable(),
	resolution_duration: z.string().nullable(),
	resolved_at: z.string().nullable(),
	serial_id: z.number(),
	serial_id_prefix: z.string(),
	solution: z.string().nullable(),
	solution_html: z.string().nullable(),
	started_at: z.string().nullable(),
	status: publicCaseStatusSchema,
	type: publicCaseTypeSchema.nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
}));
const createCustomerDeploymentArgsSchema = z.object({
	_address_1: z.string().optional(),
	_address_2: z.string().optional(),
	_city: z.string().optional(),
	_country: z.string().optional(),
	_name: z.string(),
	_parent_workspace_id: z.string(),
	_workspace_id: z.string(),
	_zipcode: z.string().optional()
});
const createCustomerDeploymentReturnsSchema = z.object({
	address_1: z.string().nullable(),
	address_2: z.string().nullable(),
	city: z.string().nullable(),
	country: z.string().nullable(),
	created_at: z.string(),
	deleted_at: z.string().nullable(),
	id: z.string(),
	name: z.string(),
	portal_token: z.string(),
	timezone: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string(),
	zipcode: z.string().nullable()
});
const createMaintenanceArgsSchema = z.object({
	_assignee_id: z.string().optional(),
	_case_id: z.string().optional(),
	_checklist_template_id: z.string().optional(),
	_deployment_id: z.string(),
	_device_ids: z.array(z.string()).optional(),
	_maintenance_id: z.string().optional(),
	_maintenance_item_id: z.string().optional(),
	_maintenance_tag_ids: z.array(z.string()).optional(),
	_scheduled_at: z.string().optional(),
	_scheduled_duration: z.string().optional(),
	_service_job_id: z.string().optional(),
	_title: z.string()
});
const createMaintenanceReturnsSchema = z.object({
	assignee_id: z.string().nullable(),
	content_planned_duration: z.string().nullable(),
	content_resolution_duration: z.string().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	id: z.string(),
	maintenance_strategy_id: z.string().nullable(),
	notes: z.string(),
	part_location_id: z.string().nullable(),
	planned_at: z.string().nullable(),
	planned_date: z.string().nullable(),
	planned_date_until: z.string().nullable(),
	planned_duration: z.string().nullable(),
	planned_until: z.string().nullable(),
	public_id: z.string(),
	resolution_duration: z.string().nullable(),
	resolved_at: z.string().nullable(),
	scheduled_at: z.string().nullable(),
	scheduled_duration: z.string().nullable(),
	scheduled_until: z.string().nullable(),
	serial_id: z.number(),
	serial_id_prefix: z.string(),
	skip_reason: z.string().nullable(),
	started_at: z.string().nullable(),
	status: publicMaintenanceStatusSchema,
	title: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
});
const createWorkspaceArgsSchema = z.object({
	_deployment_address_1: z.string().optional(),
	_deployment_address_2: z.string().optional(),
	_deployment_city: z.string().optional(),
	_deployment_country: z.string().optional(),
	_deployment_name: z.string().optional(),
	_deployment_timezone: z.string().optional(),
	_deployment_zipcode: z.string().optional(),
	_workspace_abbreviation: z.string().optional(),
	_workspace_address_1: z.string().optional(),
	_workspace_address_2: z.string().optional(),
	_workspace_business_type: publicBusinessTypeSchema.optional(),
	_workspace_city: z.string().optional(),
	_workspace_country: z.string().optional(),
	_workspace_creator_workspace_id: z.string().optional(),
	_workspace_hide_public_materials: z.boolean().optional(),
	_workspace_is_paid: z.boolean().optional(),
	_workspace_name: z.string(),
	_workspace_trial_end_date: z.string().optional(),
	_workspace_user_id: z.string().optional(),
	_workspace_whitelabel_brand_id: z.string().optional(),
	_workspace_zipcode: z.string().optional()
});
const createWorkspaceReturnsSchema = z.object({
	abbreviation: z.string(),
	address_1: z.string().nullable(),
	address_2: z.string().nullable(),
	business_type: publicBusinessTypeSchema.nullable(),
	city: z.string().nullable(),
	country: z.string().nullable(),
	created_at: z.string(),
	creator_workspace_id: z.string().nullable(),
	deleted_at: z.string().nullable(),
	hide_public_materials: z.boolean(),
	id: z.string(),
	is_paid: z.boolean(),
	name: z.string(),
	trial_end_date: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	whitelabel_brand_id: z.string().nullable(),
	zipcode: z.string().nullable()
});
const declineWorkspaceInvitationArgsSchema = z.object({ invitation_id: z.string() });
const declineWorkspaceInvitationReturnsSchema = z.undefined();
const deleteDeploymentArgsSchema = z.object({ _deployment_id: z.string() });
const deleteDeploymentReturnsSchema = z.undefined();
const deleteWorkspaceArgsSchema = z.object({ _workspace_id: z.string() });
const deleteWorkspaceReturnsSchema = z.undefined();
const deviceModelFileChunksSearchArgsSchema = z.object({
	_device_model_id: z.string().optional(),
	_full_text_weight: z.number().optional(),
	_match_count: z.number(),
	_query_embedding: z.string(),
	_query_text: z.string(),
	_rrf_k: z.number().optional(),
	_semantic_weight: z.number().optional(),
	_workspace_ids: z.array(z.string())
});
const deviceModelFileChunksSearchReturnsSchema = z.array(z.object({
	bbox: z.array(z.number()).nullable(),
	content: z.string().nullable(),
	device_model_file_id: z.string(),
	embedding: z.string().nullable(),
	fts: z.unknown(),
	headings: z.array(z.string()).nullable(),
	id: z.string(),
	label: z.string().nullable(),
	original_content: z.string().nullable(),
	page_number: z.number().nullable()
}));
const exportFifoInventoryForCsvArgsSchema = z.object({
	_deployment_id: z.string(),
	_from_date: z.string(),
	_time_zone: z.string().optional(),
	_until_date: z.string()
});
const exportFifoInventoryForCsvReturnsSchema = z.array(z.object({
	action: publicInventoryActionSchema,
	date: z.string(),
	material_number: z.string(),
	note: z.string(),
	package_number: z.string(),
	part_model: z.string(),
	quantity: z.number(),
	reference_inventory_action: publicInventoryActionSchema,
	reference_inventory_date: z.string(),
	reference_inventory_note: z.string(),
	reference_inventory_package_number: z.string(),
	reference_inventory_quantity: z.number(),
	reference_inventory_remaining_quantity: z.number(),
	reference_inventory_time: z.string(),
	serial_number: z.string(),
	single_item_price: z.number(),
	time_of_day: z.string(),
	total_price: z.number()
}));
const exportPartsInventoryCsvArgsSchema = z.object({ _deployment_id: z.string() });
const exportPartsInventoryCsvReturnsSchema = z.array(z.object({
	location: z.string(),
	location_root: z.string(),
	material_number: z.string(),
	part_model: z.string(),
	price_per_item: z.number(),
	quantity: z.number()
}));
const exportPartsInventoryWithConstraintsCsvArgsSchema = z.object({
	_deployment_id: z.string(),
	_from_date: z.string(),
	_until_date: z.string()
});
const exportPartsInventoryWithConstraintsCsvReturnsSchema = z.array(z.object({
	constraints_all: z.string(),
	constraints_firing: z.string(),
	material_number: z.string(),
	part_model: z.string(),
	price_per_item: z.number(),
	quantity_change: z.number(),
	quantity_remaining: z.number()
}));
const getChecklistTemplatesArgsSchema = z.object({
	_with_local: z.boolean().optional(),
	_with_public: z.boolean().optional(),
	_with_shared: z.boolean().optional(),
	_workspace_id: z.string()
});
const getChecklistTemplatesReturnsSchema = z.array(z.object({
	content_duration: z.string(),
	created_at: z.string(),
	description: z.string(),
	id: z.string(),
	is_deleted: z.boolean(),
	is_public: z.boolean(),
	is_shared: z.boolean(),
	title: z.string(),
	updated_at: z.string(),
	user_id: z.string(),
	workspace_id: z.string()
}));
const getDeviceDetailArgsSchema = z.object({ _id: z.string() });
const getDeviceDetailReturnsSchema = z.object({
	connection_status: publicConnectionStatusSchema.nullable(),
	connections_to_devices: z.array(connectionsToDevicesRowSchema).nullable(),
	created_at: z.string().nullable(),
	deleted_at: z.string().nullable(),
	deleted_by: z.string().nullable(),
	deployment_id: z.string().nullable(),
	device_tag_ids: z.array(z.string()).nullable(),
	device_tag_names_search: z.string().nullable(),
	device_tags: z.array(deviceTagsRowSchema).nullable(),
	id: z.string().nullable(),
	last_auth: z.string().nullable(),
	manufactured_at: z.string().nullable(),
	manufacturer: manufacturersRowSchema.nullable(),
	manufacturer_id: z.string().nullable(),
	manufacturer_name: z.string().nullable(),
	model: deviceModelsRowSchema.nullable(),
	model_id: z.string().nullable(),
	model_name: z.string().nullable(),
	name: z.string().nullable(),
	portal_token: z.string().nullable(),
	qr_code: z.string().nullable(),
	ref: z.string().nullable(),
	software_version: z.string().nullable(),
	updated_at: z.string().nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const getDeviceErrorsArgsSchema = z.object({
	_deployment_ids: z.array(z.string()),
	_device_ids: z.array(z.string()),
	_device_model_ids: z.array(z.string()),
	_device_tag_ids: z.array(z.string()),
	_from: z.string(),
	_manufacturer_ids: z.array(z.string()),
	_search_title: z.string(),
	_to: z.string()
});
const getDeviceErrorsReturnsSchema = z.array(z.object({
	_code: z.string(),
	_component: z.string(),
	_created_at: z.string(),
	_deployment: jsonSchema,
	_deployment_id: z.string(),
	_deployment_name: z.string(),
	_description: z.string(),
	_device: jsonSchema,
	_device_id: z.string(),
	_device_model: jsonSchema,
	_device_model_id: z.string(),
	_device_model_name: z.string(),
	_device_name: z.string(),
	_device_tags: jsonSchema,
	_id: z.number(),
	_manufacturer: jsonSchema,
	_manufacturer_id: z.string(),
	_manufacturer_name: z.string(),
	_parameters: jsonSchema,
	_resolved_at: z.string(),
	_severity: z.number(),
	_started_at: z.string(),
	_time_range: z.unknown(),
	_title: z.string(),
	_updated_at: z.string(),
	_x: z.number(),
	_y: z.number(),
	_z: z.number()
}));
const getDeviceErrorsAggregationByDeviceTagArgsSchema = z.object({
	_deployment_ids: z.array(z.string()),
	_device_tag_ids: z.array(z.string()),
	_from: z.string(),
	_to: z.string()
});
const getDeviceErrorsAggregationByDeviceTagReturnsSchema = z.array(z.object({
	_device_ids: z.array(z.string()),
	_device_tag_id: z.string(),
	_device_tag_name: z.string(),
	_error_count: z.number(),
	_error_ids: z.array(z.number()),
	_total_duration_seconds: z.number()
}));
const getDeviceErrorsAggregationByErrorArgsSchema = z.object({
	_deployment_ids: z.array(z.string()),
	_device_tag_ids: z.array(z.string()),
	_from: z.string(),
	_to: z.string()
});
const getDeviceErrorsAggregationByErrorReturnsSchema = z.array(z.object({
	_device_ids: z.array(z.string()),
	_error_count: z.number(),
	_error_ids: z.array(z.number()),
	_error_title: z.string(),
	_total_duration_seconds: z.number()
}));
const getDeviceErrorsGanttArgsSchema = z.object({
	_deployment_ids: z.array(z.string()),
	_device_ids: z.array(z.string()),
	_device_tag_ids: z.array(z.string()),
	_from: z.string(),
	_to: z.string()
});
const getDeviceErrorsGanttReturnsSchema = z.array(z.object({
	_deployment_id: z.string(),
	_deployment_name: z.string(),
	_device_id: z.string(),
	_device_name: z.string(),
	_id: z.number(),
	_resolved_at: z.string(),
	_started_at: z.string(),
	_title: z.string()
}));
const getDeviceLogsArgsSchema = z.object({
	_device_id: z.string(),
	_from: z.string().optional(),
	_to: z.string().optional()
});
const getDeviceLogsReturnsSchema = z.array(z.object({
	assignee_id: z.string(),
	case_item: casesRowSchema,
	description: z.string(),
	history_event: devicesHistoryRowSchema,
	id: z.string(),
	maintenance: maintenancesRowSchema,
	maintenance_item: maintenanceItemsRowSchema,
	maintenance_items: z.array(maintenanceItemsRowSchema),
	name: z.string(),
	relevant_date: z.string(),
	relevant_timerange: z.string(),
	service_job: serviceJobsRowSchema,
	status: z.string(),
	type: publicDevicesHistoryTypeSchema,
	workspace_id: z.string()
}));
const getDeviceLogsByPartArgsSchema = z.object({
	_device_id: z.string(),
	_part_id: z.string()
});
const getDeviceLogsByPartReturnsSchema = z.array(z.object({
	assignee_id: z.string(),
	case_item: casesRowSchema,
	description: z.string(),
	history_event: devicesHistoryRowSchema,
	id: z.string(),
	maintenance: maintenancesRowSchema,
	maintenance_item: maintenanceItemsRowSchema,
	maintenance_items: z.array(maintenanceItemsRowSchema),
	name: z.string(),
	relevant_date: z.string(),
	relevant_timerange: z.string(),
	service_job: serviceJobsRowSchema,
	status: z.string(),
	type: publicDevicesHistoryTypeSchema,
	workspace_id: z.string()
}));
const getDeviceModelFilesArgsSchema = z.object({ _device_model_id: z.string() });
const getDeviceModelFilesReturnsSchema = z.array(z.object({
	bucket_id: z.string(),
	created_at: z.string(),
	description: z.string().nullable(),
	id: z.string(),
	locale: z.string().nullable(),
	path: z.string(),
	processed_at: z.string().nullable(),
	shared_with_children: z.boolean(),
	thumbnail: z.string().nullable(),
	title: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string()
}));
const getDeviceModelProfileFileArgsSchema = z.object({ _device_model_id: z.string() });
const getDeviceModelProfileFileReturnsSchema = z.object({
	bucket_id: z.string(),
	created_at: z.string(),
	device_model_id: z.string(),
	id: z.string(),
	path: z.string(),
	thumbnail: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const getDeviceModelsArgsSchema = z.object({ _workspace_ids: z.array(z.string()).optional() });
const getDeviceModelsReturnsSchema = z.array(z.object({
	created_at: z.string(),
	device_ids: z.array(z.string()),
	id: z.string(),
	is_public: z.boolean(),
	manufacturer_id: z.string(),
	name: z.string(),
	parent_id: z.string(),
	slug: z.string(),
	updated_at: z.string(),
	user_id: z.string(),
	workspace_id: z.string()
}));
const getDeviceScalarValueNamesArgsSchema = z.object({ _device_id: z.string() });
const getDeviceScalarValueNamesReturnsSchema = z.array(z.object({
	name: z.string(),
	unit_id: z.string()
}));
const getDevicesAvailabilityByDeviceTagArgsSchema = z.object({
	_deployment_ids: z.array(z.string()),
	_device_tag_ids: z.array(z.string()),
	_from: z.string(),
	_to: z.string()
});
const getDevicesAvailabilityByDeviceTagReturnsSchema = z.array(z.object({
	_avg_daily_duration: z.string(),
	_avg_daily_duration_seconds: z.number(),
	_deployment_id: z.string(),
	_deployment_name: z.string(),
	_device_tag_id: z.string(),
	_device_tag_name: z.string(),
	_device_with_errors_count: z.number(),
	_dt: z.string(),
	_parallel_availability: z.number(),
	_serial_availability: z.number(),
	_total_device_count: z.number()
}));
const getDevicesByTagsArgsSchema = z.object({
	_deployment_ids: z.array(z.string()),
	_device_tag_ids: z.array(z.string())
});
const getDevicesByTagsReturnsSchema = z.array(z.object({ _device_id: z.string() }));
const getDevicesErrorDurationArgsSchema = z.object({
	_deployment_ids: z.array(z.string()),
	_device_tag_ids: z.array(z.string()),
	_from: z.string(),
	_to: z.string()
});
const getDevicesErrorDurationReturnsSchema = z.array(z.object({
	_deployment_id: z.string(),
	_deployment_name: z.string(),
	_device_id: z.string(),
	_device_name: z.string(),
	_dt: z.string(),
	_total_daily_duration: z.string(),
	_total_daily_duration_seconds: z.number()
}));
const getDevicesErrorDurationByDeviceTagArgsSchema = z.object({
	_deployment_ids: z.array(z.string()),
	_device_tag_ids: z.array(z.string()),
	_from: z.string(),
	_to: z.string()
});
const getDevicesErrorDurationByDeviceTagReturnsSchema = z.array(z.object({
	_deployment_id: z.string(),
	_deployment_name: z.string(),
	_device_tag_id: z.string(),
	_device_tag_name: z.string(),
	_device_with_errors_count: z.number(),
	_dt: z.string(),
	_parallel_availability: z.number(),
	_serial_availability: z.number(),
	_total_daily_duration: z.string(),
	_total_daily_duration_seconds: z.number()
}));
const getLastHistoryArgsSchema = z.object({ _part_id: z.string() });
const getLastHistoryReturnsSchema = z.object({
	archived_at: z.string().nullable(),
	average_price: z.number().nullable(),
	created_at: z.string(),
	deployment_id: z.string().nullable(),
	description: z.string().nullable(),
	device_id: z.string().nullable(),
	executed_at: z.string(),
	id: z.string(),
	maintenance_item_id: z.string().nullable(),
	part_id: z.string(),
	part_model_id: z.string().nullable(),
	quantity: z.number().nullable(),
	ref: z.string().nullable(),
	seq_id: z.number(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const getMaintenancesToPartModelsAllocatedDetailsArgsSchema = z.object({ _id: z.string() });
const getMaintenancesToPartModelsAllocatedDetailsReturnsSchema = z.object({
	deployment_id: z.string().nullable(),
	id: z.string().nullable(),
	maintenance: maintenancesRowSchema.nullable(),
	maintenance_id: z.string().nullable(),
	part: partsRowSchema.nullable(),
	part_category: partCategoriesRowSchema.nullable(),
	part_location: partLocationsRowSchema.nullable(),
	part_location_template: partLocationTemplatesRowSchema.nullable(),
	part_manufacturer: partManufacturersRowSchema.nullable(),
	part_model: partModelDetailsRowSchema.nullable(),
	part_to_part_location: partToPartLocationsRowSchema.nullable(),
	quantity: z.number().nullable(),
	workspace_id: z.string().nullable()
});
const getMaintenancesToPartModelsRequestedDetailsArgsSchema = z.object({ _id: z.string() });
const getMaintenancesToPartModelsRequestedDetailsReturnsSchema = z.object({
	allocated_part_locations: z.array(partToPartLocationsRowSchema).nullable(),
	allocated_parts: z.array(partsRowSchema).nullable(),
	created_at: z.string().nullable(),
	deployment_id: z.string().nullable(),
	id: z.string().nullable(),
	maintenance: maintenancesRowSchema.nullable(),
	maintenance_id: z.string().nullable(),
	notes: z.string().nullable(),
	part_model: partModelDetailsRowSchema.nullable(),
	part_model_id: z.string().nullable(),
	planned_date: z.string().nullable(),
	quantity: z.number().nullable(),
	quantity_allocated: z.number().nullable(),
	updated_at: z.string().nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
});
const getManufacturersArgsSchema = z.object({ _workspace_ids: z.array(z.string()).optional() });
const getManufacturersReturnsSchema = z.array(z.object({
	created_at: z.string(),
	device_model_ids: z.array(z.string()),
	id: z.string(),
	is_public: z.boolean(),
	name: z.string(),
	parent_id: z.string(),
	slug: z.string(),
	updated_at: z.string(),
	user_id: z.string(),
	workspace_id: z.string()
}));
const getOrCreateDeviceDataSeriesArgsSchema = z.object({
	_calculate_derivative: z.boolean().optional(),
	_deployment_id: z.string(),
	_metric_name: z.string(),
	_target_table: publicDataTriggerTargetTableSchema,
	_time_window: z.string().optional(),
	_transform: publicDataTriggerTransformationSchema.optional()
});
const getOrCreateDeviceDataSeriesReturnsSchema = z.string();
const getServiceJobDetailArgsSchema = z.object({ _id: z.string() });
const getServiceJobDetailReturnsSchema = z.object({
	address_1: z.string().nullable(),
	address_2: z.string().nullable(),
	assignee_ids: z.array(z.string()).nullable(),
	assignees: z.array(userProfilesRowSchema).nullable(),
	cases: z.array(caseDetailsRowSchema).nullable(),
	city: z.string().nullable(),
	content_children_planned_duration: z.string().nullable(),
	content_children_resolution_duration: z.string().nullable(),
	content_planned_duration: z.string().nullable(),
	content_resolution_duration: z.string().nullable(),
	country: z.string().nullable(),
	created_at: z.string().nullable(),
	deployment_id: z.string().nullable(),
	description: z.string().nullable(),
	end_at: z.string().nullable(),
	id: z.string().nullable(),
	maintenances: z.array(maintenanceDetailsRowSchema).nullable(),
	name: z.string().nullable(),
	planned_duration: z.string().nullable(),
	primary_case_id: z.string().nullable(),
	primary_maintenance_id: z.string().nullable(),
	public_id: z.string().nullable(),
	serial_id: z.number().nullable(),
	serial_id_prefix: z.string().nullable(),
	start_at: z.string().nullable(),
	updated_at: z.string().nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable(),
	zipcode: z.string().nullable()
});
const getServiceJobLogsArgsSchema = z.object({ _service_job_id: z.string() });
const getServiceJobLogsReturnsSchema = z.array(z.object({
	changed_fields: z.array(z.string()),
	created_at: z.string(),
	id: z.string(),
	new_val: jsonSchema,
	old_val: jsonSchema,
	operation: publicDbOperationSchema,
	service_job_id: z.string(),
	type: publicServiceJobsHistoryTypeSchema,
	user_id: z.string()
}));
const getServiceJobsByAllCaseTagsArgsSchema = z.object({
	_case_tag_ids: z.array(z.string()).optional(),
	_case_types: z.array(publicCaseTypeSchema).optional(),
	_deployment_ids: z.array(z.string()).optional(),
	_from_date: z.string().optional(),
	_until_date: z.string().optional(),
	_user_ids: z.array(z.string()).optional(),
	_workspace_ids: z.array(z.string()).optional()
});
const getServiceJobsByAllCaseTagsReturnsSchema = z.array(z.object({
	case_tag_id: z.string(),
	case_tag_name: z.string(),
	deployment_id: z.string(),
	deployment_name: z.string(),
	service_job_count: z.number(),
	service_job_id: z.string(),
	total_service_jobs: z.number(),
	workspace_id: z.string(),
	workspace_name: z.string()
}));
const getServiceJobsByAllMaintenanceTagsArgsSchema = z.object({
	_deployment_ids: z.array(z.string()).optional(),
	_from_date: z.string().optional(),
	_maintenance_tag_ids: z.array(z.string()).optional(),
	_until_date: z.string().optional(),
	_user_ids: z.array(z.string()).optional(),
	_workspace_ids: z.array(z.string()).optional()
});
const getServiceJobsByAllMaintenanceTagsReturnsSchema = z.array(z.object({
	deployment_id: z.string(),
	deployment_name: z.string(),
	maintenance_tag_id: z.string(),
	maintenance_tag_name: z.string(),
	service_job_count: z.number(),
	service_job_id: z.string(),
	total_service_jobs: z.number(),
	workspace_id: z.string(),
	workspace_name: z.string()
}));
const getServiceJobsByPrimaryCaseTagArgsSchema = z.object({
	_case_tag_ids: z.array(z.string()).optional(),
	_case_types: z.array(publicCaseTypeSchema).optional(),
	_deployment_ids: z.array(z.string()).optional(),
	_from_date: z.string().optional(),
	_until_date: z.string().optional(),
	_user_ids: z.array(z.string()).optional(),
	_workspace_ids: z.array(z.string()).optional()
});
const getServiceJobsByPrimaryCaseTagReturnsSchema = z.array(z.object({
	case_tag_id: z.string(),
	case_tag_name: z.string(),
	deployment_id: z.string(),
	deployment_name: z.string(),
	service_job_count: z.number(),
	service_job_id: z.string(),
	total_service_jobs: z.number(),
	workspace_id: z.string(),
	workspace_name: z.string()
}));
const getServiceJobsByPrimaryMaintenanceTagArgsSchema = z.object({
	_deployment_ids: z.array(z.string()).optional(),
	_from_date: z.string().optional(),
	_maintenance_tag_ids: z.array(z.string()).optional(),
	_until_date: z.string().optional(),
	_user_ids: z.array(z.string()).optional(),
	_workspace_ids: z.array(z.string()).optional()
});
const getServiceJobsByPrimaryMaintenanceTagReturnsSchema = z.array(z.object({
	deployment_id: z.string(),
	deployment_name: z.string(),
	maintenance_tag_id: z.string(),
	maintenance_tag_name: z.string(),
	service_job_count: z.number(),
	service_job_id: z.string(),
	total_service_jobs: z.number(),
	workspace_id: z.string(),
	workspace_name: z.string()
}));
const getServiceJobsCasesAggregationArgsSchema = z.object({
	_case_tag_ids: z.array(z.string()).optional(),
	_case_types: z.array(publicCaseTypeSchema).optional(),
	_deployment_ids: z.array(z.string()).optional(),
	_from_date: z.string().optional(),
	_until_date: z.string().optional(),
	_user_ids: z.array(z.string()).optional(),
	_workspace_ids: z.array(z.string()).optional()
});
const getServiceJobsCasesAggregationReturnsSchema = z.array(z.object({
	avg_cases: z.number(),
	case_ids: z.array(z.string()),
	case_tag_id: z.string(),
	case_tag_name: z.string(),
	deployment_id: z.string(),
	deployment_name: z.string(),
	max_cases: z.number(),
	min_cases: z.number(),
	service_job_count: z.number(),
	total_cases: z.number(),
	total_service_jobs: z.number(),
	workspace_id: z.string(),
	workspace_name: z.string()
}));
const getServiceJobsMaintenancesAggregationArgsSchema = z.object({
	_deployment_ids: z.array(z.string()).optional(),
	_from_date: z.string().optional(),
	_maintenance_tag_ids: z.array(z.string()).optional(),
	_until_date: z.string().optional(),
	_user_ids: z.array(z.string()).optional(),
	_workspace_ids: z.array(z.string()).optional()
});
const getServiceJobsMaintenancesAggregationReturnsSchema = z.array(z.object({
	avg_maintenances: z.number(),
	deployment_id: z.string(),
	deployment_name: z.string(),
	maintenance_ids: z.array(z.string()),
	maintenance_tag_id: z.string(),
	maintenance_tag_name: z.string(),
	max_maintenances: z.number(),
	min_maintenances: z.number(),
	service_job_count: z.number(),
	total_maintenances: z.number(),
	total_service_jobs: z.number(),
	workspace_id: z.string(),
	workspace_name: z.string()
}));
const getWorkspaceDeviceModelFilesArgsSchema = z.object({ _workspace_id: z.string() });
const getWorkspaceDeviceModelFilesReturnsSchema = z.array(z.object({
	bucket_id: z.string(),
	created_at: z.string(),
	description: z.string(),
	device_model_ids: z.array(z.string()),
	id: z.string(),
	locale: z.string(),
	manufacturer_ids: z.array(z.string()),
	path: z.string(),
	processed_at: z.string(),
	shared_with_children: z.boolean(),
	thumbnail: z.string(),
	title: z.string(),
	updated_at: z.string(),
	user_id: z.string(),
	workspace_id: z.string()
}));
const handleSinglePartArgsSchema = z.object({
	_device_id: z.string().optional(),
	_part: partsRowSchema
});
const handleSinglePartReturnsSchema = z.object({
	archived_at: z.string().nullable(),
	average_price: z.number().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	device_id: z.string().nullable(),
	executed_at: z.string(),
	id: z.string(),
	part_model_id: z.string(),
	quantity: z.number().nullable(),
	ref: z.string().nullable(),
	transient_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const inventoryAddArgsSchema = z.object({
	_executed_at: z.string(),
	_location_ids: z.array(z.string()).optional(),
	_location_quantities: z.array(z.number()).optional(),
	_note: z.string(),
	_package_number: z.string(),
	_package_price: z.number(),
	_part_id: z.string(),
	_quantity: z.number()
});
const inventoryAddReturnsSchema = z.object({
	action: publicInventoryActionSchema,
	created_at: z.string(),
	deployment_id: z.string(),
	executed_at: z.string(),
	id: z.string(),
	maintenance_item_id: z.string().nullable(),
	note: z.string().nullable(),
	package_number: z.string().nullable(),
	package_price: z.number().nullable(),
	part_id: z.string(),
	quantity: z.number().nullable(),
	source_part_id: z.string().nullable(),
	target_part_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const inventoryAssembleArgsSchema = z.object({
	_device_id: z.string(),
	_maintenance_item_id: z.string(),
	_part_id: z.string(),
	_quantity: z.number(),
	_source_location_ids: z.array(z.string()).optional(),
	_source_location_quantities: z.array(z.number()).optional()
});
const inventoryAssembleReturnsSchema = z.object({
	archived_at: z.string().nullable(),
	average_price: z.number().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	device_id: z.string().nullable(),
	executed_at: z.string(),
	id: z.string(),
	part_model_id: z.string(),
	quantity: z.number().nullable(),
	ref: z.string().nullable(),
	transient_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const inventoryAssembleInternalArgsSchema = z.object({
	_device_id: z.string(),
	_part: partsRowSchema,
	_part_id: z.string(),
	_quantity: z.number()
});
const inventoryAssembleInternalReturnsSchema = z.object({
	archived_at: z.string().nullable(),
	average_price: z.number().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	device_id: z.string().nullable(),
	executed_at: z.string(),
	id: z.string(),
	part_model_id: z.string(),
	quantity: z.number().nullable(),
	ref: z.string().nullable(),
	transient_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const inventoryChangeArgsSchema = z.object({
	_executed_at: z.string(),
	_location_ids: z.array(z.string()).optional(),
	_location_quantities: z.array(z.number()).optional(),
	_note: z.string(),
	_package_number: z.string(),
	_part_id: z.string(),
	_quantity: z.number()
});
const inventoryChangeReturnsSchema = z.object({
	action: publicInventoryActionSchema,
	created_at: z.string(),
	deployment_id: z.string(),
	executed_at: z.string(),
	id: z.string(),
	maintenance_item_id: z.string().nullable(),
	note: z.string().nullable(),
	package_number: z.string().nullable(),
	package_price: z.number().nullable(),
	part_id: z.string(),
	quantity: z.number().nullable(),
	source_part_id: z.string().nullable(),
	target_part_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const inventoryDisassembleArgsSchema = z.object({
	_maintenance_item_id: z.string(),
	_part_id: z.string(),
	_quantity: z.number(),
	_skip_inventory: z.boolean().optional(),
	_target_location_ids: z.array(z.string()).optional(),
	_target_location_quantities: z.array(z.number()).optional()
});
const inventoryDisassembleReturnsSchema = z.object({
	archived_at: z.string().nullable(),
	average_price: z.number().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	device_id: z.string().nullable(),
	executed_at: z.string(),
	id: z.string(),
	part_model_id: z.string(),
	quantity: z.number().nullable(),
	ref: z.string().nullable(),
	transient_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const inventoryDisassembleInternalArgsSchema = z.object({
	_part: partsRowSchema,
	_part_id: z.string(),
	_quantity: z.number(),
	_skip_inventory: z.boolean().optional()
});
const inventoryDisassembleInternalReturnsSchema = z.object({
	archived_at: z.string().nullable(),
	average_price: z.number().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	device_id: z.string().nullable(),
	executed_at: z.string(),
	id: z.string(),
	part_model_id: z.string(),
	quantity: z.number().nullable(),
	ref: z.string().nullable(),
	transient_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const inventoryIsolateArgsSchema = z.object({
	_new_ref: z.string(),
	_note: z.string(),
	_part_id: z.string(),
	_source_location_ids: z.array(z.string()).optional(),
	_source_location_quantities: z.array(z.number()).optional(),
	_target_location_ids: z.array(z.string()).optional(),
	_target_location_quantities: z.array(z.number()).optional()
});
const inventoryIsolateReturnsSchema = z.object({
	action: publicInventoryActionSchema,
	created_at: z.string(),
	deployment_id: z.string(),
	executed_at: z.string(),
	id: z.string(),
	maintenance_item_id: z.string().nullable(),
	note: z.string().nullable(),
	package_number: z.string().nullable(),
	package_price: z.number().nullable(),
	part_id: z.string(),
	quantity: z.number().nullable(),
	source_part_id: z.string().nullable(),
	target_part_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const inventoryIsolateInternalArgsSchema = z.object({
	_new_ref: z.string(),
	_part: partsRowSchema,
	_part_id: z.string()
});
const inventoryIsolateInternalReturnsSchema = z.object({
	archived_at: z.string().nullable(),
	average_price: z.number().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	device_id: z.string().nullable(),
	executed_at: z.string(),
	id: z.string(),
	part_model_id: z.string(),
	quantity: z.number().nullable(),
	ref: z.string().nullable(),
	transient_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const inventoryMoveArgsSchema = z.object({
	_deployment_id: z.string(),
	_executed_at: z.string(),
	_note: z.string(),
	_package_number: z.string(),
	_package_price: z.number(),
	_part_id: z.string(),
	_quantity: z.number(),
	_source_location_ids: z.array(z.string()).optional(),
	_source_location_quantities: z.array(z.number()).optional(),
	_target_location_ids: z.array(z.string()).optional(),
	_target_location_quantities: z.array(z.number()).optional()
});
const inventoryMoveReturnsSchema = z.object({
	action: publicInventoryActionSchema,
	created_at: z.string(),
	deployment_id: z.string(),
	executed_at: z.string(),
	id: z.string(),
	maintenance_item_id: z.string().nullable(),
	note: z.string().nullable(),
	package_number: z.string().nullable(),
	package_price: z.number().nullable(),
	part_id: z.string(),
	quantity: z.number().nullable(),
	source_part_id: z.string().nullable(),
	target_part_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const inventoryMoveInternalArgsSchema = z.object({
	_deployment_id: z.string(),
	_executed_at: z.string(),
	_package_price: z.number(),
	_part: partsRowSchema,
	_part_id: z.string(),
	_quantity: z.number()
});
const inventoryMoveInternalReturnsSchema = z.object({
	archived_at: z.string().nullable(),
	average_price: z.number().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	device_id: z.string().nullable(),
	executed_at: z.string(),
	id: z.string(),
	part_model_id: z.string(),
	quantity: z.number().nullable(),
	ref: z.string().nullable(),
	transient_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const inventoryRemoveArgsSchema = z.object({
	_executed_at: z.string(),
	_location_ids: z.array(z.string()).optional(),
	_location_quantities: z.array(z.number()).optional(),
	_note: z.string(),
	_package_number: z.string(),
	_part_id: z.string(),
	_quantity: z.number()
});
const inventoryRemoveReturnsSchema = z.object({
	action: publicInventoryActionSchema,
	created_at: z.string(),
	deployment_id: z.string(),
	executed_at: z.string(),
	id: z.string(),
	maintenance_item_id: z.string().nullable(),
	note: z.string().nullable(),
	package_number: z.string().nullable(),
	package_price: z.number().nullable(),
	part_id: z.string(),
	quantity: z.number().nullable(),
	source_part_id: z.string().nullable(),
	target_part_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const inventoryUndoArgsSchema = z.object({ _inventory_id: z.string() });
const inventoryUndoReturnsSchema = z.undefined();
const publicGlobalFiltersSchema = z.object({
	ids: z.array(z.string()).nullable(),
	deployment_ids: z.array(z.string()).nullable(),
	workspace_ids: z.array(z.string()).nullable(),
	model_ids: z.array(z.string()).nullable(),
	manufacturer_ids: z.array(z.string()).nullable(),
	case_tag_ids: z.array(z.string()).nullable(),
	maintenance_tag_ids: z.array(z.string()).nullable(),
	assignee_ids: z.array(z.string()).nullable(),
	case_types: z.array(publicCaseTypeSchema).nullable(),
	device_ids: z.array(z.string()).nullable(),
	device_tag_ids: z.array(z.string()).nullable(),
	maintenance_status: z.array(publicMaintenanceStatusSchema).nullable(),
	search: z.string().nullable(),
	case_status: z.array(publicCaseStatusSchema).nullable()
});
const listDeviceDetailsReturnsSchema = z.array(z.object({
	connection_status: publicConnectionStatusSchema.nullable(),
	connections_to_devices: z.array(connectionsToDevicesRowSchema).nullable(),
	created_at: z.string().nullable(),
	deleted_at: z.string().nullable(),
	deleted_by: z.string().nullable(),
	deployment_id: z.string().nullable(),
	device_tag_ids: z.array(z.string()).nullable(),
	device_tag_names_search: z.string().nullable(),
	device_tags: z.array(deviceTagsRowSchema).nullable(),
	id: z.string().nullable(),
	last_auth: z.string().nullable(),
	manufactured_at: z.string().nullable(),
	manufacturer: manufacturersRowSchema.nullable(),
	manufacturer_id: z.string().nullable(),
	manufacturer_name: z.string().nullable(),
	model: deviceModelsRowSchema.nullable(),
	model_id: z.string().nullable(),
	model_name: z.string().nullable(),
	name: z.string().nullable(),
	portal_token: z.string().nullable(),
	qr_code: z.string().nullable(),
	ref: z.string().nullable(),
	software_version: z.string().nullable(),
	updated_at: z.string().nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
}));
const listMaintenancesToPartModelsAllocatedDetailsArgsSchema = z.object({ _filters: publicGlobalFiltersSchema.optional() });
const listMaintenancesToPartModelsAllocatedDetailsReturnsSchema = z.array(z.object({
	deployment_id: z.string().nullable(),
	id: z.string().nullable(),
	maintenance: maintenancesRowSchema.nullable(),
	maintenance_id: z.string().nullable(),
	part: partsRowSchema.nullable(),
	part_category: partCategoriesRowSchema.nullable(),
	part_location: partLocationsRowSchema.nullable(),
	part_location_template: partLocationTemplatesRowSchema.nullable(),
	part_manufacturer: partManufacturersRowSchema.nullable(),
	part_model: partModelDetailsRowSchema.nullable(),
	part_to_part_location: partToPartLocationsRowSchema.nullable(),
	quantity: z.number().nullable(),
	workspace_id: z.string().nullable()
}));
const listMaintenancesToPartModelsRequestedDetailsArgsSchema = z.object({ _filters: publicGlobalFiltersSchema.optional() });
const listMaintenancesToPartModelsRequestedDetailsReturnsSchema = z.array(z.object({
	allocated_part_locations: z.array(partToPartLocationsRowSchema).nullable(),
	allocated_parts: z.array(partsRowSchema).nullable(),
	created_at: z.string().nullable(),
	deployment_id: z.string().nullable(),
	id: z.string().nullable(),
	maintenance: maintenancesRowSchema.nullable(),
	maintenance_id: z.string().nullable(),
	notes: z.string().nullable(),
	part_model: partModelDetailsRowSchema.nullable(),
	part_model_id: z.string().nullable(),
	planned_date: z.string().nullable(),
	quantity: z.number().nullable(),
	quantity_allocated: z.number().nullable(),
	updated_at: z.string().nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable()
}));
const listServiceJobDetailsArgsSchema = z.object({ _filters: publicGlobalFiltersSchema.optional() });
const listServiceJobDetailsReturnsSchema = z.array(z.object({
	address_1: z.string().nullable(),
	address_2: z.string().nullable(),
	assignee_ids: z.array(z.string()).nullable(),
	assignees: z.array(userProfilesRowSchema).nullable(),
	cases: z.array(casesRowSchema).nullable(),
	city: z.string().nullable(),
	content_children_planned_duration: z.string().nullable(),
	content_children_resolution_duration: z.string().nullable(),
	content_planned_duration: z.string().nullable(),
	content_resolution_duration: z.string().nullable(),
	country: z.string().nullable(),
	created_at: z.string().nullable(),
	deployment_id: z.string().nullable(),
	description: z.string().nullable(),
	end_at: z.string().nullable(),
	id: z.string().nullable(),
	maintenances: z.array(maintenancesRowSchema).nullable(),
	name: z.string().nullable(),
	planned_duration: z.string().nullable(),
	primary_case_id: z.string().nullable(),
	primary_maintenance_id: z.string().nullable(),
	public_id: z.string().nullable(),
	serial_id: z.number().nullable(),
	serial_id_prefix: z.string().nullable(),
	start_at: z.string().nullable(),
	updated_at: z.string().nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable(),
	zipcode: z.string().nullable()
}));
const parallelAvailabilityFinalfuncArgsSchema = z.object({ state: z.number() });
const parallelAvailabilityFinalfuncReturnsSchema = z.number();
const parallelAvailabilitySfuncArgsSchema = z.object({
	error_duration: z.number(),
	state: z.number()
});
const parallelAvailabilitySfuncReturnsSchema = z.number();
const removeUserFromWorkspaceArgsSchema = z.object({
	_user_id: z.string(),
	_workspace_id: z.string()
});
const removeUserFromWorkspaceReturnsSchema = z.undefined();
const reorderItemArgsSchema = z.object({
	_group_column: z.string(),
	_group_value: z.string(),
	_item_id: jsonSchema,
	_new_index: z.number(),
	_table_name: z.string()
});
const reorderItemReturnsSchema = z.number();
const reportNerrorsArgsSchema = z.object({
	_devicesin: z.array(z.string()),
	_from: z.string(),
	_interval: z.string().optional(),
	_to: z.string()
});
const reportNerrorsReturnsSchema = z.array(z.object({
	date: z.string(),
	nerrors: z.number()
}));
const reportNmissionsArgsSchema = z.object({
	_devicesin: z.array(z.string()),
	_from: z.string(),
	_interval: z.string().optional(),
	_to: z.string()
});
const reportNmissionsReturnsSchema = z.array(z.object({
	date: z.string(),
	nmissions: z.number()
}));
const revertPartArgsSchema = z.object({ _part_history: partsHistoryRowSchema });
const revertPartReturnsSchema = z.object({
	archived_at: z.string().nullable(),
	average_price: z.number().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	device_id: z.string().nullable(),
	executed_at: z.string(),
	id: z.string(),
	part_model_id: z.string(),
	quantity: z.number().nullable(),
	ref: z.string().nullable(),
	transient_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const serialAvailabilitySfuncArgsSchema = z.object({
	error_duration: z.number(),
	state: z.number()
});
const serialAvailabilitySfuncReturnsSchema = z.number();
const setServiceJobPrimaryReasonArgsSchema = z.object({
	_case_id: z.string().optional(),
	_maintenance_id: z.string().optional(),
	_service_job_id: z.string()
});
const setServiceJobPrimaryReasonReturnsSchema = z.object({
	address_1: z.string().nullable(),
	address_2: z.string().nullable(),
	city: z.string().nullable(),
	content_children_planned_duration: z.string().nullable(),
	content_children_resolution_duration: z.string().nullable(),
	content_planned_duration: z.string().nullable(),
	content_resolution_duration: z.string().nullable(),
	country: z.string().nullable(),
	created_at: z.string(),
	deployment_id: z.string(),
	description: z.string().nullable(),
	end_at: z.string(),
	id: z.string(),
	name: z.string(),
	planned_duration: z.string().nullable(),
	primary_case_id: z.string().nullable(),
	primary_maintenance_id: z.string().nullable(),
	public_id: z.string(),
	serial_id: z.number(),
	serial_id_prefix: z.string(),
	start_at: z.string(),
	updated_at: z.string(),
	user_id: z.string().nullable(),
	workspace_id: z.string(),
	zipcode: z.string().nullable()
});
const trackEdgeFunctionsCallArgsSchema = z.object({
	_function_name: z.string(),
	_limit: z.number()
});
const trackEdgeFunctionsCallReturnsSchema = z.boolean();
const trackPartOperationArgsSchema = z.object({
	_action: publicInventoryActionSchema,
	_deployment_id: z.string(),
	_executed_at: z.string().optional(),
	_note: z.string().optional(),
	_package_number: z.string().optional(),
	_package_price: z.number().optional(),
	_part_id: z.string(),
	_quantity: z.number(),
	_source_location_ids: z.array(z.string()).optional(),
	_source_location_quantities: z.array(z.number()).optional(),
	_source_part_hist_id: z.string(),
	_target_location_ids: z.array(z.string()).optional(),
	_target_location_quantities: z.array(z.number()).optional(),
	_target_part_hist_id: z.string()
});
const trackPartOperationReturnsSchema = z.object({
	action: publicInventoryActionSchema,
	created_at: z.string(),
	deployment_id: z.string(),
	executed_at: z.string(),
	id: z.string(),
	maintenance_item_id: z.string().nullable(),
	note: z.string().nullable(),
	package_number: z.string().nullable(),
	package_price: z.number().nullable(),
	part_id: z.string(),
	quantity: z.number().nullable(),
	source_part_id: z.string().nullable(),
	target_part_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const updatePartQuantityArgsSchema = z.object({
	_action: publicInventoryActionSchema,
	_change: z.number(),
	_executed_at: z.string(),
	_location_ids: z.array(z.string()).optional(),
	_location_quantities: z.array(z.number()).optional(),
	_note: z.string(),
	_package_number: z.string(),
	_package_price: z.number().optional(),
	_part: partsRowSchema,
	_quantity: z.number()
});
const updatePartQuantityReturnsSchema = z.object({
	action: publicInventoryActionSchema,
	created_at: z.string(),
	deployment_id: z.string(),
	executed_at: z.string(),
	id: z.string(),
	maintenance_item_id: z.string().nullable(),
	note: z.string().nullable(),
	package_number: z.string().nullable(),
	package_price: z.number().nullable(),
	part_id: z.string(),
	quantity: z.number().nullable(),
	source_part_id: z.string().nullable(),
	target_part_id: z.string().nullable(),
	updated_at: z.string(),
	user_id: z.string().nullable()
});
const publicFifoInventoryRecordSchema = z.object({
	part_id: z.string().nullable(),
	in_id: z.string().nullable(),
	out_id: z.string().nullable(),
	in_executed_at: z.string().nullable(),
	in_action: publicInventoryActionSchema.nullable(),
	unit_value: z.number().nullable(),
	out_unit_value: z.number().nullable(),
	out_executed_at: z.string().nullable(),
	out_action: publicInventoryActionSchema.nullable(),
	taken_quantity: z.number().nullable(),
	remaining_quantity: z.number().nullable()
});
const maintenancesToPartModelsAllocatedDetailsRowSchema = z.object({
	deployment_id: z.string().nullable(),
	id: z.string().nullable(),
	maintenance: maintenancesRowSchema.nullable(),
	maintenance_id: z.string().nullable(),
	part: partsRowSchema.nullable(),
	part_category: partCategoriesRowSchema.nullable(),
	part_location: partLocationsRowSchema.nullable(),
	part_location_template: partLocationTemplatesRowSchema.nullable(),
	part_manufacturer: partManufacturersRowSchema.nullable(),
	part_model: partModelDetailsRowSchema.nullable(),
	part_to_part_location: partToPartLocationsRowSchema.nullable(),
	quantity: z.number().nullable(),
	workspace_id: z.string().nullable()
});
const serviceJobDetailsFullRowSchema = z.object({
	address_1: z.string().nullable(),
	address_2: z.string().nullable(),
	assignee_ids: z.array(z.string()).nullable(),
	assignees: z.array(userProfilesRowSchema).nullable(),
	cases: z.array(caseDetailsRowSchema).nullable(),
	city: z.string().nullable(),
	content_children_planned_duration: z.string().nullable(),
	content_children_resolution_duration: z.string().nullable(),
	content_planned_duration: z.string().nullable(),
	content_resolution_duration: z.string().nullable(),
	country: z.string().nullable(),
	created_at: z.string().nullable(),
	deployment_id: z.string().nullable(),
	description: z.string().nullable(),
	end_at: z.string().nullable(),
	id: z.string().nullable(),
	maintenances: z.array(maintenanceDetailsRowSchema).nullable(),
	name: z.string().nullable(),
	planned_duration: z.string().nullable(),
	primary_case_id: z.string().nullable(),
	primary_maintenance_id: z.string().nullable(),
	public_id: z.string().nullable(),
	serial_id: z.number().nullable(),
	serial_id_prefix: z.string().nullable(),
	start_at: z.string().nullable(),
	updated_at: z.string().nullable(),
	user_id: z.string().nullable(),
	workspace_id: z.string().nullable(),
	zipcode: z.string().nullable()
});
const listDeviceDetailsArgsSchema = z.object({ _filters: publicGlobalFiltersSchema.optional() });
//#endregion
export { acceptWorkspaceInvitationArgsSchema, acceptWorkspaceInvitationReturnsSchema, addUserToDeploymentArgsSchema, addUserToDeploymentReturnsSchema, applySparePartUsageArgsSchema, applySparePartUsageReturnsSchema, caseCommentsInsertSchema, caseCommentsRelationshipsSchema, caseCommentsRowSchema, caseCommentsUpdateSchema, caseDetailsRelationshipsSchema, caseDetailsRowSchema, caseFilesInsertSchema, caseFilesRelationshipsSchema, caseFilesRowSchema, caseFilesUpdateSchema, caseTagsInsertSchema, caseTagsRelationshipsSchema, caseTagsRowSchema, caseTagsUpdateSchema, caseTemplatesInsertSchema, caseTemplatesRelationshipsSchema, caseTemplatesRowSchema, caseTemplatesToChecklistTemplatesInsertSchema, caseTemplatesToChecklistTemplatesRelationshipsSchema, caseTemplatesToChecklistTemplatesRowSchema, caseTemplatesToChecklistTemplatesUpdateSchema, caseTemplatesToPublicPortalsInsertSchema, caseTemplatesToPublicPortalsRelationshipsSchema, caseTemplatesToPublicPortalsRowSchema, caseTemplatesToPublicPortalsUpdateSchema, caseTemplatesUpdateSchema, casesHistoryInsertSchema, casesHistoryRelationshipsSchema, casesHistoryRowSchema, casesHistoryUpdateSchema, casesInsertSchema, casesRelationshipsSchema, casesRowSchema, casesSearchArgsSchema, casesSearchReturnsSchema, casesToCaseTagsHistoryInsertSchema, casesToCaseTagsHistoryRelationshipsSchema, casesToCaseTagsHistoryRowSchema, casesToCaseTagsHistoryUpdateSchema, casesToCaseTagsInsertSchema, casesToCaseTagsRelationshipsSchema, casesToCaseTagsRowSchema, casesToCaseTagsUpdateSchema, casesToDeviceDataTriggersInsertSchema, casesToDeviceDataTriggersRelationshipsSchema, casesToDeviceDataTriggersRowSchema, casesToDeviceDataTriggersUpdateSchema, casesToMaintenancesHistoryInsertSchema, casesToMaintenancesHistoryRelationshipsSchema, casesToMaintenancesHistoryRowSchema, casesToMaintenancesHistoryUpdateSchema, casesToMaintenancesInsertSchema, casesToMaintenancesRelationshipsSchema, casesToMaintenancesRowSchema, casesToMaintenancesUpdateSchema, casesToPartsHistoryInsertSchema, casesToPartsHistoryRelationshipsSchema, casesToPartsHistoryRowSchema, casesToPartsHistoryUpdateSchema, casesToPartsInsertSchema, casesToPartsRelationshipsSchema, casesToPartsRowSchema, casesToPartsUpdateSchema, casesToServiceJobsHistoryInsertSchema, casesToServiceJobsHistoryRelationshipsSchema, casesToServiceJobsHistoryRowSchema, casesToServiceJobsHistoryUpdateSchema, casesToServiceJobsInsertSchema, casesToServiceJobsRelationshipsSchema, casesToServiceJobsRowSchema, casesToServiceJobsUpdateSchema, casesUpdateSchema, checklistTemplateItemFilesInsertSchema, checklistTemplateItemFilesRelationshipsSchema, checklistTemplateItemFilesRowSchema, checklistTemplateItemFilesUpdateSchema, checklistTemplateItemsInsertSchema, checklistTemplateItemsRelationshipsSchema, checklistTemplateItemsRowSchema, checklistTemplateItemsToPartsInsertSchema, checklistTemplateItemsToPartsRelationshipsSchema, checklistTemplateItemsToPartsRowSchema, checklistTemplateItemsToPartsUpdateSchema, checklistTemplateItemsUpdateSchema, checklistTemplatesInsertSchema, checklistTemplatesRelationshipsSchema, checklistTemplatesRowSchema, checklistTemplatesToWorkspacesInsertSchema, checklistTemplatesToWorkspacesRelationshipsSchema, checklistTemplatesToWorkspacesRowSchema, checklistTemplatesToWorkspacesUpdateSchema, checklistTemplatesUpdateSchema, connectionsInsertSchema, connectionsRelationshipsSchema, connectionsRowSchema, connectionsToDevicesInsertSchema, connectionsToDevicesRelationshipsSchema, connectionsToDevicesRowSchema, connectionsToDevicesUpdateSchema, connectionsUpdateSchema, createCustomerDeploymentArgsSchema, createCustomerDeploymentReturnsSchema, createMaintenanceArgsSchema, createMaintenanceReturnsSchema, createWorkspaceArgsSchema, createWorkspaceReturnsSchema, declineWorkspaceInvitationArgsSchema, declineWorkspaceInvitationReturnsSchema, deleteDeploymentArgsSchema, deleteDeploymentReturnsSchema, deleteWorkspaceArgsSchema, deleteWorkspaceReturnsSchema, deploymentSettingsInsertSchema, deploymentSettingsRelationshipsSchema, deploymentSettingsRowSchema, deploymentSettingsUpdateSchema, deploymentsInsertSchema, deploymentsRelationshipsSchema, deploymentsRowSchema, deploymentsUpdateSchema, deviceDataSeriesInsertSchema, deviceDataSeriesRelationshipsSchema, deviceDataSeriesRowSchema, deviceDataSeriesUpdateSchema, deviceDataTriggersInsertSchema, deviceDataTriggersRelationshipsSchema, deviceDataTriggersRowSchema, deviceDataTriggersUpdateSchema, deviceDetailsRelationshipsSchema, deviceDetailsRowSchema, deviceErrorsInsertSchema, deviceErrorsRelationshipsSchema, deviceErrorsRowSchema, deviceErrorsUpdateSchema, deviceFilesInsertSchema, deviceFilesRelationshipsSchema, deviceFilesRowSchema, deviceFilesUpdateSchema, deviceInsightsChartsInsertSchema, deviceInsightsChartsRelationshipsSchema, deviceInsightsChartsRowSchema, deviceInsightsChartsUpdateSchema, deviceInsightsSettingsInsertSchema, deviceInsightsSettingsRelationshipsSchema, deviceInsightsSettingsRowSchema, deviceInsightsSettingsUpdateSchema, deviceModelDataTriggersInsertSchema, deviceModelDataTriggersRelationshipsSchema, deviceModelDataTriggersRowSchema, deviceModelDataTriggersUpdateSchema, deviceModelFileChunksInsertSchema, deviceModelFileChunksRelationshipsSchema, deviceModelFileChunksRowSchema, deviceModelFileChunksSearchArgsSchema, deviceModelFileChunksSearchReturnsSchema, deviceModelFileChunksUpdateSchema, deviceModelFilesInsertSchema, deviceModelFilesRelationshipsSchema, deviceModelFilesRowSchema, deviceModelFilesUpdateSchema, deviceModelProfileFilesInsertSchema, deviceModelProfileFilesRelationshipsSchema, deviceModelProfileFilesRowSchema, deviceModelProfileFilesUpdateSchema, deviceModelsInsertSchema, deviceModelsRelationshipsSchema, deviceModelsRowSchema, deviceModelsToDeviceModelFilesInsertSchema, deviceModelsToDeviceModelFilesRelationshipsSchema, deviceModelsToDeviceModelFilesRowSchema, deviceModelsToDeviceModelFilesUpdateSchema, deviceModelsUpdateSchema, deviceOrdersInsertSchema, deviceOrdersRelationshipsSchema, deviceOrdersRowSchema, deviceOrdersUpdateSchema, deviceScalarValuesInsertSchema, deviceScalarValuesRelationshipsSchema, deviceScalarValuesRowSchema, deviceScalarValuesUpdateSchema, deviceTagsInsertSchema, deviceTagsRelationshipsSchema, deviceTagsRowSchema, deviceTagsUpdateSchema, devicesHistoryInsertSchema, devicesHistoryRelationshipsSchema, devicesHistoryRowSchema, devicesHistoryUpdateSchema, devicesInsertSchema, devicesRelationshipsSchema, devicesRowSchema, devicesToDeviceTagsInsertSchema, devicesToDeviceTagsRelationshipsSchema, devicesToDeviceTagsRowSchema, devicesToDeviceTagsUpdateSchema, devicesUpdateSchema, exportFifoInventoryForCsvArgsSchema, exportFifoInventoryForCsvReturnsSchema, exportPartsInventoryCsvArgsSchema, exportPartsInventoryCsvReturnsSchema, exportPartsInventoryWithConstraintsCsvArgsSchema, exportPartsInventoryWithConstraintsCsvReturnsSchema, getChecklistTemplatesArgsSchema, getChecklistTemplatesReturnsSchema, getDeviceDetailArgsSchema, getDeviceDetailReturnsSchema, getDeviceErrorsAggregationByDeviceTagArgsSchema, getDeviceErrorsAggregationByDeviceTagReturnsSchema, getDeviceErrorsAggregationByErrorArgsSchema, getDeviceErrorsAggregationByErrorReturnsSchema, getDeviceErrorsArgsSchema, getDeviceErrorsGanttArgsSchema, getDeviceErrorsGanttReturnsSchema, getDeviceErrorsReturnsSchema, getDeviceLogsArgsSchema, getDeviceLogsByPartArgsSchema, getDeviceLogsByPartReturnsSchema, getDeviceLogsReturnsSchema, getDeviceModelFilesArgsSchema, getDeviceModelFilesReturnsSchema, getDeviceModelProfileFileArgsSchema, getDeviceModelProfileFileReturnsSchema, getDeviceModelsArgsSchema, getDeviceModelsReturnsSchema, getDeviceScalarValueNamesArgsSchema, getDeviceScalarValueNamesReturnsSchema, getDevicesAvailabilityByDeviceTagArgsSchema, getDevicesAvailabilityByDeviceTagReturnsSchema, getDevicesByTagsArgsSchema, getDevicesByTagsReturnsSchema, getDevicesErrorDurationArgsSchema, getDevicesErrorDurationByDeviceTagArgsSchema, getDevicesErrorDurationByDeviceTagReturnsSchema, getDevicesErrorDurationReturnsSchema, getLastHistoryArgsSchema, getLastHistoryReturnsSchema, getMaintenancesToPartModelsAllocatedDetailsArgsSchema, getMaintenancesToPartModelsAllocatedDetailsReturnsSchema, getMaintenancesToPartModelsRequestedDetailsArgsSchema, getMaintenancesToPartModelsRequestedDetailsReturnsSchema, getManufacturersArgsSchema, getManufacturersReturnsSchema, getOrCreateDeviceDataSeriesArgsSchema, getOrCreateDeviceDataSeriesReturnsSchema, getServiceJobDetailArgsSchema, getServiceJobDetailReturnsSchema, getServiceJobLogsArgsSchema, getServiceJobLogsReturnsSchema, getServiceJobsByAllCaseTagsArgsSchema, getServiceJobsByAllCaseTagsReturnsSchema, getServiceJobsByAllMaintenanceTagsArgsSchema, getServiceJobsByAllMaintenanceTagsReturnsSchema, getServiceJobsByPrimaryCaseTagArgsSchema, getServiceJobsByPrimaryCaseTagReturnsSchema, getServiceJobsByPrimaryMaintenanceTagArgsSchema, getServiceJobsByPrimaryMaintenanceTagReturnsSchema, getServiceJobsCasesAggregationArgsSchema, getServiceJobsCasesAggregationReturnsSchema, getServiceJobsMaintenancesAggregationArgsSchema, getServiceJobsMaintenancesAggregationReturnsSchema, getWorkspaceDeviceModelFilesArgsSchema, getWorkspaceDeviceModelFilesReturnsSchema, handleSinglePartArgsSchema, handleSinglePartReturnsSchema, inventoryAddArgsSchema, inventoryAddReturnsSchema, inventoryAssembleArgsSchema, inventoryAssembleInternalArgsSchema, inventoryAssembleInternalReturnsSchema, inventoryAssembleReturnsSchema, inventoryChangeArgsSchema, inventoryChangeReturnsSchema, inventoryDisassembleArgsSchema, inventoryDisassembleInternalArgsSchema, inventoryDisassembleInternalReturnsSchema, inventoryDisassembleReturnsSchema, inventoryIsolateArgsSchema, inventoryIsolateInternalArgsSchema, inventoryIsolateInternalReturnsSchema, inventoryIsolateReturnsSchema, inventoryMoveArgsSchema, inventoryMoveInternalArgsSchema, inventoryMoveInternalReturnsSchema, inventoryMoveReturnsSchema, inventoryRemoveArgsSchema, inventoryRemoveReturnsSchema, inventoryUndoArgsSchema, inventoryUndoReturnsSchema, jsonSchema, listDeviceDetailsArgsSchema, listDeviceDetailsReturnsSchema, listMaintenancesToPartModelsAllocatedDetailsArgsSchema, listMaintenancesToPartModelsAllocatedDetailsReturnsSchema, listMaintenancesToPartModelsRequestedDetailsArgsSchema, listMaintenancesToPartModelsRequestedDetailsReturnsSchema, listServiceJobDetailsArgsSchema, listServiceJobDetailsReturnsSchema, maintenanceApprovalFilesInsertSchema, maintenanceApprovalFilesRelationshipsSchema, maintenanceApprovalFilesRowSchema, maintenanceApprovalFilesUpdateSchema, maintenanceApprovalsHistoryInsertSchema, maintenanceApprovalsHistoryRelationshipsSchema, maintenanceApprovalsHistoryRowSchema, maintenanceApprovalsHistoryUpdateSchema, maintenanceApprovalsInsertSchema, maintenanceApprovalsRelationshipsSchema, maintenanceApprovalsRowSchema, maintenanceApprovalsUpdateSchema, maintenanceDetailsRelationshipsSchema, maintenanceDetailsRowSchema, maintenanceItemFilesInsertSchema, maintenanceItemFilesRelationshipsSchema, maintenanceItemFilesRowSchema, maintenanceItemFilesUpdateSchema, maintenanceItemTemplateFilesInsertSchema, maintenanceItemTemplateFilesRelationshipsSchema, maintenanceItemTemplateFilesRowSchema, maintenanceItemTemplateFilesUpdateSchema, maintenanceItemsInsertSchema, maintenanceItemsRelationshipsSchema, maintenanceItemsRowSchema, maintenanceItemsToPartsInsertSchema, maintenanceItemsToPartsRelationshipsSchema, maintenanceItemsToPartsRowSchema, maintenanceItemsToPartsUpdateSchema, maintenanceItemsUpdateSchema, maintenanceStrategiesHistoryInsertSchema, maintenanceStrategiesHistoryRelationshipsSchema, maintenanceStrategiesHistoryRowSchema, maintenanceStrategiesHistoryUpdateSchema, maintenanceStrategiesInsertSchema, maintenanceStrategiesRelationshipsSchema, maintenanceStrategiesRowSchema, maintenanceStrategiesToDevicesInsertSchema, maintenanceStrategiesToDevicesRelationshipsSchema, maintenanceStrategiesToDevicesRowSchema, maintenanceStrategiesToDevicesUpdateSchema, maintenanceStrategiesToMaintenanceTagsInsertSchema, maintenanceStrategiesToMaintenanceTagsRelationshipsSchema, maintenanceStrategiesToMaintenanceTagsRowSchema, maintenanceStrategiesToMaintenanceTagsUpdateSchema, maintenanceStrategiesUpdateSchema, maintenanceTagsInsertSchema, maintenanceTagsRelationshipsSchema, maintenanceTagsRowSchema, maintenanceTagsUpdateSchema, maintenancesHistoryInsertSchema, maintenancesHistoryRelationshipsSchema, maintenancesHistoryRowSchema, maintenancesHistoryUpdateSchema, maintenancesInsertSchema, maintenancesRelationshipsSchema, maintenancesRowSchema, maintenancesToDeviceDataTriggersInsertSchema, maintenancesToDeviceDataTriggersRelationshipsSchema, maintenancesToDeviceDataTriggersRowSchema, maintenancesToDeviceDataTriggersUpdateSchema, maintenancesToDevicesHistoryInsertSchema, maintenancesToDevicesHistoryRelationshipsSchema, maintenancesToDevicesHistoryRowSchema, maintenancesToDevicesHistoryUpdateSchema, maintenancesToDevicesInsertSchema, maintenancesToDevicesRelationshipsSchema, maintenancesToDevicesRowSchema, maintenancesToDevicesUpdateSchema, maintenancesToMaintenanceTagsHistoryInsertSchema, maintenancesToMaintenanceTagsHistoryRelationshipsSchema, maintenancesToMaintenanceTagsHistoryRowSchema, maintenancesToMaintenanceTagsHistoryUpdateSchema, maintenancesToMaintenanceTagsInsertSchema, maintenancesToMaintenanceTagsRelationshipsSchema, maintenancesToMaintenanceTagsRowSchema, maintenancesToMaintenanceTagsUpdateSchema, maintenancesToMaintenancesHistoryInsertSchema, maintenancesToMaintenancesHistoryRelationshipsSchema, maintenancesToMaintenancesHistoryRowSchema, maintenancesToMaintenancesHistoryUpdateSchema, maintenancesToMaintenancesInsertSchema, maintenancesToMaintenancesRelationshipsSchema, maintenancesToMaintenancesRowSchema, maintenancesToMaintenancesUpdateSchema, maintenancesToPartModelsAllocatedDetailsRelationshipsSchema, maintenancesToPartModelsAllocatedDetailsRowSchema, maintenancesToPartModelsRequestedDetailsRelationshipsSchema, maintenancesToPartModelsRequestedDetailsRowSchema, maintenancesToPartModelsRequestedInsertSchema, maintenancesToPartModelsRequestedRelationshipsSchema, maintenancesToPartModelsRequestedRowSchema, maintenancesToPartModelsRequestedUpdateSchema, maintenancesToServiceJobsHistoryInsertSchema, maintenancesToServiceJobsHistoryRelationshipsSchema, maintenancesToServiceJobsHistoryRowSchema, maintenancesToServiceJobsHistoryUpdateSchema, maintenancesToServiceJobsInsertSchema, maintenancesToServiceJobsRelationshipsSchema, maintenancesToServiceJobsRowSchema, maintenancesToServiceJobsUpdateSchema, maintenancesUpdateSchema, manufacturersInsertSchema, manufacturersRelationshipsSchema, manufacturersRowSchema, manufacturersUpdateSchema, parallelAvailabilityFinalfuncArgsSchema, parallelAvailabilityFinalfuncReturnsSchema, parallelAvailabilitySfuncArgsSchema, parallelAvailabilitySfuncReturnsSchema, partCategoriesInsertSchema, partCategoriesRelationshipsSchema, partCategoriesRowSchema, partCategoriesUpdateSchema, partInventoriesInsertSchema, partInventoriesRelationshipsSchema, partInventoriesRowSchema, partInventoriesToLocationsInsertSchema, partInventoriesToLocationsRelationshipsSchema, partInventoriesToLocationsRowSchema, partInventoriesToLocationsUpdateSchema, partInventoriesUpdateSchema, partInventoryFilesInsertSchema, partInventoryFilesRelationshipsSchema, partInventoryFilesRowSchema, partInventoryFilesUpdateSchema, partLocationTemplatesInsertSchema, partLocationTemplatesRelationshipsSchema, partLocationTemplatesRowSchema, partLocationTemplatesUpdateSchema, partLocationsInsertSchema, partLocationsRelationshipsSchema, partLocationsRowSchema, partLocationsUpdateSchema, partManufacturersInsertSchema, partManufacturersRelationshipsSchema, partManufacturersRowSchema, partManufacturersUpdateSchema, partModelConstraintsInsertSchema, partModelConstraintsRelationshipsSchema, partModelConstraintsRowSchema, partModelConstraintsUpdateSchema, partModelDetailsRelationshipsSchema, partModelDetailsRowSchema, partModelsInsertSchema, partModelsRelationshipsSchema, partModelsRowSchema, partModelsUpdateSchema, partToPartLocationsInsertSchema, partToPartLocationsRelationshipsSchema, partToPartLocationsRowSchema, partToPartLocationsUpdateSchema, partsHistoryInsertSchema, partsHistoryRelationshipsSchema, partsHistoryRowSchema, partsHistoryUpdateSchema, partsInsertSchema, partsRelationshipsSchema, partsRowSchema, partsUpdateSchema, physicalUnitsInsertSchema, physicalUnitsRelationshipsSchema, physicalUnitsRowSchema, physicalUnitsUpdateSchema, publicBusinessTypeSchema, publicCaseOriginSchema, publicCaseStatusSchema, publicCaseTypeSchema, publicCasesHistoryTypeSchema, publicCasesInsightsPeriodSchema, publicCasesToMaintenancesOriginSchema, publicChartTypeSchema, publicConnectionStatusSchema, publicDataTriggerActionSchema, publicDataTriggerOperatorSchema, publicDataTriggerTargetTableSchema, publicDataTriggerThresholdSourceSchema, publicDataTriggerTransformationSchema, publicDbOperationSchema, publicDeploymentRoleSchema, publicDeviceOrderStatusSchema, publicDevicesHistoryTypeSchema, publicFifoInventoryRecordSchema, publicGlobalFiltersSchema, publicGlobalRoleSchema, publicInventoryActionSchema, publicInventoryLocationTypeSchema, publicMaintenanceItemStatusSchema, publicMaintenanceItemsToPartsTypeSchema, publicMaintenanceStatusSchema, publicMaintenanceStrategiesHistoryTypeSchema, publicMaintenancesFinishedDeviceUtilizationPeriodSchema, publicMaintenancesHistoryTypeSchema, publicPartLocationTypeSchema, publicPartUsageTypeSchema, publicPortalsInsertSchema, publicPortalsRelationshipsSchema, publicPortalsRowSchema, publicPortalsUpdateSchema, publicServiceJobsHistoryTypeSchema, publicWorkspaceRoleSchema, qrCodesInsertSchema, qrCodesRelationshipsSchema, qrCodesRowSchema, qrCodesUpdateSchema, removeUserFromWorkspaceArgsSchema, removeUserFromWorkspaceReturnsSchema, reorderItemArgsSchema, reorderItemReturnsSchema, reportNerrorsArgsSchema, reportNerrorsReturnsSchema, reportNmissionsArgsSchema, reportNmissionsReturnsSchema, revertPartArgsSchema, revertPartReturnsSchema, serialAvailabilitySfuncArgsSchema, serialAvailabilitySfuncReturnsSchema, serviceJobDetailsFullRelationshipsSchema, serviceJobDetailsFullRowSchema, serviceJobDetailsRelationshipsSchema, serviceJobDetailsRowSchema, serviceJobsHistoryInsertSchema, serviceJobsHistoryRelationshipsSchema, serviceJobsHistoryRowSchema, serviceJobsHistoryUpdateSchema, serviceJobsInsertSchema, serviceJobsRelationshipsSchema, serviceJobsRowSchema, serviceJobsUpdateSchema, setServiceJobPrimaryReasonArgsSchema, setServiceJobPrimaryReasonReturnsSchema, storageBuckettypeSchema, trackEdgeFunctionsCallArgsSchema, trackEdgeFunctionsCallReturnsSchema, trackPartOperationArgsSchema, trackPartOperationReturnsSchema, updatePartQuantityArgsSchema, updatePartQuantityReturnsSchema, userProfilesInsertSchema, userProfilesRowSchema, userProfilesUpdateSchema, usersToDeploymentsInsertSchema, usersToDeploymentsRelationshipsSchema, usersToDeploymentsRowSchema, usersToDeploymentsUpdateSchema, usersToRolesInsertSchema, usersToRolesRelationshipsSchema, usersToRolesRowSchema, usersToRolesUpdateSchema, usersToServiceJobsHistoryInsertSchema, usersToServiceJobsHistoryRelationshipsSchema, usersToServiceJobsHistoryRowSchema, usersToServiceJobsHistoryUpdateSchema, usersToServiceJobsInsertSchema, usersToServiceJobsRelationshipsSchema, usersToServiceJobsRowSchema, usersToServiceJobsUpdateSchema, usersToWorkspacesInsertSchema, usersToWorkspacesRelationshipsSchema, usersToWorkspacesRowSchema, usersToWorkspacesUpdateSchema, whitelabelBrandsInsertSchema, whitelabelBrandsRowSchema, whitelabelBrandsUpdateSchema, workspaceInvitationsInsertSchema, workspaceInvitationsRelationshipsSchema, workspaceInvitationsRowSchema, workspaceInvitationsUpdateSchema, workspaceProfileFilesInsertSchema, workspaceProfileFilesRelationshipsSchema, workspaceProfileFilesRowSchema, workspaceProfileFilesUpdateSchema, workspaceProfilesInsertSchema, workspaceProfilesRelationshipsSchema, workspaceProfilesRowSchema, workspaceProfilesUpdateSchema, workspacesInsertSchema, workspacesRelationshipsSchema, workspacesRowSchema, workspacesUpdateSchema };
